import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import { supabase } from "./supabaseClient";
import { SalesTab, AuditTab } from "./SalesTabs";
import {
  looksLikeSalesReport, parseSalesReport, parseCatalogCatapult,
  parseListPrices, salesWeekFromFilename, priceGapPct,
} from "./salesData";

// ══════════════════════════════════════════════════════════
// STORE CONFIG
// ══════════════════════════════════════════════════════════
const STORES = [
  { abbr: "BO", name: "Boston", acct: "100001005", location: "Boston, MA", sheet: "MASTER CATALOG - FRESH", headerRow: 2, vendors: ["baldor", "cw"], region: "US" },
  { abbr: "SH", name: "Short Hills", acct: "100001010", location: "Short Hills, NJ", sheet: "MASTER CATALOG - FRESH", headerRow: 2, vendors: ["baldor", "cw"], region: "US" },
  { abbr: "KP", name: "King of Prussia", acct: "100001003", location: "King of Prussia, PA", sheet: "MASTER CATALOG - FRESH", headerRow: 2, vendors: ["baldor", "cw"], region: "US" },
  { abbr: "AV", name: "Aventura", acct: "100001001", location: "Aventura, FL", sheet: "MASTER CATALOG", headerRow: 0, vendors: ["freshpoint_fl", "mrgreen", "cw"], region: "US" },
  { abbr: "WP", name: "West Palm Beach", acct: "100001004", location: "West Palm Beach, FL", sheet: "MASTER CATALOG - FRESH", headerRow: 3, vendors: ["freshpoint_fl", "mrgreen", "cw"], region: "US" },
  { abbr: "CH", name: "Chicago", acct: "100001006", location: "Chicago, IL", sheet: "MASTER CATALOG - FRESH", headerRow: 2, vendors: ["testa", "midwest", "cw"], region: "US" },
  { abbr: "DA", name: "Dallas", acct: "100001007", location: "Dallas, TX", sheet: "MASTER CATALOG - FRESH", headerRow: 2, vendors: ["freshpoint_da", "hardies", "cw"], region: "US" },
  { abbr: "SV", name: "Silicon Valley", acct: "100001011", location: "San Jose, CA", sheet: "MASTER CATALOG - FRESH", headerRow: 2, vendors: ["ok", "vesta", "cw"], region: "US" },
  { abbr: "LA", name: "Los Angeles", acct: "100001002", location: "Los Angeles, CA", sheet: "MASTER CATALOG - FRESH", headerRow: 2, vendors: ["charlies", "vesta", "freshpoint_sc", "cw"], region: "US" },
  // Canada
  { abbr: "TO", name: "Toronto", acct: "800001000", location: "Toronto, ON", sheet: "CAN MASTER CATALOG - FRESH", headerRow: 2, vendors: ["zeus", "bondi", "burnac"], region: "CAN" },
  { abbr: "SG", name: "Sherway Gardens", acct: "800001001", location: "Toronto, ON", sheet: "CAN MASTER CATALOG - FRESH", headerRow: 2, vendors: ["zeus", "bondi", "burnac"], region: "CAN" },
  { abbr: "DM", name: "Don Mills", acct: "800001002", location: "Toronto, ON", sheet: "CAN MASTER CATALOG - FRESH", headerRow: 2, vendors: ["zeus", "bondi", "burnac"], region: "CAN" },
  { abbr: "EC", name: "Eaton Centre", acct: "800001003", location: "Toronto, ON", sheet: "CAN MASTER CATALOG - FRESH", headerRow: 2, vendors: ["zeus", "bondi", "burnac"], region: "CAN" },
];

const ACTIVE_STORES = ["BO", "SH", "KP", "AV", "WP", "CH", "DA", "SV", "LA", "TO", "SG", "DM", "EC"];
const CW_REGION = { BO: "NY", SH: "NY", KP: "NY", AV: "FL", WP: "FL", CH: "Chicago", DA: "Texas", SV: "SF", LA: "LA" };
const CAN_STORES = ["TO", "SG", "DM", "EC"];

// ══════════════════════════════════════════════════════════
// DATE PARSING FROM FILENAMES
// ══════════════════════════════════════════════════════════
// Snap any YYYY-MM-DD to the Monday of its (Monday-start) week
function snapToMonday(dateStr) {
  const p = String(dateStr).split("-");
  if (p.length !== 3) return dateStr;
  const d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
  if (isNaN(d.getTime())) return dateStr;
  const dow = d.getDay();            // 0=Sun … 6=Sat
  const back = (dow + 6) % 7;        // Mon→0, Tue→1 … Sun→6
  d.setDate(d.getDate() - back);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// This week's Monday (used as the automatic-dating fallback and chart right-anchor)
function thisMonday() {
  const now = new Date();
  return snapToMonday(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`);
}

function parseDateFromFilename(filename) {
  const fn = filename.replace(/\.[^.]+$/, ""); // strip extension
  let m;
  let raw = null;
  // YYYY-MM-DD or YYYY_MM_DD
  m = fn.match(/(\d{4})[-_](\d{2})[-_](\d{2})/);
  if (m) raw = `${m[1]}-${m[2]}-${m[3]}`;
  // MMDDYY or MM_DD_YY or MM-DD-YY (6 digits)
  if (!raw) {
    m = fn.match(/(\d{2})[-_]?(\d{2})[-_]?(\d{2})(?!\d)/);
    if (m) {
      const mm = m[1], dd = m[2], yy = m[3];
      const yr = parseInt(yy) > 50 ? `19${yy}` : `20${yy}`;
      if (parseInt(mm) <= 12 && parseInt(dd) <= 31) raw = `${yr}-${mm}-${dd}`;
    }
  }
  // MMDDYYYY or MM_DD_YYYY or MM-DD-YYYY
  if (!raw) {
    m = fn.match(/(\d{2})[-_]?(\d{2})[-_]?(\d{4})/);
    if (m) {
      const mm = m[1], dd = m[2], yyyy = m[3];
      if (parseInt(mm) <= 12 && parseInt(dd) <= 31) raw = `${yyyy}-${mm}-${dd}`;
    }
  }
  // No date found — return null so the caller can reject the file (we never
  // silently stamp an undated file with today's date). Resolved dates are
  // snapped to their week's Monday so weekly points align.
  return raw ? snapToMonday(raw) : null;
}

// ══════════════════════════════════════════════════════════
// LIST PRICES — persistence
// ══════════════════════════════════════════════════════════
// parseListPrices returns { [store]: { price: {...}, placeholder: Set } }. A Set
// serialises to `{}` in JSON, which is why this blob was originally left out of
// persistence — but leaving it out meant every reload dropped the list prices
// entirely, so the Sales tab could only ever show the captured (net ÷ qty)
// price. Converting the Set to an array on the way out and back on the way in
// costs nothing and lets the export survive a refresh like every other input.
function serializeListPrices(lp) {
  const out = {};
  for (const [store, b] of Object.entries(lp || {})) {
    out[store] = { price: b?.price || {}, placeholder: Array.from(b?.placeholder || []) };
  }
  return out;
}
function hydrateListPrices(raw) {
  const out = {};
  for (const [store, b] of Object.entries(raw || {})) {
    // Tolerate a Set that was stored before this fix (it round-trips as {}).
    const ph = Array.isArray(b?.placeholder) ? b.placeholder : [];
    out[store] = { price: b?.price || {}, placeholder: new Set(ph) };
  }
  return out;
}

// Mirrors ST_META in SalesTabs so the export reads the same as the screen.
const SALES_STATUS_LABEL = {
  matched: "In catalog", idMismatch: "ID mismatch",
  notInCatalog: "Not in catalog", unknown: "No catalog",
};

const FILTER_LABELS = {
  all: "All items", red: "Under 25% margin", yellow: "Over 55% margin", safe: "25–55% margin",
  errors: "Errors", unavail: "Unavailable", newin: "New in", disc: "Discounted",
};

const AUDIT_BUCKET_LABEL = {
  notSold: "In catalog, no sales",
  soldIn: "Sold, in catalog",
  soldNotIn: "Sold, not in catalog",
};

// The Monday that starts the week containing d — the same bucket every file
// uses, so a manual edit and a report value are directly comparable.
const mondayOf = (d) => {
  const x = new Date(d);
  const off = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - off);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

const todayStamp = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}${String(d.getFullYear()).slice(-2)}`;
};

// Friendly labels for vendor keys (used in the Inputs ledger and upload log)
const VENDOR_LABELS = { baldor: "Baldor", freshpoint_fl: "FP S. Florida", freshpoint_da: "FP Dallas", freshpoint_sc: "FP S. California", mrgreen: "Mr Greens", testa: "Testa", midwest: "Midwest", hardies: "Hardie's", ok: "OK Produce", vesta: "Vesta", charlies: "Charlie's", zeus: "Zeus", bondi: "Bondi", burnac: "Burnac", cw: "CW/Dairyland" };

// ══════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════
// IndexedDB key-value store — localStorage (~5MB) is too small for full vendor
// lists + 13 stores of items + weeks of history. IDB gives us far more room.
// Everything is wrapped so a failure degrades gracefully rather than throwing.
// ══════════════════════════════════════════════════════════
// Persistence is backed by a shared Supabase table (app_state) so the whole team
// sees one dataset. These helpers keep the same names/signatures the app already
// used for IndexedDB, so the rest of the data lifecycle is unchanged.
async function idbGet(key) {
  try {
    if (!supabase) return undefined;
    const { data, error } = await supabase.from("app_state").select("value").eq("key", key).maybeSingle();
    if (error) return undefined;
    return data ? data.value : undefined;
  } catch { return undefined; }
}
// idbGet above collapses "the read failed" and "nothing is stored under this
// key" into the same `undefined`. That distinction matters enormously on load:
// treating a failed read as "first run" is what let an empty starting state get
// written over real data. idbRead keeps them apart and retries transient
// failures, which is the common case — a free-tier Supabase project pauses after
// ~7 days idle, so the first requests after a quiet spell hit a project that is
// still waking up and fail while later ones succeed.
async function idbRead(key, attempts = 3) {
  if (!supabase) return { ok: false, value: undefined, error: new Error("Backend not configured") };
  let lastErr = null;
  for (let i = 0; i < attempts; i++) {
    try {
      const { data, error } = await supabase.from("app_state").select("value").eq("key", key).maybeSingle();
      if (!error) return { ok: true, value: data ? data.value : null };   // null = genuinely absent
      lastErr = error;
    } catch (e) { lastErr = e; }
    if (i < attempts - 1) await new Promise((r) => setTimeout(r, 400 * Math.pow(2, i)));  // 400ms, 800ms
  }
  return { ok: false, value: undefined, error: lastErr };
}

async function idbSet(key, value) {
  if (!supabase) throw new Error("Backend not configured");
  const { error } = await supabase.from("app_state").upsert({ key, value, updated_at: new Date().toISOString() });
  if (error) throw error;
  return true;
}
async function idbDel(key) {
  try { if (supabase) await supabase.from("app_state").delete().eq("key", key); } catch {}
}
async function idbKeys() {
  try {
    if (!supabase) return [];
    const { data, error } = await supabase.from("app_state").select("key");
    if (error) return [];
    return (data || []).map((r) => r.key);
  } catch { return []; }
}
async function idbClear() {
  try { if (supabase) await supabase.from("app_state").delete().neq("key", ""); } catch {}
}

// ══════════════════════════════════════════════════════════
// PERSISTENT HISTORY STORAGE
// ══════════════════════════════════════════════════════════
// Storage key: "hist:{store}" → JSON array of { date, items: { d365: { cost, price } } }
// We batch all items per store+date into one key to minimize storage calls.

// 8-week retention window for history data
const HISTORY_RETENTION_WEEKS = 26;   // was 8 — two months proved too short to read a trend, and snapshots are small

// Sales keeps a LONGER window than pricing history (26 weeks vs 8): assortment
// decisions need a seasonal span, and two or three weeks can't tell a dead item
// from a seasonal one.
function trimSalesWeeks(salesWeeks, keepWeeks = 26) {
  const out = {};
  for (const [store, byWeek] of Object.entries(salesWeeks || {})) {
    const weeks = Object.keys(byWeek).sort().slice(-keepWeeks);
    out[store] = {};
    weeks.forEach((w) => { out[store][w] = byWeek[w]; });
  }
  return out;
}

function getRetentionCutoff() {
  const d = new Date();
  d.setDate(d.getDate() - HISTORY_RETENTION_WEEKS * 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function trimHistory(history) {
  const cutoff = getRetentionCutoff();
  return history.filter((h) => h.date >= cutoff);
}

async function loadHistory(store) {
  try {
    let hist = await idbGet(`hist:${store}`);
    // One-time migration: pull any legacy localStorage history into IDB, then free it.
    if (hist == null) {
      try {
        const raw = localStorage.getItem(`hist:${store}`);
        if (raw) {
          hist = JSON.parse(raw);
          await idbSet(`hist:${store}`, hist).catch(() => {});
          localStorage.removeItem(`hist:${store}`);
        }
      } catch {}
    }
    if (!hist) return [];
    const trimmed = trimHistory(hist);
    if (trimmed.length < hist.length) { try { await idbSet(`hist:${store}`, trimmed); } catch {} }
    return trimmed;
  } catch { return []; }
}

async function saveHistory(store, history) {
  try {
    const trimmed = trimHistory(history);
    await idbSet(`hist:${store}`, trimmed);
    return true;
  } catch (e) { console.error("History save failed:", e); return false; }
}

// Append a snapshot for a given date. Merges with existing if date already present.
// Authoritative-clear semantics: when a vendor list is received this pass
// (rec.listReceived), the item's cost is REPLACED by this pass's value — cleared
// if the item is no longer on the list. Likewise price is replaced when a sales
// export is received this pass (exportReceived). A pass that lacks a given stream
// leaves that stream's prior value untouched, so a vendor-only and a sales-only
// upload for the same week still combine. This prevents stale/phantom costs (e.g.
// from an old file) from sticking once the correct file for that week is processed.
function mergeSnapshot(history, date, itemsMap, exportReceived) {
  const existing = history.find((h) => h.date === date);
  if (existing) {
    for (const [d365, rec] of Object.entries(itemsMap)) {
      const merged = { ...existing.items[d365] };
      if (rec.listReceived) {
        merged.listReceived = true;
        if (rec.cost != null) merged.cost = rec.cost; else delete merged.cost;
      } else if (rec.cost != null) {
        merged.cost = rec.cost;
      }
      if (exportReceived) {
        if (rec.price != null) merged.price = rec.price; else delete merged.price;
      } else if (rec.price != null) {
        merged.price = rec.price;
      }
      existing.items[d365] = merged;
    }
    if (exportReceived) existing.exportReceived = true;
  } else {
    history.push({ date, items: itemsMap, exportReceived: !!exportReceived });
  }
  history.sort((a, b) => a.date.localeCompare(b.date));
  return trimHistory(history);
}

// ── History identity ─────────────────────────────────────
// Snapshots used to be keyed on the D365 number alone. Several stores carry the
// same D365 on two catalog rows with different vendors and pack sizes — Boston
// has ~78, LA 33, SV 54 — so the two variants shared one bucket and whichever
// was written last silently overwrote the other. The stat card showed the row
// you clicked while the graph showed its twin. Cost and price are per-variant,
// so the key has to be too.
//
// Retail-side edits (Mkt Price, New Price, discounts) deliberately stay keyed on
// D365: they are a judgement about what the shelf price should be, not about a
// particular vendor's pack, so sharing them across variants is correct.
function histKey(it) {
  const d = String(it?.d365 ?? "").trim();
  const v = String(it?.vpn ?? "").trim();
  return v ? `${d}|${v}` : d;
}

// Read one item's record from a snapshot, falling back to the legacy
// D365-only key so history written before this change still resolves.
// The fallback is withheld for duplicated D365s: that bucket holds whichever
// variant happened to be written last, so handing it to either row would be a
// guess. A gap is the honest answer, and the variants separate cleanly from the
// next upload onward.
function readHistRec(snapshot, it) {
  if (!snapshot || !snapshot.items) return null;
  const rec = snapshot.items[histKey(it)];
  if (rec) return rec;
  if (it?.dupD365) return null;
  const legacy = String(it?.d365 ?? "").trim();
  return (legacy && snapshot.items[legacy]) || null;
}

// Extract history for a single item across all snapshots.
// cost/price are null when not observed; listReceived = this item's vendor list
// arrived that week (even if the item wasn't on it); exportReceived = a sales
// export arrived that week (date-global).
function getItemHistory(history, item) {
  return history
    .map((h) => ({ h, rec: readHistRec(h, item) }))
    .filter(({ rec }) => !!rec)
    .map(({ h, rec }) => ({
      date: h.date,
      cost: rec.cost ?? null,
      price: rec.price ?? null,
      listReceived: !!rec.listReceived,
      exportReceived: !!h.exportReceived,
    }));
}

// ══════════════════════════════════════════════════════════
// "NEW IN" DETECTION — items back on the vendor list this week
// ══════════════════════════════════════════════════════════
// An item is "New In" when, last week, its vendor price list WAS received but the
// item wasn't on it (no cost — the old "VPN not found" state), and this week the
// list carries it with a cost (orderable). Detection is week-over-week off the
// Monday-bucketed history snapshots, so it can't be corrupted by mid-week
// recomputes and won't fire just because a vendor file was loaded for the first
// time (that requires last week's list to have been received AND missing the item).
function tagNewIn(items, store, historyMap) {
  const hist = (historyMap && historyMap[store]) || [];
  // Snapshots are stored ascending by date; compare the two most recent weeks.
  const thisWk = hist.length >= 1 ? hist[hist.length - 1] : null;
  const lastWk = hist.length >= 2 ? hist[hist.length - 2] : null;
  if (!thisWk || !lastWk) return items.map((it) => ({ ...it, newIn: false }));
  return items.map((it) => {
    const lastRec = readHistRec(lastWk, it);
    const thisRec = readHistRec(thisWk, it);
    const wasReceivedButAbsent = !!(lastRec && lastRec.listReceived && lastRec.cost == null);
    const nowAvailable = !!(thisRec && thisRec.cost != null);
    // Guard: it must be orderable NOW (live cost present). A stale/phantom cost
    // lingering in history can't flag New In on an item that has no current cost.
    return { ...it, newIn: wasReceivedButAbsent && nowAvailable && it.cost != null };
  });
}

// ══════════════════════════════════════════════════════════
// WEIGHT ESTIMATES (LB per EA)
// ══════════════════════════════════════════════════════════
const AVG_W = {APPLE:0.33,AVOCADO:0.5,BANANA:0.25,BROCCOLI:1.0,CABBAGE:3.0,CANTALOUPE:3.0,CARROT:0.5,CELERY:2.0,CHICORY:0.8,CORN:0.5,CUCUMBER:0.75,'DRAGON FRUIT':0.75,EGGPLANT:1.0,ENDIVE:0.5,GRAPEFRUIT:0.75,HONEYDEW:5.0,KALE:0.5,LEEK:0.5,LEMON:0.2,LETTUCE:1.0,LIME:0.15,MANGO:0.75,MUSHROOM:0.5,NECTARINE:0.33,ONION:0.75,ORANGE:0.4,PAPAYA:1.5,PASSION:1.0,PEACH:0.33,PEAR:0.4,PEPPER:0.4,PEPINO:0.4,PLANTAIN:0.5,POMEGRANATE:0.5,POTATO:1.0,RADICCHIO:0.5,RHUBARB:0.2,RUTABAGA:1.5,SHALLOT:0.15,'SQUASH YELLOW':0.5,'SQUASH, GREEN':0.5,SQUASH:1.5,STARFRUIT:0.33,'SWEET POTATO':1.0,TANGERINE:0.25,TOMATO:0.5,'WATERMELON BABY':5.0,'WATERMELON, MINI':5.0,WATERMELON:8.0,ZUCCHINI:0.5};

// KG weight estimates for Canada (kg per ea)
const AVG_W_KG = {APPLE:0.18,AVOCADO:0.22,BANANA:0.11,BROCCOLI:0.45,CABBAGE:1.36,CANTALOUPE:1.36,CARROT:0.23,CELERY:0.91,CORN:0.23,CUCUMBER:0.34,'DRAGON FRUIT':0.34,EGGPLANT:0.45,GRAPEFRUIT:0.34,HONEYDEW:2.27,KALE:0.23,LEEK:0.23,LEMON:0.09,LETTUCE:0.45,LIME:0.07,MANGO:0.34,MUSHROOM:0.23,NECTARINE:0.15,ONION:0.34,ORANGE:0.18,PAPAYA:0.68,PEACH:0.15,PEAR:0.18,PEPPER:0.18,PLANTAIN:0.23,POMEGRANATE:0.23,POTATO:0.45,TOMATO:0.23,'SWEET POTATO':0.45,TANGERINE:0.11,ZUCCHINI:0.23};

function estWeight(pn) {
  const u = String(pn).toUpperCase();
  const ozm = u.match(/(\d+\.?\d*)\s*OZ/);
  if (ozm) return parseFloat(ozm[1]) / 16;
  let best = null, bl = 0;
  for (const [kw, wt] of Object.entries(AVG_W)) {
    if (u.includes(kw) && kw.length > bl) { best = wt; bl = kw.length; }
  }
  return best;
}

function estWeightKg(pn) {
  const u = String(pn).toUpperCase();
  // Check for embedded gram weight in product name (e.g. "150GR", "200G")
  const gm = u.match(/(\d+)\s*(?:GR|G)\b/);
  if (gm) return parseInt(gm[1]) / 1000;
  let best = null, bl = 0;
  for (const [kw, wt] of Object.entries(AVG_W_KG)) {
    if (u.includes(kw) && kw.length > bl) { best = wt; bl = kw.length; }
  }
  return best;
}

// ══════════════════════════════════════════════════════════
// CANADA CASEPACK PARSER
// ══════════════════════════════════════════════════════════
function parseCasepackCAN(rawSize, desc = "") {
  if (!rawSize || String(rawSize).trim() === "") return { qty: null, unit: null, error: "Size/pack not provided" };
  const s = String(rawSize).trim().toUpperCase();
  let m;

  // Per-unit pricing: EACH, EA, BUNCH, LBS alone
  if (/^(EACH|EA|BUNCH|BU|LBS?|PER\s*LB)$/i.test(s)) return { qty: 1, unit: s.includes("LB") ? "lb" : "ea", error: null };

  // Xs pattern: "80s" = 80 count
  m = s.match(/^(\d+)\s*S$/i);
  if (m) return { qty: parseInt(m[1]), unit: "ct", error: null };

  // X x Y LBS retail packs: "12x3lbs" = 12 packs of 3lb
  m = s.match(/^(\d+)\s*[Xx]\s*(\d+\.?\d*)\s*(?:LBS?|#)/i);
  if (m) return { qty: parseInt(m[1]), unit: `${parseFloat(m[2])}lb_ea`, error: null };

  // X x Y KG retail packs
  m = s.match(/^(\d+)\s*[Xx]\s*(\d+\.?\d*)\s*KG/i);
  if (m) return { qty: parseInt(m[1]), unit: `${parseFloat(m[2])}kg_ea`, error: null };

  // X x Y G retail packs: "6x400g"
  m = s.match(/^(\d+)\s*[Xx]\s*(\d+)\s*G\b/i);
  if (m) return { qty: parseInt(m[1]), unit: `${parseInt(m[2]) / 1000}kg_ea`, error: null };

  // X x PINT: "12x1pint" = 12 count
  m = s.match(/^(\d+)\s*[Xx]\s*\d*\s*(?:PINT|PT)/i);
  if (m) return { qty: parseInt(m[1]), unit: "ct", error: null };

  // X x 1 (no unit): "6x1" = 6 count
  m = s.match(/^(\d+)\s*[Xx]\s*(\d+)\s*$/);
  if (m) return { qty: parseInt(m[1]) * parseInt(m[2]), unit: "ct", error: null };

  // Total weight: "40lb" / "40lbs" = total lb, convert to kg
  m = s.match(/^(\d+\.?\d*)\s*(?:LBS?|#)\s*$/i);
  if (m) return { qty: Math.round(parseFloat(m[1]) * 0.4536 * 100) / 100, unit: "kg", error: null };

  // Total weight kg: "5kg"
  m = s.match(/^(\d+\.?\d*)\s*KG\s*$/i);
  if (m) return { qty: parseFloat(m[1]), unit: "kg", error: null };

  // Count: "48ct" / "48 ct"
  m = s.match(/^(\d+)\s*(?:CT|C|PCS|PC)\s*$/i);
  if (m) return { qty: parseInt(m[1]), unit: "ct", error: null };

  // Count range: "72-88ct" → avg
  m = s.match(/^(\d+)\s*[-\/]\s*(\d+)\s*(?:CT|C)?\s*$/i);
  if (m) return { qty: (parseInt(m[1]) + parseInt(m[2])) / 2, unit: "ct", error: null };

  // X/Y# = X packs of Y lb: "11/1#" = 11lb total
  m = s.match(/^(\d+)\s*\/\s*(\d+\.?\d*)\s*(?:#|LB)/i);
  if (m) return { qty: Math.round(parseInt(m[1]) * parseFloat(m[2]) * 0.4536 * 100) / 100, unit: "kg", error: null };

  // X/Y KG
  m = s.match(/^(\d+)\s*\/\s*(\d+\.?\d*)\s*KG/i);
  if (m) return { qty: parseInt(m[1]) * parseFloat(m[2]), unit: "kg", error: null };

  // Dozen
  m = s.match(/^(\d+)\s*DZ/i);
  if (m) return { qty: parseInt(m[1]) * 12, unit: "ct", error: null };

  // Fallback to US parser
  return parseCasepack(rawSize, desc, "");
}

// ══════════════════════════════════════════════════════════
// CASEPACK OVERRIDE RULES
// ══════════════════════════════════════════════════════════
// Each rule: { id, store (abbr or "ALL"), vendorKey, matchType ("d365"|"vpn"|"keyword"),
//   matchValue, caseQty, caseUnit ("LB"|"EA"|"KG"), retailQty, retailUnit ("EA"|"LB"|"KG") }
// Example: 30 LB papaya case = 8 EA → caseQty:30, caseUnit:"LB", retailQty:8, retailUnit:"EA"

// Check if a casepack rule matches this item. Returns override cp object or null.
// Rule meaning: "ignore what the vendor calls the casepack — this case = retailQty retailUnit"
function matchCasepackRule(rules, storeAbbr, vendorKey, d365, vpn, productName) {
  const d365Clean = String(d365).trim();
  const vpnClean = String(vpn).trim().toUpperCase();
  const nameUpper = String(productName).toUpperCase();

  for (const rule of rules) {
    // Store must match (or rule is for ALL stores)
    if (rule.store !== "ALL" && rule.store !== storeAbbr) continue;
    // Vendor must match (or rule is for ALL vendors)
    if (rule.vendorKey !== "ALL" && rule.vendorKey !== vendorKey) continue;

    // Match check
    const mv = String(rule.matchValue).trim().toUpperCase();
    let matched = false;
    if (rule.matchType === "d365" && d365Clean === mv) matched = true;
    else if (rule.matchType === "vpn" && (vpnClean === mv || vpnClean.replace(/^0+/, "") === mv.replace(/^0+/, ""))) matched = true;
    else if (rule.matchType === "keyword" && nameUpper.includes(mv)) matched = true;

    if (matched) {
      const rQty = parseFloat(rule.retailQty);
      const ru = String(rule.retailUnit).toUpperCase();
      // Map retail unit to casepack unit type
      const cpUnit = (ru === "LB" || ru === "KG") ? "lb" : "ct";
      return {
        qty: rQty,
        unit: cpUnit,
        error: null,
        ruleId: rule.id,
        ruleLabel: `Rule: 1 case = ${rQty} ${ru}`,
      };
    }
  }
  return null;
}

const VENDOR_OPTIONS = [
  { key: "ALL", label: "All Vendors" },
  { key: "baldor", label: "Baldor" },
  { key: "freshpoint_fl", label: "Freshpoint S. Florida" },
  { key: "freshpoint_da", label: "Freshpoint Dallas" },
  { key: "freshpoint_sc", label: "Freshpoint S. California" },
  { key: "mrgreen", label: "Mr Greens" },
  { key: "testa", label: "Testa" },
  { key: "midwest", label: "Midwest" },
  { key: "hardies", label: "Hardie's" },
  { key: "ok", label: "OK Produce" },
  { key: "vesta", label: "Vesta" },
  { key: "charlies", label: "Charlie's" },
  { key: "cw", label: "CW / Dairyland" },
  { key: "zeus", label: "Zeus (CAN)" },
  { key: "bondi", label: "Bondi (CAN)" },
  { key: "burnac", label: "Burnac / Provincial (CAN)" },
];

const STORE_OPTIONS = [
  { key: "ALL", label: "All Stores" },
  ...STORES.map((s) => ({ key: s.abbr, label: `${s.abbr} - ${s.name}` })),
];

// ══════════════════════════════════════════════════════════
// CASEPACK PARSER — ported from build_output.py parse_cp()
// ══════════════════════════════════════════════════════════
function parseCasepack(rawSize, desc = "", measure = "") {
  if (!rawSize || String(rawSize).trim() === "") return { qty: null, unit: null, error: "Size/pack not provided" };
  const s = String(rawSize).trim().toUpperCase();
  const d = String(desc || "").toUpperCase();
  // Strip container prefixes/suffixes
  let si = s.replace(/^(CASE|CS|CTN|BOX|BX|SACK|BAG|FLAT|FLT|FT|LUG|SK|W\/B)\s*[/]?\s*/i, "").trim();
  si = si.replace(/\s*[/]?\s*(CASE|CS|CTN|BOX|BX|SACK|BAG|FLAT|FLT|FT|LUG|SK|W\/B)\s*$/i, "").trim();
  si = si.replace(/\s+(?:CASE|CS)\s*(?:\/\s*(?:EACH|EA))?\s*$/i, "").trim();
  si = si.replace(/\s*\/\s*[A-Z].*$/, "").trim();
  let m;

  // === WEIGHT PATTERNS ===
  m = si.match(/^(\d+\.?\d*)\s*L?\s*(?:LB|#|LBS)S?\s*$/i);
  if (m) return { qty: parseFloat(m[1]), unit: "lb", error: null };
  m = si.match(/^(\d+\.?\d*)\s*(?:LB|#)\b/i);
  if (m && !si.includes("/") && !si.toUpperCase().includes("X")) return { qty: parseFloat(m[1]), unit: "lb", error: null };

  // === WEIGHT RANGES ===
  m = si.match(/^(\d+\.?\d*)\s*-\s*(\d+\.?\d*)\s*(?:LB|#|LBS)/i);
  if (m) return { qty: (parseFloat(m[1]) + parseFloat(m[2])) / 2, unit: "lb", error: null };
  m = si.match(/^(\d+\.?\d*)\s*-\s*(\d+\.?\d*)\s*#/);
  if (m) return { qty: (parseFloat(m[1]) + parseFloat(m[2])) / 2, unit: "lb", error: null };

  // === X x FRACTION OZ ===
  m = si.match(/^(\d+)\s*[Xx]\s*(\d+)\s*\/\s*(\d+)\s*(?:OZ|Z)\b/i);
  if (m) return { qty: parseInt(m[1]), unit: `${parseInt(m[2]) / parseInt(m[3])}oz`, error: null };
  m = si.match(/^(\d+)\s*\/\s*\.(\d+)\s*(?:OZ|Z)\b/i);
  if (m) return { qty: parseInt(m[1]), unit: `${parseFloat("0." + m[2])}oz`, error: null };

  // === X x Y LB (retail packs) ===
  m = si.match(/^(\d+)\s*[Xx]\s*(\d+\.?\d*)\s*(?:LB|#)/i);
  if (m) return { qty: parseInt(m[1]), unit: `${parseFloat(m[2])}lb_ea`, error: null };
  m = si.match(/^(\d+)\s*[Xx]\s*(\d+)\s*CT/i);
  if (m) return { qty: parseInt(m[1]), unit: "ct", error: null };
  m = si.match(/^(\d+)\s*[Xx]\s*(\d+\.?\d*)\s*(?:OZ|GR)/i);
  if (m) return { qty: parseInt(m[1]), unit: `${parseFloat(m[2])}oz`, error: null };
  m = si.match(/^(\d+)\s*[Xx]\s*\d*\s*(?:PT|PTS|PINT|EACH|EA)\b/i);
  if (m) return { qty: parseInt(m[1]), unit: "ea", error: null };
  m = si.match(/^(\d+)\s*X\s*PTS?$/i);
  if (m) return { qty: parseInt(m[1]), unit: "ea", error: null };

  // === REVERSED: "8 OZ x 12" ===
  m = si.match(/^(\d+\.?\d*)\s*OZ\s*[Xx]\s*(\d+)/i);
  if (m) return { qty: parseInt(m[2]), unit: `${parseFloat(m[1])}oz`, error: null };

  // === X/Y PATTERNS ===
  m = si.match(/^(\d+)\s*\/\s*(\d+\.?\d*)\s*(?:LB|#)/i);
  if (m) return { qty: parseInt(m[1]) * parseFloat(m[2]), unit: "lb", error: null };
  m = si.match(/^(\d+)\s*\/\s*(\d+\.?\d*)\s*(?:OZ|Z)\b/i);
  if (m) return { qty: parseInt(m[1]), unit: `${parseFloat(m[2])}oz`, error: null };
  m = si.match(/^(\d+)\s*[Xx\/]\s*(\d*\.?\d+)\s*(?:OZ|Z)\b/i);
  if (m) return { qty: parseInt(m[1]), unit: `${parseFloat(m[2])}oz`, error: null };

  // === COUNT PATTERNS ===
  m = si.match(/^(\d+)\s*\/\s*(\d+)\s*\/\s*(\d+)\s*(?:CT|C)?\s*/);
  if (m) return { qty: parseInt(m[2]), unit: "ct", error: null };
  m = si.match(/^(\d+)\s*[-\/]\s*(\d+)\s*(?:CT|C)\s+(\d+\.?\d*)\s*(?:LB|#)/i);
  if (m) return { qty: parseFloat(m[3]), unit: "lb", error: null };
  m = si.match(/^(\d+)\s*[-\/]\s*(\d+)\s*(?:CT|C|'S)?\s*$/i);
  if (m) return { qty: (parseInt(m[1]) + parseInt(m[2])) / 2, unit: "ct", error: null };
  m = si.match(/^(\d+)\s*(?:CT|C)\b/i);
  if (m) return { qty: parseInt(m[1]), unit: "ct", error: null };
  m = si.match(/^(\d+)\s*$/);
  if (m && parseInt(m[1]) > 2) return { qty: parseInt(m[1]), unit: "ct", error: null };

  // === DOZEN ===
  m = si.match(/^(\d+)\s*-?\s*(\d+)?\s*DZ/i);
  if (m) {
    if (m[2]) return { qty: ((parseInt(m[1]) + parseInt(m[2])) / 2) * 12, unit: "ct", error: null };
    return { qty: parseInt(m[1]) * 12, unit: "ct", error: null };
  }

  // === PACK/PIECE/BUNCH ===
  m = si.match(/^(\d+)\s*PK\b/i); if (m) return { qty: parseInt(m[1]), unit: "ea", error: null };
  m = si.match(/^(\d+)\s*PC\b/i); if (m) return { qty: parseInt(m[1]), unit: "ct", error: null };
  m = si.match(/^(\d+)\s*(?:EA|EACH)\b/i); if (m) return { qty: parseInt(m[1]), unit: "ct", error: null };
  m = si.match(/^(\d+)\s*(?:BUN(?:CH)?|BU|BSH)\b/i); if (m) return { qty: parseInt(m[1]), unit: "ct", error: null };
  m = si.match(/^(\d+)\s*\/?\s*\d*\s*BK/i); if (m) return { qty: parseInt(m[1]), unit: "ea", error: null };
  m = si.match(/^(\d+)\s*PKG/i); if (m) return { qty: parseInt(m[1]), unit: "ea", error: null };

  // === PINT ===
  m = si.match(/^(\d+)\s*\/?\s*\d*\s*(?:PT|PNT|PINT)/i);
  if (m) return { qty: parseInt(m[1]), unit: "ea", error: null };

  // === BUSHEL ===
  m = si.match(/^1\s*[-\s]?\s*1\/9\s*(?:BU|BSH)?/i);
  if (m) {
    if (d.includes("PEPPER") || d.includes("BELL")) return { qty: 115, unit: "ct", error: null };
    if (d.includes("CHICORY")) return { qty: 19, unit: "ct", error: null };
    return { qty: 120, unit: "ct", error: null };
  }
  m = si.match(/^(?:1\/2|½)\s*(?:BU|BSH)?/i);
  if (m) {
    if (d.includes("CUCUMBER")) return { qty: 48, unit: "ct", error: null };
    return { qty: 60, unit: "ct", error: null };
  }

  // === LAYER ===
  m = si.match(/(?:LYR|LAYER)\s+(\d+)\s*-?\s*(\d+)?\s*CT/i);
  if (m) {
    if (m[2]) return { qty: (parseInt(m[1]) + parseInt(m[2])) / 2, unit: "ct", error: null };
    return { qty: parseInt(m[1]), unit: "ct", error: null };
  }
  if (si.includes("LAYER") || si.includes("LYR")) {
    if (d.includes("POMEGRANATE")) return { qty: 4, unit: "lb", error: null };
    if (d.includes("PASSION")) return { qty: 15, unit: "ct", error: null };
    if (d.includes("PEPINO")) return { qty: 8, unit: "ct", error: null };
    const m2 = si.match(/(\d+)\s*-?\s*(\d+)?\s*CT/i);
    if (m2) {
      if (m2[2]) return { qty: (parseInt(m2[1]) + parseInt(m2[2])) / 2, unit: "ct", error: null };
      return { qty: parseInt(m2[1]), unit: "ct", error: null };
    }
    return { qty: null, unit: null, error: `Layer pack: ${s}` };
  }

  // === HALF CASE ===
  if (si.includes("HALF")) {
    const m2 = si.match(/(\d+)/);
    if (m2) return { qty: parseInt(m2[1]), unit: "ct", error: null };
    return { qty: null, unit: null, error: `Half case: ${s}` };
  }

  // === STANDALONE UNITS ===
  if (["POUND", "LB", "#"].includes(si)) return { qty: 1, unit: "lb", error: null };
  m = si.match(/^(\d+)\s*[Xx\/]\s*(\d+\.?\d*)\s*KG/i);
  if (m) return { qty: Math.round(parseInt(m[1]) * parseFloat(m[2]) * 2.2 * 10) / 10, unit: "lb", error: null };
  m = si.match(/^(\d+\.?\d*)\s*KG/i);
  if (m) return { qty: Math.round(parseFloat(m[1]) * 2.2 * 10) / 10, unit: "lb", error: null };
  m = si.match(/^(\d+\.?\d*)\s*OZ$/i);
  if (m) return { qty: parseFloat(m[1]) / 16, unit: "lb", error: null };
  m = si.match(/^1\s*\/\s*(\d+\.?\d*)\s*(?:LB|#)/i);
  if (m) return { qty: parseFloat(m[1]), unit: "lb", error: null };
  if (si.includes("GAL")) {
    const gm = si.match(/(\d+)/);
    return { qty: gm ? parseInt(gm[1]) : 1, unit: "gal", error: null };
  }
  if (/^1?\s*TRAY/i.test(si)) return { qty: 1, unit: "ea", error: null };

  // === GENERIC CASE — product-based defaults ===
  if (["CASE", "CS", "CTN", "CARTON", "BOX", "BX", "EACH", "EA", "PC", ""].includes(si) || si.startsWith("CASE") || si.startsWith("CS ")) {
    if (d.includes("HARRY")) return { qty: 8, unit: "ea", error: null };
    if (d.includes("RADICCHIO") || d.includes("CASTELFRANCO")) return { qty: 12, unit: "ct", error: null };
    if (d.includes("FRISEE") || d.includes("FRISSEE")) return { qty: 12, unit: "ct", error: null };
    if (d.includes("LETTUCE") || d.includes("LITTLE GEM") || d.includes("LOLA") || d.includes("LOLLA")) return { qty: 24, unit: "ct", error: null };
    if ((d.includes("KING OYSTER") || d.includes("KING TRUMPET"))) return { qty: 6, unit: "lb", error: null };
    if (d.includes("BUTTERNUT") && d.includes("SQUASH")) return { qty: 35, unit: "lb", error: null };
    if (d.includes("ACORN") && d.includes("SQUASH")) return { qty: 35, unit: "lb", error: null };
    if (d.includes("PEACH")) {
      if (d.includes("2-LAYER") || d.includes("2 LAYER")) return { qty: 22, unit: "lb", error: null };
      return { qty: 25, unit: "lb", error: null };
    }
    if (d.includes("HEIRLOOM") && d.includes("TOMATO")) return { qty: 10, unit: "lb", error: null };
    if (d.includes("RHUBARB")) return { qty: 11, unit: "lb", error: null };
    if (d.includes("GRAPE") && !d.includes("TOMATO")) return { qty: 19, unit: "lb", error: null };
    if (d.includes("RED PEAR") || (d.includes("PEAR") && d.includes("RED"))) return { qty: 27, unit: "lb", error: null };
    if (d.includes("ATAULFO") && d.includes("MANGO")) return { qty: 18, unit: "ct", error: null };
    if (d.includes("RED ENDIVE") || d.includes("ENDIVE RED")) return { qty: 6.6, unit: "lb", error: null };
    if (d.includes("HONEYCRISP") && d.includes("APPLE")) return { qty: 40, unit: "lb", error: null };
    if (d.includes("CORN")) return { qty: 48, unit: "ct", error: null };
    if (d.includes("POTATO")) return { qty: 50, unit: "lb", error: null };
    if (d.includes("LEMON")) return { qty: 40, unit: "lb", error: null };
    if (d.includes("ARTICHOKE") && d.includes("BABY")) return { qty: 20, unit: "lb", error: null };
    if (d.includes("PASSION")) return { qty: 15, unit: "ct", error: null };
    if (d.includes("PEPINO")) return { qty: 8, unit: "ct", error: null };
    if (d.includes("MANGO") && (d.includes("ATAULFO") || d.includes("HONEY"))) return { qty: 18, unit: "ct", error: null };
    if (d.includes("MANGO") && !d.includes("KENT")) return { qty: 10, unit: "ct", error: null };
    if (d.includes("ENDIVE") && d.includes("RED")) return { qty: 6.6, unit: "lb", error: null };
    if (d.includes("ENDIVE") && d.includes("BELGIAN")) return { qty: 10, unit: "lb", error: null };
    if (d.includes("GOOSEBERR")) return { qty: 10, unit: "lb", error: null };
    if (d.includes("PERSIMMON")) return { qty: 10, unit: "lb", error: null };
    if (d.includes("BEEFSTEAK") && d.includes("TOMATO")) return { qty: 20, unit: "lb", error: null };
    if (d.includes("TOMATO") && d.includes("CHERRY")) return { qty: 10, unit: "lb", error: null };
    if (d.includes("TOMATO")) return { qty: 25, unit: "lb", error: null };
    if (d.includes("MUSHROOM") && (d.includes("KING") || d.includes("TRUMPET") || d.includes("OYSTER"))) return { qty: 6, unit: "lb", error: null };
    if (d.includes("MUSHROOM") && (d.includes("BEECH") || d.includes("SHIMEJI"))) return { qty: 8, unit: "lb", error: null };
    if (d.includes("MUSHROOM")) return { qty: 5, unit: "lb", error: null };
    if (d.includes("PEAR") && d.includes("BARTLETT")) return { qty: 40, unit: "lb", error: null };
    if (d.includes("FIG")) {
      const fm = d.match(/(\d+)\s*[-\/]?\s*(\d+)\s*OZ/);
      if (fm) return { qty: parseInt(fm[1]), unit: `${parseFloat(fm[2])}oz`, error: null };
    }
    // Try description extraction
    let dm = d.match(/(\d+)\s*(?:CT|COUNT|PC|PCS)/i); if (dm) return { qty: parseInt(dm[1]), unit: "ct", error: null };
    dm = d.match(/(\d+\.?\d*)\s*(?:LB|#)/i); if (dm) return { qty: parseFloat(dm[1]), unit: "lb", error: null };
    dm = d.match(/(\d+)\s*\/\s*(\d+\.?\d*)\s*OZ/i); if (dm) return { qty: parseInt(dm[1]), unit: `${parseFloat(dm[2])}oz`, error: null };
    if (["EACH", "EA", "PC"].includes(si)) return { qty: 1, unit: "ea", error: null };
    return { qty: null, unit: null, error: `Can't parse: ${s} / ${d.slice(0, 50)}` };
  }

  // === CATCH-ALL ===
  m = si.match(/^(\d+\.?\d*)\s*#/);
  if (m) return { qty: parseFloat(m[1]), unit: "lb", error: null };
  m = si.match(/^(\d+)\s*\/\s*(\d+\.?\d*)\s*$/);
  if (m) return { qty: parseInt(m[1]), unit: `${parseFloat(m[2])}oz`, error: null };
  if (["OF", "FLAT OF", ""].includes(si)) {
    if (d.includes("TOMATO")) return { qty: 20, unit: "ct", error: null };
  }
  return { qty: null, unit: null, error: `Cannot parse: ${s}` };
}

// ══════════════════════════════════════════════════════════
// UNIT COST CALCULATOR — ported from calc_uc()
// ══════════════════════════════════════════════════════════
function calcUnitCost(vendorPrice, qty, cpUnit, saleUnit) {
  if (vendorPrice == null || qty == null || qty === 0) return { cost: null, note: "Missing" };
  const p = parseFloat(vendorPrice);
  if (isNaN(p)) return { cost: null, note: "Invalid price" };
  const su = String(saleUnit).toUpperCase().trim();
  // Xlb_ea retail packs
  if (cpUnit && String(cpUnit).includes("lb_ea")) {
    const eaWt = parseFloat(String(cpUnit).replace("lb_ea", ""));
    if (["EA", "EACH", "PC", "HD"].includes(su)) return { cost: p / qty, note: null };
    if (["LB", "KG"].includes(su)) return { cost: p / (qty * eaWt), note: null };
  }
  // Xkg_ea retail packs (Canada)
  if (cpUnit && String(cpUnit).includes("kg_ea")) {
    const eaWtKg = parseFloat(String(cpUnit).replace("kg_ea", ""));
    if (["EA", "EACH", "PC", "HD"].includes(su)) return { cost: p / qty, note: null };
    if (su === "KG") return { cost: p / (qty * eaWtKg), note: null };
    if (su === "LB") return { cost: p / (qty * eaWtKg * 2.2046), note: null };
  }
  if (cpUnit === "kg") {
    if (su === "KG") return { cost: p / qty, note: null };
    if (su === "LB") return { cost: p / (qty * 2.2046), note: null };
    if (["EA", "EACH", "PC", "HD"].includes(su)) return { cost: p / qty, note: "cost per KG, selling by EA" };
  }
  if (cpUnit === "lb") {
    if (su === "LB") return { cost: p / qty, note: null };
    if (su === "KG") return { cost: p / (qty * 0.4536), note: null };
    if (["EA", "EACH", "PC", "HD"].includes(su)) return { cost: p / qty, note: "cost per LB, selling by EA" };
  }
  if (cpUnit === "ct") {
    if (["EA", "EACH", "PC", "HD"].includes(su)) return { cost: p / qty, note: null };
    if (su === "LB") return { cost: p / qty, note: "cost per CT, selling by LB" };
    if (su === "KG") return { cost: p / qty, note: "cost per CT, selling by KG" };
  }
  if (cpUnit === "ea") return { cost: p / qty, note: null };
  if (cpUnit && String(cpUnit).includes("oz")) {
    const ozm = String(cpUnit).match(/(\d+\.?\d*)oz/);
    if (ozm) {
      if (["EA", "EACH"].includes(su)) return { cost: p / qty, note: null };
      if (su === "LB") return { cost: p / (qty * parseFloat(ozm[1]) / 16), note: null };
      if (su === "KG") return { cost: p / (qty * parseFloat(ozm[1]) / 35.274), note: null };
    }
  }
  if (cpUnit === "gal") return { cost: p / qty, note: null };
  return { cost: p / qty, note: `Approx: ${cpUnit}->${su}` };
}

// ── Canada dual-unit pricing (TO, SG, DM, EC only) ─────────────────────────
// Canada catalogs price by KG; these stores also want to see the equivalent per
// LB. Purely derived from the existing per-unit price — it never feeds margin or
// the 30% target, which stay on the per-unit basis.
const KG_PER_LB = 2.20462;
const CAN_DUAL_STORES = ["TO", "SG", "DM", "EC"];
const isCanDual = (abbr) => CAN_DUAL_STORES.includes(abbr);
// Only KG items get a per-LB figure. Dividing an each-price by 2.20462 would be
// meaningless, so those rows stay blank — which also makes the column scannable:
// anything populated is a weight item.
const isKgItem = (item) => String(item?.saleUnit || "").trim().toUpperCase() === "KG";
const toLb = (perUnit) => (perUnit == null ? null : Math.round((perUnit / KG_PER_LB) * 100) / 100);
const fromLb = (perLb) => (perLb == null ? null : Math.round(perLb * KG_PER_LB * 100) / 100);

function roundTo09(p) {
  if (p == null || isNaN(p)) return null;
  let b = Math.floor(p * 10) / 10;
  let r = b + 0.09;
  if (r < p - 0.05) r += 0.10;
  return Math.round(r * 100) / 100;
}

function calcMarginPct(salesPrice, unitCost) {
  if (salesPrice == null || unitCost == null) return null;
  const s = parseFloat(salesPrice), c = parseFloat(unitCost);
  if (isNaN(s) || isNaN(c) || s === 0) return null;
  return Math.round(((s - c) / s) * 1000) / 10;
}

// ══════════════════════════════════════════════════════════
// FILE PARSERS
// ══════════════════════════════════════════════════════════

// Read XLSX ArrayBuffer -> workbook
function readXlsx(arrayBuffer) {
  return XLSX.read(arrayBuffer, { type: "array" });
}

// --- CATALOG PARSER ---
// ══════════════════════════════════════════════════════════
// REGIONAL (MULTI-STORE) CATALOG PARSER
// ══════════════════════════════════════════════════════════
// US catalogs are moving from one-file-per-store to one file per region, with a
// column per store flagged "x" — the shape Canada has always used. Both formats
// are live during the rollout, so this parser is selected by CONTENT, never by
// filename or sheet name. Three observed facts make that necessary:
//   - the same sheet name holds different formats in different files right now
//     (Tri-State's "MASTER CATALOG - FRESH" is the new shape; Boston's is the
//     old one, with the new shape on a WIP tab beside it)
//   - the WIP tabs are named inconsistently ("... FRESH WIP" vs "WIP MASTER
//     CATALOG -FRESH") and will be renamed again once they replace the old tabs
//   - Canada's workbook has a REMOVED sheet that matches the real catalog on
//     header score, store flags and the Catapult ID column. Only produce row
//     count separates them.
//
// So: a candidate sheet must have at least one column headed with a known store
// code AND a Catapult ID column; among candidates the most produce rows wins.
// The Catapult ID requirement is what rejects ORDER GUIDE, which carries the
// same store flags but uses a plain "ID" column.

// Store codes seen in catalog headers that are deliberately NOT parsed.
// NY/DT/LF are BOH-only (no retail produce). BG is not a produce store.
// PO is Post Oaks, Houston — opening later; adding it to STORES is all that's
// needed to switch it on.
const KNOWN_INACTIVE_STORE_COLS = ["NY", "DT", "LF", "BG", "PO"];

function regionalHeaderScore(row) {
  const cells = (row || []).map((c) => String(c ?? "").trim().toUpperCase());
  const has = (kw) => cells.some((c) => c.includes(kw));
  return ["BOH", "CATAPULT", "STATUS", "CATEGORY", "DEPART", "PRODUCT"]
    .reduce((n, kw) => n + (has(kw) ? 1 : 0), 0);
}

function regionalColumns(headerRow) {
  const headers = (headerRow || []).map((h) => String(h ?? "").trim());
  const c = {};
  let gotStatus = false;
  headers.forEach((h, i) => {
    const cu = h.toUpperCase();
    if ((cu === "CATEGORY" || cu.includes("DEPART")) && c.Dept == null) c.Dept = i;
    if (cu === "BOH/RTL" && c.RTL == null) c.RTL = i;
    if (((cu.includes("D365") && cu.includes("ITEM")) || cu === "PRODUCT ID") && c.D365_ID == null) c.D365_ID = i;
    if (cu.includes("CATAPULT") && c.Catapult == null) c.Catapult = i;
    if ((cu.includes("PRODUCT NAME") || cu.includes("PRODUCT DESC") || cu === "DESCRIPTION") && c.Name == null) c.Name = i;
    if (cu.includes("SALE") && cu.includes("UNIT") && !cu.includes("PURCHASE") && !cu.includes("PACKING") && !cu.includes("INVENTORY") && c.SaleUnit == null) c.SaleUnit = i;
    if ((cu.includes("VENDOR NAME") || cu === "VENDOR") && c.Vendor == null) c.Vendor = i;
    if ((cu.includes("VENDOR PRODUCT") || cu.includes("VENDORPRODUCT") || cu === "VPN") && c.VPN == null) c.VPN = i;
    // Two columns can contain "STATUS" ("Status (Pre-Order/Stock/Backup)" and
    // "Additional Product Status"). Lock onto the primary one.
    if (cu.includes("STATUS")) {
      const isPrimary = cu === "STATUS" || cu.includes("BACKUP") || cu.includes("BACK-UP") ||
        cu.includes("PRE-ORDER") || cu.includes("PREORDER") || cu.includes("STOCK");
      if (isPrimary && !gotStatus) { c.Status = i; gotStatus = true; }
      else if (!gotStatus && c.Status == null && !cu.includes("ADDITIONAL")) c.Status = i;
    }
  });
  if (c.RTL == null) headers.forEach((h, i) => { if (h.toUpperCase().includes("BOH") && c.RTL == null) c.RTL = i; });
  return c;
}

// Exact header match only. "Any short uppercase header" would wrongly catch the
// ID / OH / ORD columns that order-guide sheets carry.
function regionalStoreCols(headerRow) {
  const headers = (headerRow || []).map((h) => String(h ?? "").trim().toUpperCase());
  const active = {}, inactive = [];
  headers.forEach((h, i) => {
    if (STORES.some((s) => s.abbr === h)) { if (active[h] == null) active[h] = i; }
    else if (KNOWN_INACTIVE_STORE_COLS.includes(h)) { if (!inactive.includes(h)) inactive.push(h); }
  });
  return { active, inactive };
}

const rowIsProduce = (row, c) =>
  String(row[c.Dept] ?? "").toUpperCase().includes("PRODUCE") &&
  ["RTL", "BOH/RTL"].includes(String(row[c.RTL] ?? "").trim().toUpperCase());

// Status rule: exclude anything containing BACK (BACKUP / BACK-UP / BACK UP /
// back up all appear in real files) and anything marked PRODUCTION (made
// in-house, no purchasable cost). Everything else is kept — STOCK, every
// spelling of PRE-ORDER, SEASONAL, CONSIGNMENT, EVENT and blanks — because an
// allowlist silently drops values that turn up without warning. West Coast
// currently ships produce with no status at all.
const statusExcludedFromPricing = (s) => {
  const u = String(s ?? "").trim().toUpperCase();
  return u.includes("BACK") || u === "PRODUCTION";
};

// Find the best regional sheet in a workbook. Returns null if this workbook
// isn't in the regional format, which is the signal to fall back to the
// per-store parser.
// Read just the first `nRows` rows of a sheet. Scanning every sheet in full is
// not viable: these workbooks carry RelProd (~55k rows) and InvMast (~58k rows
// x 100 cols) alongside the catalog, and parsing those to find a header row
// locks the browser for minutes. Header detection only ever needs the top.
function peekRows(ws, nRows = 12) {
  if (!ws || !ws["!ref"]) return [];
  try {
    const full = XLSX.utils.decode_range(ws["!ref"]);
    const end = Math.min(full.e.r, full.s.r + nRows - 1);
    const range = { s: { r: full.s.r, c: full.s.c }, e: { r: end, c: full.e.c } };
    return XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", range });
  } catch { return []; }
}

function findRegionalSheet(wb) {
  // Pass 1 — cheap. Header rows only, to find sheets that look regional.
  const candidates = [];
  for (const nm of wb.SheetNames) {
    const ws = wb.Sheets[nm];
    if (!ws) continue;
    const head = peekRows(ws, 12);
    if (!head.length) continue;
    let hdrRow = 0, best = -1;
    for (let i = 0; i < head.length; i++) {
      const s = regionalHeaderScore(head[i]);
      if (s > best) { best = s; hdrRow = i; }
    }
    const cols = regionalColumns(head[hdrRow]);
    const { active, inactive } = regionalStoreCols(head[hdrRow]);
    // Must have store flags AND a Catapult ID column. The second condition is
    // what rejects ORDER GUIDE, which has the flags but only a plain "ID".
    if (!Object.keys(active).length || cols.Catapult == null) continue;
    if (cols.Dept == null || cols.RTL == null || cols.D365_ID == null) continue;
    candidates.push({ sheet: nm, hdrRow, cols, active, inactive });
  }
  if (!candidates.length) return null;

  // Pass 2 — full read, candidates only (normally one or two). Most produce
  // rows wins: that is what separates Canada's real catalog from its REMOVED
  // sheet, which matches on every other signal.
  let pick = null;
  for (const c of candidates) {
    let raw;
    try { raw = XLSX.utils.sheet_to_json(wb.Sheets[c.sheet], { header: 1, defval: "" }); } catch { continue; }
    let n = 0;
    for (let r = c.hdrRow + 1; r < raw.length; r++) if (rowIsProduce(raw[r], c.cols)) n++;
    if (!pick || n > pick.produceRows) pick = { ...c, raw, produceRows: n };
  }
  return pick && pick.produceRows > 0 ? pick : null;
}

// Returns { byStore, catapultByStore, meta }.
//   byStore         — pricing rows, PRODUCTION excluded
//   catapultByStore — the POS/purchasing ID bridge for Sales + Audit, which
//                     KEEPS production items. They ring in Catapult, so leaving
//                     them out would dump them into Audit's "sold, not in
//                     catalog" bucket, which is supposed to mean "nobody knows
//                     what this is".
function parseRegionalCatalog(wb) {
  const pick = findRegionalSheet(wb);
  if (!pick) return null;
  const { raw, hdrRow, cols, active, inactive, sheet } = pick;

  const byStore = {}, catapultByStore = {}, seenPricing = {}, seenBridge = {};
  for (const abbr of Object.keys(active)) {
    byStore[abbr] = []; catapultByStore[abbr] = [];
    seenPricing[abbr] = new Set(); seenBridge[abbr] = new Set();
  }
  const meta = { sheet, headerRow: hdrRow, stores: Object.keys(active), inactiveCols: inactive,
    rows: 0, excludedBackup: 0, excludedProduction: 0, dupRows: 0, noCatapult: 0 };

  for (let r = hdrRow + 1; r < raw.length; r++) {
    const row = raw[r];
    if (!rowIsProduce(row, cols)) continue;
    meta.rows++;

    const statusRaw = cols.Status != null ? String(row[cols.Status] ?? "").trim() : "";
    const statusU = statusRaw.toUpperCase();
    const isBackup = statusU.includes("BACK");
    const isProduction = statusU === "PRODUCTION";
    if (isBackup) meta.excludedBackup++;
    if (isProduction) meta.excludedProduction++;

    // trim() also strips the non-breaking spaces seen in Tri-State's D365 values.
    let d365 = String(row[cols.D365_ID] ?? "").trim();
    if (d365 && d365.includes(".") && /^\d+\.0+$/.test(d365)) d365 = String(parseInt(parseFloat(d365)));
    const catapult = String(row[cols.Catapult] ?? "").trim().replace(/\.0$/, "");
    if (!d365 && !catapult) continue;
    if (!catapult) meta.noCatapult++;

    const name = String(row[cols.Name] ?? "").trim();
    // Sale units arrive mixed case in every region (ea/EA/lb/LB). Normalise, or
    // anything comparing against "LB"/"KG" silently fails on half the rows.
    const unit = String(row[cols.SaleUnit] ?? "").trim().toUpperCase() || "EA";
    const vendor = String(row[cols.Vendor] ?? "").trim();
    const vpn = String(row[cols.VPN] ?? "").trim().replace(/\.0$/, "");

    for (const [abbr, ix] of Object.entries(active)) {
      const flag = String(row[ix] ?? "").trim().toUpperCase();
      if (flag !== "X" && flag !== "REQ") continue;

      // ID bridge keeps production, drops backup (a backup row is not the sell item).
      if (!isBackup) {
        const bKey = d365 || "C" + catapult;
        if (!seenBridge[abbr].has(bKey)) {
          seenBridge[abbr].add(bKey);
          catapultByStore[abbr].push({ d365, cid: catapult, name, status: statusU });
        }
      }

      if (statusExcludedFromPricing(statusRaw)) continue;
      // De-duplicate identical rows. Dual-vendor variants differ by VPN and are
      // kept; exact repeats (seen on florals) are not.
      const pKey = `${d365}|${vpn}|${vendor}`;
      if (seenPricing[abbr].has(pKey)) { meta.dupRows++; continue; }
      seenPricing[abbr].add(pKey);
      byStore[abbr].push({ d365, catapult, name, unit, vendor, vpn, status: statusU });
    }
  }
  return { byStore, catapultByStore, meta };
}

// ── Legacy per-store catalog (one file per store, no store-flag columns).
// Still used by any store that hasn't moved to the regional format yet.
function parseCatalog(wb, storeConfig) {
  const sheetName = storeConfig.sheet;
  const hdrRow = storeConfig.headerRow;
  const ws = wb.Sheets[sheetName] || wb.Sheets[wb.SheetNames[0]];
  if (!ws) return [];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  if (raw.length <= hdrRow) return [];

  // Build column map from header row
  const headers = raw[hdrRow].map((h) => String(h).trim());
  const colMap = {};
  let gotId = false;
  let gotStatus = false;
  headers.forEach((h, i) => {
    const cu = h.toUpperCase();
    if ((cu.includes("DEPART") || cu === "CATEGORY") && !colMap.Dept) colMap.Dept = i;
    if ((cu === "BOH/RTL" || cu === "RTL" || cu.includes("BOH")) && !colMap.RTL) colMap.RTL = i;
    if (!gotId && (cu.includes("D365 ITEM") || cu === "PRODUCT ID")) { colMap.D365_ID = i; gotId = true; }
    if (cu.includes("PRODUCT DESC") || cu === "DESCRIPTION") colMap.Name = i;
    if (cu.includes("SALE") && cu.includes("UNIT") && !cu.includes("PURCHASE") && !cu.includes("PACKING") && !cu.includes("INVENTORY")) colMap.SaleUnit = i;
    if (cu.includes("VENDOR NAME") || cu === "VENDOR") colMap.Vendor = i;
    if (cu.includes("VENDOR PRODUCT") || cu.includes("VENDORPRODUCT") || cu === "VPN") colMap.VPN = i;
    // Status: prefer the primary status column; don't let "Additional Product Status" override it.
    if (cu.includes("STATUS")) {
      const isPrimary = cu === "STATUS" || cu.includes("BACKUP") || cu.includes("BACK-UP") || cu.includes("PRE-ORDER") || cu.includes("PREORDER") || cu.includes("STORE");
      if (isPrimary && !gotStatus) { colMap.Status = i; gotStatus = true; }
      else if (!gotStatus && colMap.Status == null && !cu.includes("ADDITIONAL")) colMap.Status = i;
    }
  });
  if (!gotId) {
    headers.forEach((h, i) => { if (h.toUpperCase().trim() === "ID" && !colMap.D365_ID) colMap.D365_ID = i; });
  }
  if (!gotId) {
    headers.forEach((h, i) => { if (h.toUpperCase().includes("D365") && !h.toUpperCase().includes("VENDOR")) colMap.D365_ID = i; });
  }

  const items = [];
  for (let r = hdrRow + 1; r < raw.length; r++) {
    const row = raw[r];
    const dept = String(row[colMap.Dept] || "").toUpperCase();
    const rtl = String(row[colMap.RTL] || "").toUpperCase();
    if (!dept.includes("PRODUCE") || !rtl.includes("RTL")) continue;
    // Require Status = STOCK (exclude Backup, Backup 2, blank, etc.)
    if (colMap.Status != null) {
      const status = String(row[colMap.Status] || "").toUpperCase().trim();
      if (status !== "STOCK") continue;
    }
    let d365 = String(row[colMap.D365_ID] || "").trim();
    if (d365 && d365.includes(".")) d365 = String(parseInt(parseFloat(d365)));
    items.push({
      d365,
      name: String(row[colMap.Name] || "").trim(),
      unit: String(row[colMap.SaleUnit] || "EA").trim().toUpperCase(),  // files ship ea/EA/lb/LB mixed
      vendor: String(row[colMap.Vendor] || "").trim(),
      vpn: String(row[colMap.VPN] || "").trim().replace(/\.0$/, ""),
    });
  }
  return items;
}

// --- BALDOR PARSER ---
function parseBaldor(wb) {
  const sheetName = wb.SheetNames.includes("ALL ITEMS") ? "ALL ITEMS" : wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });
  const items = {};
  for (const row of raw) {
    const num = String(row.item || row.Item || row.NUMBER || row.Number || "").trim();
    if (!num) continue;
    items[num] = {
      Number: num,
      Description: String(row.description || row.Description || ""),
      Size: String(row.package || row.Package || row.SIZE || row.Size || ""),
      Measure: String(row.unit_Size || row.Unit_Size || row.MEASURE || row.Measure || ""),
      Price: parseFloat(row.price || row.Price) || null,
    };
  }
  return items;
}

// Comp history rides along with sales retention (26 weeks) rather than pricing's
// 8, because the comp line is a seasonal story.
const COMP_WEEKS_KEPT = 26;
function trimCompWeeks(weeks) {
  const keys = Object.keys(weeks || {}).sort();
  if (keys.length <= COMP_WEEKS_KEPT) return weeks;
  const keep = keys.slice(-COMP_WEEKS_KEPT);
  const out = {};
  for (const k of keep) out[k] = weeks[k];
  return out;
}

// ══════════════════════════════════════════════════════════
// COMPETITIVE (WHOLE FOODS) PRICING
// ══════════════════════════════════════════════════════════
// Two inputs, deliberately kept apart:
//   the weekly report  -> stored RAW, exactly as it arrives
//   the mapping file   -> stored separately, replaced wholesale
// Comp prices are DERIVED from the two at read time rather than resolved once
// and stored. That is what makes the mapping a real source of truth: fix a
// wrong WF ID or a bad multiplier and every past week corrects itself, with no
// re-uploads. Resolving at write time would bake today's mistakes into history.

// Which Eataly stores a Whole Foods city stands in for.
const COMP_CITY_STORES = {
  "TORONTO": ["TO", "SG", "DM", "EC"],   // CAD, used unconverted
  "SANTA CLARA": ["SV"],
  "DALLAS": ["DA"],
  "CHICAGO": ["CH"],
  "BOSTON": ["BO"],
  "AVENTURA": ["AV", "WP"],
  "BEVERLY HILLS": ["LA"],
};
const COMP_STORE_CITY = (() => {
  const out = {};
  for (const [city, stores] of Object.entries(COMP_CITY_STORES)) for (const s of stores) out[s] = city;
  return out;
})();

const LB_PER_KG = 2.20462;
const normUnit = (u) => {
  const s = String(u ?? "").trim().toUpperCase();
  if (s === "EACH" || s === "EA") return "EA";
  if (s === "LB" || s === "LBS" || s === "POUND") return "LB";
  if (s === "KG" || s === "KILOGRAM" || s === "KILO") return "KG";
  return s;
};

// SheetJS turns the CSV's date column into an Excel serial; render it back.
function compAsOfDate(v) {
  if (v == null || v === "") return "";
  if (typeof v === "number" && v > 20000 && v < 80000) {
    const d = new Date(Date.UTC(1899, 11, 30) + v * 86400000);
    return d.toISOString().slice(0, 10);
  }
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v).trim();
}

function looksLikeCompReport(wb) {
  for (const nm of wb.SheetNames.slice(0, 3)) {
    const raw = XLSX.utils.sheet_to_json(wb.Sheets[nm], { header: 1, defval: "", range: 0 });
    for (const row of raw.slice(0, 5)) {
      const h = (row || []).map((c) => String(c ?? "").trim().toUpperCase());
      if (h.includes("STORE CITY") && h.includes("ITEM ID") && h.includes("ITEM PRICE")) return true;
    }
  }
  return false;
}

// Raw rows, untouched apart from trimming and unit normalisation. No mapping is
// applied here on purpose.
function parseCompReport(wb) {
  const nm = wb.SheetNames[0];
  const raw = XLSX.utils.sheet_to_json(wb.Sheets[nm], { header: 1, defval: "" });
  let hdrRow = 0;
  for (let i = 0; i < Math.min(raw.length, 6); i++) {
    const h = raw[i].map((c) => String(c ?? "").trim().toUpperCase());
    if (h.includes("ITEM ID") && h.includes("ITEM PRICE")) { hdrRow = i; break; }
  }
  const H = raw[hdrRow].map((c) => String(c ?? "").trim().toUpperCase());
  const ix = (name) => H.indexOf(name);
  const cCity = ix("STORE CITY"), cId = ix("ITEM ID"), cPrice = ix("ITEM PRICE"),
    cUom = ix("ITEM PRICE UOM"), cName = ix("ITEM NAME"), cAsOf = ix("AS OF DATE");
  if (cCity < 0 || cId < 0 || cPrice < 0) return null;

  const byCity = {};
  let asOf = "", skipped = 0, unknownCities = new Set();
  for (let r = hdrRow + 1; r < raw.length; r++) {
    const row = raw[r];
    const city = String(row[cCity] ?? "").trim().toUpperCase();
    const id = String(row[cId] ?? "").trim().toLowerCase();
    const price = parseFloat(row[cPrice]);
    if (!city || !id || !isFinite(price) || price <= 0) { skipped++; continue; }
    if (!COMP_CITY_STORES[city]) { unknownCities.add(city); continue; }
    if (!asOf && cAsOf >= 0) asOf = compAsOfDate(row[cAsOf]);
    // Item ID is unique within a city, so first write wins and later repeats
    // would be a data problem rather than a variant.
    (byCity[city] = byCity[city] || {});
    if (byCity[city][id] == null) {
      byCity[city][id] = { price, uom: normUnit(row[cUom]), name: String(row[cName] ?? "").trim() };
    }
  }
  return { byCity, asOf, skipped, unknownCities: [...unknownCities] };
}

function looksLikeCompMapping(wb) {
  for (const nm of wb.SheetNames.slice(0, 3)) {
    const raw = XLSX.utils.sheet_to_json(wb.Sheets[nm], { header: 1, defval: "", range: 0 });
    for (const row of raw.slice(0, 5)) {
      const h = (row || []).map((c) => String(c ?? "").trim().toUpperCase());
      if (h.some((x) => x.includes("WF ID")) && h.some((x) => x.includes("EATALY ID"))) return true;
    }
  }
  return false;
}

// -> { [d365]: { wfId, mult, eaUnit, wfUnit, name } }, first row winning on
// duplicates, as agreed: one Eataly item pointing at two WF items takes the
// first and the second is reported rather than silently dropped.
function parseCompMapping(wb) {
  const nm = wb.SheetNames.find((n) => n.toUpperCase().includes("MAPPING")) || wb.SheetNames[0];
  const raw = XLSX.utils.sheet_to_json(wb.Sheets[nm], { header: 1, defval: "" });
  let hdrRow = 0;
  for (let i = 0; i < Math.min(raw.length, 6); i++) {
    const h = raw[i].map((c) => String(c ?? "").trim().toUpperCase());
    if (h.some((x) => x.includes("EATALY ID")) && h.some((x) => x.includes("WF ID"))) { hdrRow = i; break; }
  }
  const H = raw[hdrRow].map((c) => String(c ?? "").trim().toUpperCase());
  const find = (pred) => H.findIndex(pred);
  const cE = find((h) => h.includes("EATALY ID"));
  const cW = find((h) => h.includes("WF ID"));
  const cMult = find((h) => h.includes("MULTIPLIER") || h.includes("CONVERSION"));
  // The two "Sales Unit" columns sit either side of the WF ID.
  const unitCols = H.map((h, i) => (h.includes("SALES UNIT") || h.includes("SALE UNIT") ? i : -1)).filter((i) => i >= 0);
  const cEU = unitCols.find((i) => i < cW);
  const cWU = unitCols.find((i) => i > cW);
  if (cE < 0 || cW < 0) return null;

  // An Eataly item may list more than one WF item (CORN, YELLOW points at both
  // Bi-color and White Corn). Keep them all in file order: at lookup time the
  // first one that actually has a price in that city wins, so a second row acts
  // as a fallback rather than being discarded.
  const map = {}, multi = [];
  for (let r = hdrRow + 1; r < raw.length; r++) {
    const row = raw[r];
    const d365 = String(row[cE] ?? "").trim();
    const wfId = String(row[cW] ?? "").trim().toLowerCase();
    if (!d365 || !wfId) continue;
    const m = cMult >= 0 ? parseFloat(row[cMult]) : NaN;
    const entry = {
      wfId,
      mult: isFinite(m) && m > 0 ? m : null,
      eaUnit: cEU >= 0 ? normUnit(row[cEU]) : "",
      wfUnit: cWU >= 0 ? normUnit(row[cWU]) : "",
      name: String(row[cW + 1] ?? "").trim(),
    };
    if (map[d365]) { map[d365].push(entry); multi.push(d365); }
    else map[d365] = [entry];
  }
  return { map, multi: [...new Set(multi)] };
}

// One item's comp price for one store, from raw rows + mapping.
// Conversion order: an explicit multiplier always wins; otherwise LB<->KG is
// arithmetic and applied automatically; otherwise the number passes through.
// A unit mismatch with no multiplier is reported, never guessed at.
function compPriceFor(d365, store, cityRows, mapping) {
  const alts = mapping && mapping[String(d365 ?? "").trim()];
  if (!alts || !alts.length) return null;
  const city = COMP_STORE_CITY[store];
  if (!city || !cityRows || !cityRows[city]) return null;
  let m = null, rec = null;
  for (const alt of alts) {                 // first alternative with a price here
    const hit = cityRows[city][alt.wfId];
    if (hit) { m = alt; rec = hit; break; }
  }
  if (!rec) return null;
  const wfU = rec.uom || m.wfUnit, eaU = m.eaUnit;
  let price = rec.price, how = "";
  if (m.mult != null) { price = rec.price * m.mult; how = `× ${m.mult}`; }
  else if (wfU === "KG" && eaU === "LB") { price = rec.price / LB_PER_KG; how = "KG→LB"; }
  else if (wfU === "LB" && eaU === "KG") { price = rec.price * LB_PER_KG; how = "LB→KG"; }
  else if (wfU && eaU && wfU !== eaU) { how = `unit mismatch (${wfU} vs ${eaU})`; }
  return { price: Math.round(price * 100) / 100, raw: rec.price, uom: wfU, how,
    mismatch: !!(wfU && eaU && wfU !== eaU && m.mult == null), name: rec.name, wfId: m.wfId };
}

// --- CW / DAIRYLAND PARSER ---
function parseCW(wb, region) {
  // Try the region sheet (NY, FL, etc.)
  const sheetName = wb.SheetNames.find((s) => s.toUpperCase() === region.toUpperCase()) || wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  if (!ws) return {};
  const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });
  const items = {};
  for (const row of raw) {
    const num = String(row["Item ID"] || row.item_id || row.Number || "").trim();
    if (!num) continue;
    items[num] = {
      Number: num,
      Description: String(row.Item || row.Description || ""),
      Size: String(row["Pack Size"] || row.Size || ""),
      Measure: String(row["Price UOM"] || row.Measure || ""),
      Price: parseFloat(row.Price || row.price) || null,
    };
  }
  return items;
}

// --- FRESHPOINT S. FLORIDA PARSER ---
// File: FP_FL.xlsx — header row 1 (0-indexed), columns: Number, Description, Size, Measure, Date, Price
function parseFreshpointFL(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  // Header is row index 1 (skip row 0), data starts row 2
  for (let r = 2; r < raw.length; r++) {
    const row = raw[r];
    const num = String(row[0] || "").trim();
    if (!num) continue;
    items[num] = {
      Number: num,
      Description: String(row[1] || ""),
      Size: String(row[2] || ""),
      Measure: String(row[3] || ""),
      Price: parseFloat(row[5]) || null,
    };
  }
  return items;
}

// --- FRESHPOINT DALLAS PARSER ---
// File: FP_DA.xlsx — header row 1, cols: [0]=Number, [1]=Description, [5]=Size, [6]=Measure, last=Price
function parseFreshpointDA(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  for (let r = 2; r < raw.length; r++) {
    const row = raw[r];
    const num = String(row[0] || "").trim();
    if (!num) continue;
    items[num] = {
      Number: num,
      Description: String(row[1] || ""),
      Size: String(row[5] || ""),
      Measure: String(row[6] || ""),
      Price: parseFloat(row[row.length - 1]) || null,
    };
  }
  return items;
}

// --- FRESHPOINT S. CALIFORNIA PARSER ---
// File: FP_SC.xlsx — no header, cols: Number, Description, Size, Measure, BeginDate, EndDate, Price
function parseFreshpointSC(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  for (let r = 0; r < raw.length; r++) {
    const row = raw[r];
    const num = String(row[0] || "").trim();
    if (!num || num === "Number") continue; // skip if header row
    items[num] = {
      Number: num,
      Description: String(row[1] || ""),
      Size: String(row[2] || ""),
      Measure: String(row[3] || ""),
      Price: parseFloat(row[6]) || null,
    };
  }
  return items;
}

// --- MR GREENS / FLORIDA VEG PARSER ---
// Dual-column layout: cols 0-4 = set1, cols 6-10 = set2 (if present). Filter to CASE measure.
function parseMrGreens(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  // Row 0 is header, data starts row 1
  for (let r = 1; r < raw.length; r++) {
    const row = raw[r];
    // Set 1: Description[0], Size[1], Number[2], Measure[3], Price[4]
    if (row[2] && String(row[3]).toUpperCase().trim() === "CASE") {
      let num = String(row[2]).trim();
      if (num.endsWith(".0")) num = num.slice(0, -2);
      items[num] = {
        Number: num,
        Description: String(row[0] || ""),
        Size: String(row[1] || ""),
        Measure: "CASE",
        Price: parseFloat(row[4]) || null,
      };
    }
    // Set 2: Description[6], Size[7], Number[8], Measure[9], Price[10]
    if (row.length > 10 && row[8] && String(row[9]).toUpperCase().trim() === "CASE") {
      let num = String(row[8]).trim();
      if (num.endsWith(".0")) num = num.slice(0, -2);
      items[num] = {
        Number: num,
        Description: String(row[6] || ""),
        Size: String(row[7] || ""),
        Measure: "CASE",
        Price: parseFloat(row[10]) || null,
      };
    }
  }
  return items;
}

// --- TESTA PARSER ---
// Columns: Number, Sheet, Description, Size, Measure, Price, SoldPrev
function parseTesta(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  for (let r = 1; r < raw.length; r++) {
    const row = raw[r];
    const num = String(row[0] || "").trim();
    if (!num) continue;
    items[num] = {
      Number: num,
      Description: String(row[2] || ""),
      Size: String(row[3] || ""),
      Measure: String(row[4] || ""),
      Price: parseFloat(row[5]) || null,
    };
  }
  return items;
}

// --- MIDWEST PARSER ---
// Columns: Number, Description, Size, Price
function parseMidwest(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  for (let r = 1; r < raw.length; r++) {
    const row = raw[r];
    const num = String(row[0] || "").trim();
    if (!num) continue;
    items[num] = {
      Number: num,
      Description: String(row[1] || ""),
      Size: String(row[2] || ""),
      Measure: "CASE",
      Price: parseFloat(row[3]) || null,
    };
  }
  return items;
}

// --- HARDIE'S PARSER ---
// Dual-column wide format: Set1 cols C,F,I,M (2,5,8,12) and Set2 cols R,V,Y,AB (17,21,24,27)
// Also handles header-based format if present
function parseHardies(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  if (raw.length < 2) return items;

  // Check if first row is header with ITEM/DESCRIPTION columns
  const r0 = raw[0].map((v) => String(v).trim().toUpperCase());
  const isNewFormat = r0.includes("ITEM") && r0.includes("DESCRIPTION");

  if (isNewFormat) {
    const itemCol = r0.indexOf("ITEM");
    const descCol = r0.indexOf("DESCRIPTION");
    const packCol = r0.findIndex((h) => h.includes("PACK") || h.includes("SIZE"));
    const priceCol = r0.findIndex((h) => h.includes("PRICE") || h.includes("COST"));
    for (let r = 1; r < raw.length; r++) {
      const row = raw[r];
      const n = row[itemCol], d = row[descCol], pk = packCol >= 0 ? row[packCol] : "", pr = priceCol >= 0 ? row[priceCol] : "";
      if (n != null && n !== "" && !isNaN(parseFloat(pr))) {
        let num = typeof n === "number" ? String(Math.round(n)) : String(n).trim();
        if (num.endsWith(".0")) num = num.slice(0, -2);
        items[num] = { Number: num, Description: String(d || ""), Size: String(pk || ""), Measure: "CASE", Price: parseFloat(pr) || null };
      }
    }
  } else {
    // Wide format with two sets of items side by side
    // Set 1: col 2=VPN, col 5=Desc, col 8=Pack, col 12=Price
    // Set 2: col 17=VPN, col 21=Desc, col 24=Pack, col 27=Price
    const colSets = [[2, 5, 8, 12], [17, 21, 24, 27]];
    for (let r = 1; r < raw.length; r++) {
      const row = raw[r];
      for (const [ic, dc, pc, prc] of colSets) {
        if (prc >= row.length) continue;
        const n = row[ic], d = row[dc], pk = row[pc], pr = row[prc];
        if (n != null && n !== "" && pr != null && pr !== "" && !isNaN(parseFloat(pr))) {
          let num = typeof n === "number" ? String(Math.round(n)) : String(n).trim();
          if (num.endsWith(".0")) num = num.slice(0, -2);
          items[num] = { Number: num, Description: String(d || ""), Size: String(pk || ""), Measure: "CASE", Price: parseFloat(pr) || null };
        }
      }
    }
  }
  return items;
}

// --- OK PRODUCE PARSER ---
// Skiprows=6, cols: [0]=Number, [1]=Description, [5]=Price (preferred) or [3]=Price fallback
// Parses weight/count/pack from description inline
function parseOKProduce(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  // Auto-detect header row by scanning for "ITEM" in col 0
  let startRow = 6; // default
  for (let r = 0; r < Math.min(15, raw.length); r++) {
    const c0 = String(raw[r][0] || "").toUpperCase().trim();
    if (c0.includes("ITEM") && (c0.includes("#") || c0.includes("ID") || c0.includes("NO"))) {
      startRow = r + 2; // skip header row + any sub-header rows
      break;
    }
  }
  // Find price column: prefer col 5, fallback col 3, or last numeric col
  for (let r = startRow; r < raw.length; r++) {
    const row = raw[r];
    let num = row[0], desc = row[1];
    if (num == null || num === "" || num === 0) continue;
    const descStr = String(desc || "").trim();
    if (descStr.includes("New/Now") || descStr.includes("NEW/NOW")) continue;
    // Price: prefer col 5, then col 3, then last col
    let price;
    if (row.length > 5 && row[5] != null && row[5] !== "" && row[5] !== 0) price = parseFloat(row[5]);
    else if (row.length > 3 && row[3] != null && row[3] !== "") price = parseFloat(row[3]);
    else price = parseFloat(row[row.length - 1]);
    if (isNaN(price)) continue;
    // Parse size from description
    let sz = "", okWeight = null, okPack = null, okCount = null;
    const d = descStr;
    let m;
    m = d.match(/(\d+)\/(\d+\.?\d*)#/);
    if (m) { okPack = parseInt(m[1]); sz = `${m[1]}/${m[2]}#`; }
    else if ((m = d.match(/(\d+)\/(\d+\.?\d*)\s*[Oo][Zz]/))) { okPack = parseInt(m[1]); sz = `${m[1]}/${m[2]}Oz`; }
    else if ((m = d.match(/(\d+)\/(\d+)\s*[Cc][Tt].*?(\d+\.?\d*)#/))) { okCount = (parseInt(m[1]) + parseInt(m[2])) / 2; okWeight = parseFloat(m[3]); sz = `${m[1]}/${m[2]}Ct ${m[3]}#`; }
    else if ((m = d.match(/(\d+)\s*[Cc][Tt].*?(\d+\.?\d*)#/))) { okCount = parseInt(m[1]); okWeight = parseFloat(m[2]); sz = `${m[1]}Ct ${m[2]}#`; }
    else if ((m = d.match(/(\d+)\/(\d+)\s*[Cc][Tt]/))) { okCount = (parseInt(m[1]) + parseInt(m[2])) / 2; sz = `${m[1]}/${m[2]}Ct`; }
    else if ((m = d.match(/(\d+)\s*[Cc][Tt]/))) { okCount = parseInt(m[1]); sz = `${m[1]}Ct`; }
    else if ((m = d.match(/(\d+\.?\d*)#/))) { okWeight = parseFloat(m[1]); sz = `${m[1]}#`; }

    let numStr = typeof num === "number" ? String(Math.round(num)) : String(num).trim();
    if (numStr.endsWith(".0")) numStr = numStr.slice(0, -2);
    const itemData = {
      Number: numStr,
      Description: descStr,
      Size: sz,
      Measure: "CASE",
      Price: price,
      OK_Weight_LB: okWeight,
      OK_Pack_Qty: okPack,
      OK_Count: okCount,
    };
    // Store under full number
    items[numStr] = itemData;
    // Also store under 5-digit key (strip trailing "00") for catalog VPN matching
    if (numStr.length === 7 && numStr.endsWith("00")) {
      items[numStr.slice(0, 5)] = itemData;
    }
    // And stripped of leading zeros
    const stripped = numStr.replace(/^0+/, "");
    if (stripped !== numStr) items[stripped] = itemData;
  }
  return items;
}

// --- VESTA PARSER ---
// Skiprows=10, cols: Description, Size, Number, Price
function parseVesta(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  for (let r = 10; r < raw.length; r++) {
    const row = raw[r];
    const num = String(row[2] || "").trim();
    if (!num) continue;
    items[num] = {
      Number: num,
      Description: String(row[0] || ""),
      Size: String(row[1] || ""),
      Measure: "CASE",
      Price: parseFloat(row[3]) || null,
    };
  }
  return items;
}

// --- CHARLIE'S PARSER ---
// Header row 0. 7-col: Description,Brand,Pack,Size,Weight,Number,Price. 6-col: Description,Pack,Size,Weight,Number,Price
function parseCharlies(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  if (raw.length < 2) return items;
  const colCount = raw[0].length;
  const has7 = colCount >= 7;
  for (let r = 1; r < raw.length; r++) {
    const row = raw[r];
    let desc, pack, size, weight, num, price;
    if (has7) {
      [desc, , pack, size, weight, num, price] = row; // skip Brand col
    } else {
      [desc, pack, size, weight, num, price] = row;
    }
    const numStr = String(num || "").trim();
    if (!numStr) continue;
    const weightLB = parseFloat(weight) || null;
    const packQty = parseFloat(pack) || null;
    items[numStr] = {
      Number: numStr,
      Description: String(desc || ""),
      Size: String(size || ""),
      Measure: "CASE",
      Price: parseFloat(price) || null,
      Weight_LB: weightLB,
      Pack_Qty: packQty,
    };
  }
  return items;
}

// ══════════════════════════════════════════════════════════
// CANADA VENDOR PARSERS
// ══════════════════════════════════════════════════════════

// --- ZEUS PARSER ---
// Sheet: "Price List (products)", header row 9, cols: Product Code, Description, Size, Price
// Dash in price = not quoted this week
function parseZeus(wb) {
  const sheetName = wb.SheetNames.find((s) => s.toLowerCase().includes("price list")) || wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  // Find header row (usually row 9, but scan for "Product Code")
  let hdrRow = 9;
  for (let r = 0; r < Math.min(15, raw.length); r++) {
    const rowStr = raw[r].map((v) => String(v).toUpperCase());
    if (rowStr.some((h) => h.includes("PRODUCT CODE") || h.includes("PROD CODE"))) { hdrRow = r; break; }
  }
  // Map columns from header
  const headers = raw[hdrRow] ? raw[hdrRow].map((h) => String(h).trim().toUpperCase()) : [];
  const codeCol = headers.findIndex((h) => h.includes("PRODUCT CODE") || h.includes("PROD CODE") || h === "CODE");
  const descCol = headers.findIndex((h) => h.includes("DESCRIPTION") || h === "DESC");
  const sizeCol = headers.findIndex((h) => h.includes("SIZE") || h.includes("PACK"));
  const priceCol = headers.findIndex((h) => h.includes("PRICE") || h.includes("COST"));

  for (let r = hdrRow + 1; r < raw.length; r++) {
    const row = raw[r];
    const code = String(row[codeCol >= 0 ? codeCol : 0] || "").trim();
    if (!code) continue;
    const priceRaw = row[priceCol >= 0 ? priceCol : 3];
    // Dash means not quoted
    if (priceRaw === "-" || priceRaw === "—" || priceRaw === "" || priceRaw == null) {
      items[code] = { Number: code, Description: String(row[descCol >= 0 ? descCol : 1] || ""), Size: String(row[sizeCol >= 0 ? sizeCol : 2] || ""), Measure: "CASE", Price: null, notQuoted: true };
      continue;
    }
    const price = parseFloat(priceRaw);
    if (isNaN(price)) continue;
    items[code] = {
      Number: code,
      Description: String(row[descCol >= 0 ? descCol : 1] || ""),
      Size: String(row[sizeCol >= 0 ? sizeCol : 2] || ""),
      Measure: "CASE",
      Price: price,
    };
  }
  return items;
}

// --- BONDI PARSER ---
// Header row 0, cols: PROD#, DESCRIPTION, PACKAGING, PRICE, ORIGIN
// Leading zeros on PROD# — strip before matching
// EACH/BUNCH in packaging = per-unit pricing
function parseBondi(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });
  const items = {};
  for (const row of raw) {
    let num = String(row["PROD#"] || row["Prod#"] || row["PROD"] || row["Item"] || "").trim();
    if (!num) continue;
    // Strip leading zeros
    num = num.replace(/^0+/, "") || "0";
    const price = parseFloat(row.PRICE || row.Price || row.price);
    if (isNaN(price)) continue;
    const packaging = String(row.PACKAGING || row.Packaging || row.Size || "").trim();
    items[num] = {
      Number: num,
      Description: String(row.DESCRIPTION || row.Description || ""),
      Size: packaging,
      Measure: "CASE",
      Price: price,
    };
  }
  return items;
}

// --- BURNAC / PROVINCIAL FRUIT PARSER ---
// Sheet name changes weekly ("Active Cost Sheet Week XX"). Header row 1.
// Key cols: "BURNAC ITEM # " (trailing space), "CASE PACK", "CASE WEIGHT\n(LB)"
// Price col: find col containing both "FINAL" and "BILLOUT" in header
// Item numbers may have suffix "v:multiple" — strip it
function parseBurnac(wb) {
  // Find the active cost sheet
  const sheetName = wb.SheetNames.find((s) => s.toUpperCase().includes("ACTIVE COST")) || wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const items = {};
  if (raw.length < 3) return items;

  // Header is row 1 (0-indexed)
  const headers = raw[1].map((h) => String(h).trim().toUpperCase().replace(/\n/g, " "));
  // Find columns
  const itemCol = headers.findIndex((h) => h.includes("BURNAC ITEM") || h.includes("ITEM #") || h.includes("ITEM NO"));
  const packCol = headers.findIndex((h) => h === "CASE PACK" || h.includes("CASE PACK"));
  const weightCol = headers.findIndex((h) => h.includes("CASE WEIGHT") && h.includes("LB"));
  // Price col: find one containing FINAL and BILLOUT
  let priceCol = headers.findIndex((h) => h.includes("FINAL") && h.includes("BILLOUT"));
  if (priceCol < 0) priceCol = headers.findIndex((h) => h.includes("FINAL") || h.includes("BILLOUT"));
  if (priceCol < 0) priceCol = headers.findIndex((h) => h.includes("PRICE") || h.includes("COST"));
  const descCol = headers.findIndex((h) => h.includes("DESCRIPTION") || h.includes("DESC") || h.includes("PRODUCT"));

  for (let r = 2; r < raw.length; r++) {
    const row = raw[r];
    let num = String(row[itemCol >= 0 ? itemCol : 0] || "").trim();
    if (!num) continue;
    // Strip v:multiple suffix
    num = num.replace(/\s*v:.*$/i, "").trim();
    // Strip trailing .0
    if (num.endsWith(".0")) num = num.slice(0, -2);

    const price = priceCol >= 0 ? parseFloat(row[priceCol]) : null;
    if (price == null || isNaN(price)) continue;

    const casePack = packCol >= 0 ? row[packCol] : "";
    const caseWeight = weightCol >= 0 ? parseFloat(row[weightCol]) : null;

    items[num] = {
      Number: num,
      Description: descCol >= 0 ? String(row[descCol] || "") : "",
      Size: String(casePack || ""),
      Measure: "CASE",
      Price: price,
      CaseWeight_LB: !isNaN(caseWeight) ? caseWeight : null,
      CasePack: casePack ? parseInt(String(casePack).replace(/[^\d]/g, "")) || null : null,
    };
  }
  return items;
}

// --- CANADA CATALOG PARSER ---
// Single file for all 4 stores. Store flag columns (TO, SG, DM, EC) with x/X/REQ = carries item
// Sheet: "CAN MASTER CATALOG - FRESH", header row 2
// Filter: CATEGORY contains PRODUCE, BOH/RTL contains RTL, Status NOT BACK
// Item IDs have leading zeros — strip
function parseCanadaCatalog(wb) {
  const sheetName = wb.SheetNames.find((s) => s.toUpperCase().includes("CAN MASTER")) || wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  // Detect the header row — different exports put it at different offsets (some have
  // two title rows above it, some none). Score the first dozen rows by how many
  // expected column names they contain and pick the best; fall back to row 2.
  const headerScore = (row) => {
    if (!row) return 0;
    const cells = row.map((c) => String(c).trim().toUpperCase());
    const has = (kw) => cells.some((c) => c.includes(kw));
    let s = 0;
    if (has("BOH")) s++;
    if (has("CATEGORY")) s++;
    if (has("D365") || has("ITEM NO")) s++;
    if (has("PRODUCT NAME")) s++;
    if (has("VENDOR NAME")) s++;
    return s;
  };
  let hdrRow = 2, best = 0;
  for (let i = 0; i < Math.min(raw.length, 12); i++) {
    const s = headerScore(raw[i]);
    if (s > best) { best = s; hdrRow = i; }
  }
  if (best < 2) hdrRow = 2; // nothing looked like a header — keep legacy default
  if (raw.length <= hdrRow) return {};

  const headers = raw[hdrRow].map((h) => String(h).trim());
  const colMap = {};
  let gotStatus = false; // lock onto the primary status column once found
  headers.forEach((h, i) => {
    const cu = h.toUpperCase();
    if ((cu.includes("D365") && cu.includes("ITEM")) || cu === "PRODUCT ID" || (cu.includes("ITEM NO") && !cu.includes("VENDOR"))) colMap.D365_ID = i;
    if (cu === "CATEGORY" || cu.includes("DEPART")) colMap.Dept = i;
    if (cu === "BOH/RTL" || cu.includes("BOH")) colMap.RTL = i;
    if (cu.includes("PRODUCT NAME") || cu.includes("PRODUCT DESC") || cu === "DESCRIPTION") colMap.Name = i;
    if (cu.includes("SALE") && cu.includes("UNIT") && !cu.includes("PURCHASE") && !cu.includes("PACKING") && !cu.includes("INVENTORY")) colMap.SaleUnit = i;
    if (cu.includes("VENDOR NAME") || cu === "VENDOR") colMap.Vendor = i;
    if (cu.includes("VENDOR PRODUCT") || cu.includes("VENDORPRODUCT") || cu === "VPN") colMap.VPN = i;
    // Status: a catalog can have two columns containing "STATUS" (e.g. "Status (Pre-Order/Store Backup)"
    // and "Additional Product Status"). Lock onto the primary one and never let "Additional…" override it.
    if (cu.includes("STATUS")) {
      const isPrimary = cu === "STATUS" || cu.includes("BACKUP") || cu.includes("BACK-UP") || cu.includes("PRE-ORDER") || cu.includes("PREORDER") || cu.includes("STORE");
      if (isPrimary && !gotStatus) { colMap.Status = i; gotStatus = true; }
      else if (!gotStatus && colMap.Status == null && !cu.includes("ADDITIONAL")) colMap.Status = i;
    }
    // Store flag columns
    if (cu === "TO") colMap.TO = i;
    if (cu === "SG") colMap.SG = i;
    if (cu === "DM") colMap.DM = i;
    if (cu === "EC") colMap.EC = i;
  });

  // Build items per store
  const storeItems = { TO: [], SG: [], DM: [], EC: [] };
  for (let r = hdrRow + 1; r < raw.length; r++) {
    const row = raw[r];
    const dept = String(row[colMap.Dept] || "").toUpperCase();
    const rtl = String(row[colMap.RTL] || "").toUpperCase();
    if (!dept.includes("PRODUCE") || !rtl.includes("RTL")) continue;
    // Status filter: exclude BACKUP
    if (colMap.Status != null) {
      const status = String(row[colMap.Status] || "").toUpperCase().trim();
      if (status.includes("BACK")) continue;
    }
    // Keep full D365 ID (Canada IDs have leading zeros)
    let d365 = String(row[colMap.D365_ID] || "").trim();
    if (d365.includes(".")) d365 = String(parseInt(parseFloat(d365)));

    const item = {
      d365,
      name: String(row[colMap.Name] || "").trim(),
      unit: String(row[colMap.SaleUnit] || "EA").trim().toUpperCase(),  // files ship ea/EA/lb/LB mixed
      vendor: String(row[colMap.Vendor] || "").trim(),
      vpn: String(row[colMap.VPN] || "").trim().replace(/\.0$/, "").replace(/^0+/, "") || "0",
    };

    // Check each store flag
    for (const abbr of ["TO", "SG", "DM", "EC"]) {
      if (colMap[abbr] != null) {
        const flag = String(row[colMap[abbr]] || "").trim().toUpperCase();
        if (flag === "X" || flag === "REQ") {
          storeItems[abbr].push({ ...item });
        }
      }
    }
  }
  return storeItems;
}

// --- SALES PRICE EXPORT PARSER ---
// Returns { lookup: { "item|acct": { price, unit, fromDate } }, count }
function parseSalesPrice(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });
  const lookup = {};
  for (const row of raw) {
    const item = String(row.Item || row.item || "").trim().replace(/^0+/, "");
    const acct = String(row["Account selection"] || row.Account || row["Account Selection"] || "").trim();
    const price = parseFloat(row["Price decimal"] || row["Price Decimal"] || row.Price || row.price);
    // Read sales unit from export (may be "Unit", "Unit of measure", "UoM", "Sales unit")
    const unit = String(
      row.Unit || row["Unit of measure"] || row["Unit Of Measure"] || row.UoM || row["Sales unit"] || row["Sales Unit"] || ""
    ).trim().toUpperCase();
    // Read From date for dedup (keep most recent)
    let fromDate = row["From date"] || row["From Date"] || row["FromDate"] || row["Start date"] || "";
    // Parse Excel serial date if numeric
    if (typeof fromDate === "number") {
      fromDate = new Date((fromDate - 25569) * 86400000).toISOString().slice(0, 10);
    } else {
      fromDate = String(fromDate);
    }

    if (item && acct && !isNaN(price)) {
      const key = `${item}|${acct}`;
      const existing = lookup[key];
      // Keep the most recent record per item+account
      if (!existing || fromDate > (existing.fromDate || "")) {
        lookup[key] = { price, unit: unit || null, fromDate };
      }
    }
  }
  return lookup;
}

// ══════════════════════════════════════════════════════════
// VPN MATCHING — ported from find_vprice()
// ══════════════════════════════════════════════════════════
// Catalog VPNs are maintained by hand, so they drift from the vendor's file in
// small ways: case, dashes, spaces, leading zeros. Match through a normalised
// index built once per vendor list and cached, tried tightest-first so an exact
// code always beats a loosened one.
//
// Ambiguity is REFUSED, not guessed. If two vendor codes collapse onto the same
// normalised form the lookup returns nothing and the row is flagged. A wrong
// cost flows silently into margin and pricing; a flagged row is visible and
// gets fixed. This matters more than it looks: Charlie's uses codes like
// 013-01086 while Vesta uses bare numbers like 13, so short numeric VPNs are
// far more collision-prone than Baldor-style codes.
const vendorIndexCache = new WeakMap();
function buildVendorIndex(vendorItems) {
  const cached = vendorIndexCache.get(vendorItems);
  if (cached) return cached;
  const upper = new Map(), noPunct = new Map(), noZero = new Map();
  const add = (map, key, val) => {
    if (!key) return;
    if (map.has(key)) { if (map.get(key) !== val) map.set(key, null); return; }  // null = ambiguous
    map.set(key, val);
  };
  for (const [k, v] of Object.entries(vendorItems)) {
    const u = String(k).trim().toUpperCase();
    add(upper, u, v);
    const np = u.replace(/[-\s._]/g, "");
    add(noPunct, np, v);
    add(noZero, np.replace(/^0+/, ""), v);
  }
  const idx = { upper, noPunct, noZero, size: Object.keys(vendorItems).length };
  vendorIndexCache.set(vendorItems, idx);
  return idx;
}

function findVendorPrice(vpn, vendorItems) {
  if (!vpn || vpn === "nan" || vpn === "NaN") return { data: null, error: "VPN is blank" };
  const vs = String(vpn).trim();
  const vc = vs.endsWith(".0") ? vs.slice(0, -2) : vs;

  // Exact, case-sensitive first — unchanged behaviour for anything already matching.
  for (const attempt of [vs, vc, vs.replace(/^0+/, "")]) {
    if (vendorItems[attempt]) return { data: vendorItems[attempt], error: null };
  }

  const idx = buildVendorIndex(vendorItems);
  const u = vc.toUpperCase();
  const np = u.replace(/[-\s._]/g, "");
  for (const hit of [idx.upper.get(u), idx.noPunct.get(np), idx.noZero.get(np.replace(/^0+/, ""))]) {
    if (hit) return { data: hit, error: null };
  }

  // OK Produce: 5-digit catalog VPN → 7-digit vendor number
  const with00 = idx.upper.get(u + "00");
  if (with00) return { data: with00, error: null };

  // Name the list size so "not on this week's list" reads differently from
  // "the list never loaded".
  return { data: null, error: `VPN ${vs} not found in vendor list (${idx.size} codes)` };
}

// ══════════════════════════════════════════════════════════
// PROCESS A STORE — combine catalog + vendor + sales price
// ══════════════════════════════════════════════════════════
function processStore(catalogItems, vendorData, salesPriceLookup, storeConfig, casepackRules = []) {
  const acct = storeConfig.acct;
  // Which D365s appear on more than one catalog row (dual vendor / dual pack
  // size)? Those rows must not inherit legacy D365-keyed history — see
  // readHistRec.
  const d365Counts = {};
  for (const ci of catalogItems) {
    const d = String(ci?.d365 ?? "").trim();
    if (d) d365Counts[d] = (d365Counts[d] || 0) + 1;
  }
  return catalogItems.map((ci, idx) => {
    const id = `${storeConfig.abbr}-${ci.d365}-${idx}`;
    let error = "";
    let unitCost = null;
    let casepackStr = "";
    let costUnit = "";

    // Sales price lookup first — so we can use its unit as fallback
    const spKey = `${ci.d365.replace(/^0+/, "")}|${acct}`;
    const spRecord = salesPriceLookup[spKey] ?? null;
    const salesPrice = spRecord?.price ?? null;
    const salesUnitFromExport = spRecord?.unit || null;
    const effectiveUnit = ci.unit || salesUnitFromExport || "EA";

    // Identify which vendor
    const vUpper = String(ci.vendor).toUpperCase();
    let vendorKey = null;
    let cwRegion = null;
    if (vUpper.includes("BALDOR")) vendorKey = "baldor";
    else if (vUpper.includes("FRESHPOINT") && (vUpper.includes("SOUTH FL") || vUpper.includes("S. FL") || vUpper.includes("SO FLA"))) vendorKey = "freshpoint_fl";
    else if (vUpper.includes("FRESHPOINT") && vUpper.includes("DALLAS")) vendorKey = "freshpoint_da";
    else if (vUpper.includes("FRESHPOINT") && (vUpper.includes("CAL") || vUpper.includes("S.C") || vUpper.includes("SOCAL"))) vendorKey = "freshpoint_sc";
    else if (vUpper.includes("FLORIDA VEG") || vUpper.includes("MR GREEN") || vUpper.includes("MR. GREEN")) vendorKey = "mrgreen";
    else if (vUpper.includes("TESTA")) vendorKey = "testa";
    else if (vUpper.includes("MIDWEST")) vendorKey = "midwest";
    else if (vUpper.includes("HARDIE")) vendorKey = "hardies";
    else if (vUpper.includes("OK PRODUCE") || vUpper.includes("O.K. PRODUCE") || vUpper.includes("OK PRO")) vendorKey = "ok";
    else if (vUpper.includes("VESTA")) vendorKey = "vesta";
    else if (vUpper.includes("CHARLIE")) vendorKey = "charlies";
    // Canada vendors
    else if (vUpper.includes("ZEUS")) vendorKey = "zeus";
    else if (vUpper.includes("BONDI")) vendorKey = "bondi";
    else if (vUpper.includes("BURNAC") || vUpper.includes("PROVINCIAL")) vendorKey = "burnac";
    else if (vUpper.includes("DAIRYLAND") || vUpper.includes("CHEF'S WAREHOUSE") || vUpper.includes("CHEFS WAREHOUSE") || vUpper.includes("CHEF\u2019S WAREHOUSE")) { vendorKey = "cw"; cwRegion = CW_REGION[storeConfig.abbr] || "NY"; }
    else if (ci.vendor) error = `Vendor not mapped: ${ci.vendor}`;

    // Find vendor price
    if (vendorKey && (vendorKey !== "cw" ? vendorData[vendorKey] : vendorData[`cw_${cwRegion}`] || vendorData.cw)) {
      // For CW, use region-specific data
      const vItems = vendorKey === "cw" ? (vendorData[`cw_${cwRegion}`] || vendorData.cw) : vendorData[vendorKey];
      const { data: vr, error: ve } = findVendorPrice(ci.vpn, vItems);
      if (ve) {
        error = ve;
      } else if (vr) {
        const sz = vr.Size || "";
        const vDesc = vr.Description || "";
        const vPrice = vr.Price;
        casepackStr = sz;
        if (vPrice == null || isNaN(vPrice)) {
          error = "Vendor price blank";
        } else {
          let cp;

          // === CHECK CASEPACK OVERRIDE RULES FIRST ===
          const ruleOverride = matchCasepackRule(casepackRules, storeConfig.abbr, vendorKey, ci.d365, ci.vpn, ci.name);
          if (ruleOverride) {
            cp = ruleOverride;
            casepackStr = ruleOverride.ruleLabel;
          }
          // === CHARLIE'S SPECIAL: use Weight_LB and Pack_Qty directly ===
          else if (vendorKey === "charlies" && vr.Weight_LB != null && vr.Weight_LB > 0) {
            const wlb = vr.Weight_LB;
            const pq = vr.Pack_Qty;
            const su2 = effectiveUnit.toUpperCase();
            if (su2 === "LB" || su2 === "KG") {
              cp = { qty: wlb, unit: "lb", error: null };
            } else if (su2 === "EA" || su2 === "EACH" || su2 === "PC" || su2 === "HD") {
              if (pq != null && pq > 1) {
                cp = { qty: parseInt(pq), unit: "ea", error: null };
              } else {
                // Try size field for count
                const rs = String(sz).split("(")[0].trim().toUpperCase();
                let m2 = rs.match(/^(\d+)\s*\/\s*(\d+)\s*CT/);
                if (m2) cp = { qty: (parseInt(m2[1]) + parseInt(m2[2])) / 2, unit: "ct", error: null };
                else {
                  m2 = rs.match(/^(\d+)\s*CT/);
                  if (m2) cp = { qty: parseInt(m2[1]), unit: "ct", error: null };
                  else cp = parseCasepack(sz, vDesc, vr.Measure || "");
                }
              }
            } else {
              cp = { qty: wlb, unit: "lb", error: null };
            }
          }
          // === OK PRODUCE SPECIAL: use OK_Weight_LB, OK_Pack_Qty, OK_Count ===
          else if (vendorKey === "ok") {
            const ow = vr.OK_Weight_LB, op = vr.OK_Pack_Qty, oc = vr.OK_Count;
            const su2 = effectiveUnit.toUpperCase();
            if (op != null && op > 0) cp = { qty: parseInt(op), unit: "ea", error: null };
            else if ((su2 === "LB" || su2 === "KG") && ow != null && ow > 0) cp = { qty: ow, unit: "lb", error: null };
            else if ((su2 === "EA" || su2 === "EACH" || su2 === "PC" || su2 === "HD") && oc != null && oc > 0) cp = { qty: oc, unit: "ct", error: null };
            else if (ow != null && ow > 0) cp = { qty: ow, unit: "lb", error: null };
            else if (oc != null && oc > 0) cp = { qty: oc, unit: "ct", error: null };
            else cp = parseCasepack(sz, vDesc, vr.Measure || "");
          }
          // === BURNAC / PROVINCIAL: use CaseWeight_LB and CasePack ===
          else if (vendorKey === "burnac") {
            const su2 = effectiveUnit.toUpperCase();
            if (su2 === "KG" && vr.CaseWeight_LB != null && vr.CaseWeight_LB > 0) {
              cp = { qty: Math.round(vr.CaseWeight_LB * 0.4536 * 100) / 100, unit: "kg", error: null };
            } else if ((su2 === "EA" || su2 === "EACH") && vr.CasePack != null && vr.CasePack > 0) {
              cp = { qty: vr.CasePack, unit: "ct", error: null };
            } else if (vr.CaseWeight_LB != null && vr.CaseWeight_LB > 0) {
              cp = { qty: Math.round(vr.CaseWeight_LB * 0.4536 * 100) / 100, unit: "kg", error: null };
            } else if (vr.CasePack != null && vr.CasePack > 0) {
              cp = { qty: vr.CasePack, unit: "ct", error: null };
            } else {
              cp = parseCasepackCAN(sz, vDesc);
            }
          }
          // === ZEUS / BONDI: use Canada casepack parser ===
          else if (vendorKey === "zeus" || vendorKey === "bondi") {
            cp = parseCasepackCAN(sz, vDesc);
          }
          // === CW SPECIAL: 1/X LB format ===
          else if (vendorKey === "cw") {
            const cwm = String(sz).toUpperCase().trim().match(/^1\s*\/\s*(\d+\.?\d*)\s*(?:LB|#)/i);
            if (cwm) cp = { qty: parseFloat(cwm[1]), unit: "lb", error: null };
            else cp = parseCasepack(sz, vDesc, vr.Measure || "");
          }
          // === ALL OTHERS: standard casepack parse (use Canada parser for CAN stores) ===
          else {
            const isCAN = CAN_STORES.includes(storeConfig.abbr);
            cp = isCAN ? parseCasepackCAN(sz, vDesc) : parseCasepack(sz, vDesc, vr.Measure || "");
          }

          if (cp.error) {
            error = cp.error;
          } else {
            const { cost, note } = calcUnitCost(vPrice, cp.qty, cp.unit, effectiveUnit);
            unitCost = cost;
            casepackStr = `${cp.qty} ${cp.unit}`;
            // Determine the unit that unit cost is expressed in
            if (cp.unit === "lb" || (cp.unit && String(cp.unit).includes("lb_ea"))) costUnit = "LB";
            else if (cp.unit === "kg" || (cp.unit && String(cp.unit).includes("kg_ea"))) costUnit = "KG";
            else if (cp.unit === "ct" || cp.unit === "ea") costUnit = "EA";
            else if (cp.unit && String(cp.unit).includes("oz")) costUnit = "EA";
            else if (cp.unit === "gal") costUnit = "GAL";
            else costUnit = effectiveUnit;

            if (note && note.includes("cost per LB, selling by EA")) {
              const w = estWeight(ci.name);
              if (w && unitCost != null) {
                unitCost *= w;
                casepackStr += ` (est ${w} lb/ea)`;
                error = `Est. ${w} lb/ea LB→EA`;
              } else { error = note; }
            } else if (note && note.includes("cost per CT, selling by LB")) {
              const w = estWeight(ci.name);
              if (w && unitCost != null) {
                unitCost /= w;
                casepackStr += ` (est ${w} lb/ea)`;
                error = `Est. ${w} lb/ea CT→LB`;
              } else { error = note; }
            } else if (note && note.includes("cost per KG, selling by EA")) {
              const wkg = estWeightKg(ci.name);
              if (wkg && unitCost != null) {
                unitCost *= wkg;
                casepackStr += ` (est ${wkg} kg/ea)`;
                error = `Est. ${wkg} kg/ea KG→EA`;
              } else { error = note; }
            } else if (note && note.includes("cost per CT, selling by KG")) {
              const wkg = estWeightKg(ci.name);
              if (wkg && unitCost != null) {
                unitCost /= wkg;
                casepackStr += ` (est ${wkg} kg/ea)`;
                error = `Est. ${wkg} kg/ea CT→KG`;
              } else { error = note; }
            } else if (note && note.toLowerCase().includes("approx")) {
              error = note;
            }
          }
        }
      }
    } else if (vendorKey && !error) {
      error = `Vendor file not loaded: ${vendorKey}`;
    } else if (!error && !ci.vendor) {
      error = "No vendor assigned";
    }

    // Margin calcs
    const currentMargin = calcMarginPct(salesPrice, unitCost);
    const newRetail = unitCost != null ? roundTo09(unitCost / 0.70) : null;
    const newMargin = calcMarginPct(newRetail, unitCost);

    return {
      id,
      d365: ci.d365,
      dupD365: (d365Counts[String(ci.d365 ?? "").trim()] || 0) > 1,
      catapult: "",
      name: ci.name,
      costUnit: costUnit || "",
      saleUnit: effectiveUnit,
      vendor: ci.vendor,
      vpn: ci.vpn,
      cost: unitCost != null ? Math.round(unitCost * 10000) / 10000 : null,
      price: salesPrice,
      margin: currentMargin ?? 0,
      newPrice: newRetail,
      newMargin: newMargin,
      casepack: casepackStr,
      error,
      vendorKey,
      cwRegion,
      acctId: acct,
      discount: null, // { active: true, discountPrice: number } when set
      marketPrice: null, // manually entered market comparison price
      adjPrice: null, // manually entered adjusted/new price
      weeks: currentMargin != null ? [currentMargin, currentMargin, currentMargin, currentMargin] : [0, 0, 0, 0],
    };
  });
}

// ══════════════════════════════════════════════════════════
// UI COMPONENTS
// ══════════════════════════════════════════════════════════

function EditableCell({ value, field, itemId, onSave, isNumeric, isCurrency, mono, align, clearable }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));
  const startEdit = () => { setDraft(String(value ?? "")); setEditing(true); };
  const commit = () => {
    setEditing(false);
    if (isNumeric) {
      const t = draft.trim();
      if (t === "") { if (clearable && value != null) onSave(itemId, field, null); return; }
      const parsed = parseFloat(t);
      if (isNaN(parsed)) return;
      if (parsed !== value) onSave(itemId, field, parsed);
      return;
    }
    if (draft !== value) onSave(itemId, field, draft);
  };
  if (editing) {
    return <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
      style={{ width: isNumeric ? 64 : field === "name" ? 200 : 90, padding: "2px 6px", border: "1px solid var(--accent)", borderRadius: 4, fontSize: 12, background: "var(--bg-primary)", color: "var(--text-primary)", textAlign: align || "center", fontFamily: mono ? "'DM Mono', monospace" : "inherit", outline: "none" }} />;
  }
  const display = value == null ? "—" : isCurrency ? `$${Number(value).toFixed(2)}` : value;
  return (
    <span onClick={startEdit} title={clearable ? "Click to edit · clear the box and press Enter to delete" : "Click to edit"}
      style={{ cursor: "text", borderBottom: "1px dashed transparent", transition: "border-color 0.15s", padding: "1px 2px", borderRadius: 2 }}
      onMouseEnter={(e) => { e.currentTarget.style.borderBottomColor = "var(--text-tertiary)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderBottomColor = "transparent"; }}>
      {display}
    </span>
  );
}

// Gross profit cell. Three states, deliberately distinct:
//   null qty  → "—"  the item has no usable Catapult ID, or no sales week is
//                    loaded. We cannot tell, and a 0 here would read as "this
//                    doesn't sell", which is the delisting mistake.
//   qty 0     → "$0" mapped cleanly, genuinely sold nothing last week.
//   otherwise → the dollar figure.
function GPCell({ value, qty, bold }) {
  if (qty == null) return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  if (value == null) return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  const dim = qty === 0;
  return (
    <span style={{ color: dim ? "var(--text-tertiary)" : value < 0 ? "#e05252" : "var(--text-primary)", fontWeight: bold ? 700 : 400 }}>
      {value < 0 ? "-" : ""}${Math.abs(value).toFixed(2)}
    </span>
  );
}

// New GP $ minus GP $. With no New Price typed this is the effect of the cost
// move on its own; with one typed it is the effect of the whole decision.
// "—" means not computable (one side missing), which is not the same as no change.
// Gap to market. Above market is the one that costs you volume, so it reads
// red; below market reads green. Neutral inside ±2% because a cent or two on a
// $3 item is noise, not a position.
function GapCell({ value }) {
  if (value == null) return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  const near = Math.abs(value) < 2;
  return (
    <span style={{ color: near ? "var(--text-tertiary)" : value > 0 ? "#e05252" : "#3a9a5c", fontWeight: near ? 400 : 600 }}>
      {value > 0 ? "+" : value < 0 ? "−" : ""}{Math.abs(value).toFixed(1)}%
    </span>
  );
}

const mktTitle = (item, week) => {
  if (item.marketIsManual) return `Entered by hand${item.comp ? ` · Whole Foods reported $${item.comp.price}` : ""}`;
  if (!item.comp) return item.marketPrice != null ? "Carried from an earlier week" : "No Whole Foods match for this item";
  const c = item.comp;
  const bits = [`${c.name || c.wfId}`, `$${c.raw}/${c.uom || "?"}`];
  if (c.how) bits.push(c.how);
  if (week) bits.push(`wk ${week}`);
  return bits.join(" · ") + (c.mismatch ? " — units differ, no multiplier set" : "");
};

function GPDeltaCell({ value }) {
  if (value == null) return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  if (Math.abs(value) < 0.005) return <span style={{ color: "var(--text-tertiary)" }}>$0.00</span>;
  return (
    <span style={{ color: value > 0 ? "#3a9a5c" : "#e05252" }}>
      {value > 0 ? "+" : "−"}${Math.abs(value).toFixed(2)}
    </span>
  );
}

const money2 = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

const gpTitle = (item, week) => {
  if (item.qtySold == null) return week ? "No Catapult ID match — sales cannot be attributed to this row" : "No sales data loaded";
  if (item.gpPrice == null || item.gpCost == null) return `No ${item.gpPrice == null ? "price" : "cost"} recorded on or before the week of ${week}`;
  // Name the source week for anything carried forward, so a figure built from
  // three-week-old prices never looks like this week's.
  const from = (wk) => (wk && wk !== week ? ` (from ${wk})` : "");
  return `Week of ${week}: ${item.qtySold} sold × (${money2(item.gpPrice)}${from(item.gpPriceWeek)} − ${money2(item.gpCost)}${from(item.gpCostWeek)})`;
};

const newGpTitle = (item) => {
  if (item.qtySold == null || item.newGpDollars == null) return "";
  const p = item.newGpUsesAdj ? item.adjPrice : item.price;
  return `${item.newGpUsesAdj ? "New Price" : "Current price"} ${money2(p)} − cost ${money2(item.cost)} × ${item.qtySold} sold`;
};

// One row of the weighted-margin header. "—" where the basket is empty, which
// is a real state: no sales week loaded, or nothing in view has both a cost and
// last week's units.
// ── Margin & profit history ──────────────────────────────
// Four panels: department on the left, current filter selection on the right,
// margin above profit in each column. Left-to-right is the comparison that
// matters — "did the slice move more than the department" — which is why they
// sit side by side rather than stacked.
//
// Each panel is scaled to its OWN data. A slice running at -38% and a department
// at 40% drawn on one shared axis would look alike; drawn separately they read
// correctly, at the cost of not being height-comparable. The 30% target line is
// drawn only where 30% actually falls inside the visible range, so it never
// sits pinned to an edge implying something it isn't.
function TrendPanel({ title, rows, kind, color, fixed }) {
  const W = 420, H = 150, pad = { t: 14, r: 14, b: 26, l: 54 };
  const iw = W - pad.l - pad.r, ih = H - pad.t - pad.b;
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);
  const key = kind === "margin" ? "margin" : "profit";
  const vals = rows.map((r) => r[key]).filter((v) => v != null);
  if (!vals.length) return (
    <div style={{ padding: "34px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: 12 }}>
      {title}: nothing to plot yet
    </div>
  );
  let lo = Math.min(...vals), hi = Math.max(...vals);
  let yMin, yMax;
  if (fixed) {
    // The department margin sits in a narrow band, so auto-scaling turns a two
    // point move into a cliff. Pin it to a fixed window that matches how the
    // number is actually judged, and only widen if the data genuinely leaves it.
    yMin = Math.min(fixed[0], lo - 2);
    yMax = Math.max(fixed[1], hi + 2);
  } else {
    if (lo === hi) { lo -= Math.abs(lo || 1) * 0.1; hi += Math.abs(hi || 1) * 0.1; }
    const span = hi - lo;
    yMin = lo - span * 0.18; yMax = hi + span * 0.18;
    if (kind === "profit" && yMin > 0) yMin = 0;          // dollars read from zero
  }
  const showTarget = kind === "margin" && 30 > yMin && 30 < yMax;
  const n = rows.length;
  const X = (i) => (n === 1 ? pad.l + iw / 2 : pad.l + (i / (n - 1)) * iw);
  const Y = (v) => pad.t + ih - ((v - yMin) / (yMax - yMin || 1)) * ih;
  const fmt = (v) => kind === "margin" ? `${v.toFixed(1)}%` : `${v < 0 ? "-" : ""}$${Math.abs(Math.round(v)).toLocaleString()}`;

  const segs = []; let cur = [];
  rows.forEach((r, i) => {
    if (r[key] != null) cur.push({ x: X(i), y: Y(r[key]), i });
    else { if (cur.length) segs.push(cur); cur = []; }
  });
  if (cur.length) segs.push(cur);
  const dots = segs.reduce((a, sg) => a.concat(sg), []);

  const track = (clientX) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r || !r.width) return;
    if (n === 1) { setHover(0); return; }
    const vx = ((clientX - r.left) / r.width) * W;
    const i = Math.round(((vx - pad.l) / iw) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };
  const hv = hover != null ? rows[hover] : null;
  const boxW = 150, boxH = 50;
  const hx = hover == null ? 0 : X(hover);
  const bx = hx + boxW + 12 > W ? hx - boxW - 8 : hx + 8;

  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color, padding: "0 2px 2px" }}>{title}</div>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}
        onMouseMove={(e) => track(e.clientX)} onMouseLeave={() => setHover(null)}
        onTouchStart={(e) => e.touches[0] && track(e.touches[0].clientX)}
        onTouchMove={(e) => e.touches[0] && track(e.touches[0].clientX)} onTouchEnd={() => setHover(null)}>
        {[0, 1, 2, 3].map((k) => {
          const v = yMin + ((yMax - yMin) * k) / 3;
          return <g key={k}>
            <line x1={pad.l} y1={Y(v)} x2={pad.l + iw} y2={Y(v)} stroke="#222536" strokeDasharray="3 4" />
            <text x={pad.l - 7} y={Y(v) + 3.5} textAnchor="end" fill="#585d6e" fontSize={9} fontFamily="'DM Mono', monospace">{fmt(v)}</text>
          </g>;
        })}
        {showTarget && <g>
          <line x1={pad.l} y1={Y(30)} x2={pad.l + iw} y2={Y(30)} stroke="#3a9a5c" strokeWidth={1} strokeDasharray="2 4" opacity={0.55} />
          <text x={pad.l + iw} y={Y(30) - 4} textAnchor="end" fill="#3a9a5c" fontSize={8.5} opacity={0.75}>target 30%</text>
        </g>}
        {rows.map((r, i) => (n <= 14 || i % 2 === 0) && (
          <text key={r.week} x={X(i)} y={H - 8} textAnchor="middle" fontFamily="'DM Mono', monospace"
            fill={hover === i ? "var(--text-primary)" : "#585d6e"} fontSize={8.5}>{r.week.slice(5)}</text>
        ))}
        {hover != null && <line x1={hx} y1={pad.t} x2={hx} y2={pad.t + ih} stroke="#585d6e" strokeDasharray="2 3" />}
        {segs.map((sg, i) => sg.length > 1 && (
          <polyline key={i} fill="none" stroke={color} strokeWidth={2} points={sg.map((p) => `${p.x},${p.y}`).join(" ")} />
        ))}
        {dots.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={3} fill={color} stroke="var(--bg-primary)" strokeWidth={1.5} />)}
        {hover != null && hv && (
          <g pointerEvents="none">
            <rect x={bx} y={pad.t + 2} width={boxW} height={boxH} rx={7} fill="#1c1f2b" stroke="#2a2d3a" />
            <text x={bx + 9} y={pad.t + 17} fill="#e8eaf0" fontSize={10} fontWeight={700}>Week of {hv.week}</text>
            <text x={bx + 9} y={pad.t + 31} fill={color} fontSize={10.5} fontFamily="'DM Mono', monospace">{hv[key] == null ? "—" : fmt(hv[key])}</text>
            <text x={bx + 9} y={pad.t + 44} fill="#8b90a0" fontSize={9.5} fontFamily="'DM Mono', monospace">{hv.count} items counted</text>
          </g>
        )}
      </svg>
    </div>
  );
}

// Summary boxes under each column, matching the pattern on the item detail
// modal: min / max / range / average, then first-to-last change. "Items counted"
// is the coverage figure — a column whose range is wide there is telling you the
// margin moved because the basket changed, not because pricing did.
function TrendStats({ rows, kind, label, color, fmt }) {
  const vals = rows.map((r) => r[kind]).filter((v) => v != null);
  const n = vals.length;
  const min = n ? Math.min(...vals) : null;
  const max = n ? Math.max(...vals) : null;
  const avg = n ? vals.reduce((a, b) => a + b, 0) / n : null;
  const first = n ? vals[0] : null, last = n ? vals[n - 1] : null;
  // Percentage points for a margin, percent for everything else — a margin going
  // 38% to 40% is +2pt, not +5.3%, and conflating the two misreads badly.
  const change = first == null || last == null ? null
    : kind === "margin" ? last - first
    : first === 0 ? null : ((last - first) / Math.abs(first)) * 100;
  const changeStr = change == null ? "—"
    : kind === "margin" ? `${change >= 0 ? "+" : "−"}${Math.abs(change).toFixed(1)}pt`
    : `${change >= 0 ? "+" : "−"}${Math.abs(change).toFixed(1)}%`;
  const cell = { fontFamily: "'DM Mono', monospace", fontSize: 11.5, color: "var(--text-primary)", textAlign: "right" };
  const key = { fontSize: 11, color: "var(--text-tertiary)" };
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color, marginBottom: 6 }}>
        {label} <span style={{ color: "var(--text-tertiary)", fontWeight: 400 }}>({n} pt{n !== 1 ? "s" : ""})</span>
      </div>
      {[["Min", min], ["Max", max], ["Range", min != null ? max - min : null], ["Average", avg]].map(([k, v]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "1.5px 0" }}>
          <span style={key}>{k}</span><span style={cell}>{v == null ? "—" : fmt(v)}</span>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0 0", marginTop: 4, borderTop: "1px solid var(--border-light, #1a1d24)" }}>
        <span style={key}>% Change</span>
        <span style={{ ...cell, color: change == null || Math.abs(change) < 0.05 ? "var(--text-tertiary)" : change > 0 ? "#3a9a5c" : "#e05252" }}>{changeStr}</span>
      </div>
    </div>
  );
}

function TrendColumnStats({ rows }) {
  const pct = (v) => `${v.toFixed(1)}%`;
  const usd = (v) => `${v < 0 ? "-" : ""}$${Math.abs(Math.round(v)).toLocaleString()}`;
  const cnt = (v) => String(Math.round(v));
  return (
    <div style={{ display: "flex", gap: 18, marginTop: 14, padding: "12px 14px", background: "var(--bg-tertiary, #14161e)", border: "1px solid var(--border, #222536)", borderRadius: 9 }}>
      <TrendStats rows={rows} kind="margin" label="Weighted margin" color="#4a7cff" fmt={pct} />
      <TrendStats rows={rows} kind="profit" label="Profit" color="#3a9a5c" fmt={usd} />
      <TrendStats rows={rows} kind="count" label="Items counted" color="#9333ea" fmt={cnt} />
    </div>
  );
}

function MarginTrendModal({ store, storeRows, filterRows, filterLabel, onClose }) {
  const two = !!filterRows;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg-panel, #12141c)", border: "1px solid #2a2d3a", borderRadius: 12, width: two ? 960 : 520, maxWidth: "95vw", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "16px 20px", borderBottom: "1px solid #222536" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>Margin &amp; profit history — {store}</div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>
              Weighted across items with units sold and a cost that week. Each panel is scaled to its own data.
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--text-tertiary)", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: two ? "1fr 1fr" : "1fr", gap: 22, padding: "16px 20px 22px" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>Department</div>
            <TrendPanel title="Weighted margin" rows={storeRows} kind="margin" color="#4a7cff" fixed={[0, 50]} />
            <div style={{ height: 10 }} />
            <TrendPanel title="Profit" rows={storeRows} kind="profit" color="#3a9a5c" />
            <TrendColumnStats rows={storeRows} />
          </div>
          {two && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>
                {filterLabel}
                <div style={{ fontSize: 9.5, fontWeight: 400, color: "var(--text-tertiary)", textTransform: "none", letterSpacing: 0, marginTop: 2 }}>
                  today's filter selection, traced back through history
                </div>
              </div>
              <TrendPanel title="Weighted margin" rows={filterRows} kind="margin" color="#9333ea" />
              <div style={{ height: 10 }} />
              <TrendPanel title="Profit" rows={filterRows} kind="profit" color="#3a9a5c" />
              <TrendColumnStats rows={filterRows} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MarginRow({ label, s, week, dim }) {
  const pct = (v) => (v == null ? "—" : `${v.toFixed(1)}%`);
  const money = (v) => (v == null ? "—" : `${v < 0 ? "-" : "+"}$${Math.abs(v).toFixed(0)}`);
  const pptColor = s.ppt == null || Math.abs(s.ppt) < 0.05 ? "var(--text-tertiary)" : s.ppt > 0 ? "#3a9a5c" : "#e05252";
  const dollColor = s.profitDelta == null || Math.abs(s.profitDelta) < 0.5 ? "var(--text-tertiary)" : s.profitDelta > 0 ? "#3a9a5c" : "#e05252";
  const mono = { fontFamily: "'DM Mono', monospace", fontSize: 18, fontWeight: 700, textAlign: "right" };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "58px 92px 92px 96px 70px 92px", columnGap: 12, alignItems: "baseline", opacity: dim ? 0.92 : 1 }}
      title={s.count ? `${s.count} items with units sold in the week of ${week}` : "No items with both a cost and units sold last week"}>
      <span style={{ fontSize: 10.5, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</span>
      <span style={{ ...mono, color: "var(--accent)" }}>{pct(s.actual)}</span>
      <span style={{ ...mono, color: s.next == null ? "var(--text-tertiary)" : "var(--text-primary)" }}>{pct(s.next)}</span>
      <span style={{ ...mono, color: s.profit == null ? "var(--text-tertiary)" : "var(--text-primary)" }}>
        {s.profit == null ? "—" : `${s.profit < 0 ? "-" : ""}$${Math.abs(Math.round(s.profit)).toLocaleString()}`}
      </span>
      <span style={{ ...mono, fontSize: 15, color: pptColor }}>{s.ppt == null ? "—" : `${s.ppt > 0 ? "+" : s.ppt < 0 ? "−" : ""}${Math.abs(s.ppt).toFixed(1)}`}</span>
      <span style={{ ...mono, fontSize: 15, color: dollColor }}>{money(s.profitDelta)}</span>
    </div>
  );
}

function MarginBar({ value }) {
  if (value == null || isNaN(value)) return <span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>—</span>;
  // Colour carries the flag; the old position-in-range bar cost ~80px of width
  // on every row without answering a question anyone was asking.
  const color = value < 25 ? "#e05252" : value > 55 ? "#d4a017" : value >= 28 && value <= 35 ? "#3a9a5c" : "#6b8aad";
  return (
    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: value < 25 || value > 55 ? 700 : 500, color, fontSize: 13 }}>
      {value.toFixed(1)}%
    </span>
  );
}

function StatusDot({ margin }) {
  if (margin == null) return <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#585d6e", flexShrink: 0 }} />;
  const bg = margin < 25 ? "#e05252" : margin > 55 ? "#d4a017" : "#3a9a5c";
  return <div style={{ width: 8, height: 8, borderRadius: "50%", background: bg, flexShrink: 0 }} />;
}

function DiscountPriceInput({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));
  const startEdit = () => { setDraft(String(value ?? "")); setEditing(true); };
  const commit = () => {
    setEditing(false);
    const parsed = parseFloat(draft);
    if (!isNaN(parsed) && parsed !== value) onChange(parsed);
  };
  if (editing) {
    return <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
      style={{ width: 68, padding: "2px 6px", border: "1px solid #9333ea", borderRadius: 4, fontSize: 12, background: "rgba(147,51,234,0.1)", color: "#c084fc", textAlign: "center", fontFamily: "'DM Mono', monospace", outline: "none" }} />;
  }
  return (
    <span onClick={startEdit} title="Click to set discount price"
      style={{ cursor: "text", color: "#c084fc", fontWeight: 600, fontSize: 13, borderBottom: "1px dashed rgba(147,51,234,0.4)", padding: "1px 2px" }}>
      ${Number(value).toFixed(2)}
    </span>
  );
}

function AddItemModal({ onClose, onAdd }) {
  const [form, setForm] = useState({ d365: "", catapult: "", name: "", unit: "EA", vendor: "", vpn: "", price: "", cost: "" });
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "var(--bg-primary)", borderRadius: 12, padding: 28, width: 440, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>Add New Item</h3>
        {[{ key: "d365", label: "D365 ID", ph: "e.g. 100500" }, { key: "name", label: "Product Name", ph: "e.g. TOMATO ROMA" }, { key: "vendor", label: "Vendor", ph: "e.g. Baldor" }, { key: "vpn", label: "VPN", ph: "e.g. 87500" }, { key: "price", label: "Retail Price", ph: "e.g. 4.99" }, { key: "cost", label: "Unit Cost", ph: "e.g. 2.50" }].map(({ key, label, ph }) => (
          <div key={key} style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</label>
            <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={ph}
              style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 13, background: "var(--bg-secondary)", color: "var(--text-primary)", outline: "none", boxSizing: "border-box", marginTop: 4 }} />
          </div>
        ))}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Sales Unit</label>
          <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
            style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 13, background: "var(--bg-secondary)", color: "var(--text-primary)", outline: "none", marginTop: 4 }}>
            <option value="EA">EA</option><option value="LB">LB</option><option value="HD">HD</option>
          </select>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={() => { if (form.d365 && form.name) { onAdd(form); onClose(); } }}
            style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: "var(--accent)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Add Item</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// CASEPACK RULES PANEL
// ══════════════════════════════════════════════════════════
function InputsPanel({ inputs, onRemoveWeek, onClose, dataWeeks = [] }) {
  // Group ledger entries by the week (Monday) they landed on, newest first.
  const byWeek = {};
  for (const e of inputs) { (byWeek[e.date] = byWeek[e.date] || []).push(e); }
  // Weeks that hold stored data but have no ledger row — an entry removed
  // before this panel knew how to clean up after it. They must still be listed,
  // or the data is stranded with no way to reach it from the UI.
  for (const w of dataWeeks) { if (w && !byWeek[w]) byWeek[w] = []; }
  const weeks = Object.keys(byWeek).sort((a, b) => b.localeCompare(a));
  const typeColor = (t) => t === "Vendor list" ? "#e05252" : t === "Sales export" ? "#4a7cff" : "#8b90a0";
  const fmtTs = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#12141c", border: "1px solid #2a2d3a", borderRadius: 12, width: 720, maxWidth: "92vw", maxHeight: "86vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #222536" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#e8eaf0" }}>Inputs</div>
            <div style={{ fontSize: 11, color: "#8b90a0", marginTop: 2 }}>Every file you've ingested, grouped by the week it was dated to. Removing a week deletes that week's snapshot from all stores and its files here.</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#8b90a0", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: "auto", padding: "8px 20px 20px" }}>
          {weeks.length === 0 && <div style={{ color: "#585d6e", fontSize: 13, padding: "24px 0", textAlign: "center" }}>No files ingested yet.</div>}
          {weeks.map((wk) => (
            <div key={wk} style={{ marginTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#c3c7d1", letterSpacing: ".3px" }}>Week of {wk} <span style={{ color: "#585d6e", fontWeight: 400 }}>· {byWeek[wk].length} file{byWeek[wk].length !== 1 ? "s" : ""}</span></span>
                <button onClick={() => onRemoveWeek(wk)}
                  style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid #3a2d33", background: "transparent", color: "#8b90a0", fontSize: 11, cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#e05252"; e.currentTarget.style.color = "#e05252"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#3a2d33"; e.currentTarget.style.color = "#8b90a0"; }}>
                  Remove week
                </button>
              </div>
              <div style={{ border: "1px solid #1e2129", borderRadius: 8, overflow: "hidden" }}>
                {byWeek[wk].map((e, i) => (
                  <div key={e.id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderTop: i ? "1px solid #1a1d24" : "none", fontSize: 12 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: typeColor(e.type), textTransform: "uppercase", letterSpacing: ".4px", width: 84, flexShrink: 0 }}>{e.type}</span>
                    <span style={{ color: "#e8eaf0", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'DM Mono', monospace", fontSize: 11 }} title={e.name}>{e.name}</span>
                    <span style={{ color: "#8b90a0", width: 130, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.target}>{e.target}</span>
                    <span style={{ color: "#585d6e", width: 64, flexShrink: 0, textAlign: "right", fontFamily: "'DM Mono', monospace" }}>{e.rows != null ? `${e.rows} rows` : "—"}</span>
                    <span style={{ color: "#585d6e", width: 108, flexShrink: 0, textAlign: "right" }}>{fmtTs(e.uploadedAt)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CasepackRulesPanel({ rules, onUpdate, onClose }) {
  const [editingRule, setEditingRule] = useState(null);
  const emptyRule = { store: "ALL", vendorKey: "ALL", matchType: "keyword", matchValue: "", retailQty: "", retailUnit: "EA" };
  const [newRule, setNewRule] = useState({ ...emptyRule });

  const handleAdd = () => {
    if (!newRule.matchValue || !newRule.retailQty) return;
    onUpdate([...rules, { ...newRule, id: `rule_${Date.now()}`, retailQty: parseFloat(newRule.retailQty) }]);
    setNewRule({ ...emptyRule });
  };

  const handleDelete = (id) => onUpdate(rules.filter((r) => r.id !== id));

  const handleEditSave = () => {
    if (!editingRule) return;
    onUpdate(rules.map((r) => r.id === editingRule.id ? { ...editingRule, retailQty: parseFloat(editingRule.retailQty) } : r));
    setEditingRule(null);
  };

  const selectStyle = { padding: "6px 10px", borderRadius: 6, border: "1px solid #2a2d3a", background: "#161820", color: "#e8eaf0", fontSize: 12, outline: "none" };
  const inputStyle = { ...selectStyle, width: 80 };
  const inputWideStyle = { ...selectStyle, width: 140 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#0f1117", borderRadius: 16, padding: "28px 32px", width: 1050, maxWidth: "95vw", maxHeight: "90vh", overflow: "auto", boxShadow: "0 24px 80px rgba(0,0,0,0.5)", border: "1px solid #2a2d3a" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: "#e8eaf0" }}>Casepack Override Rules</h2>
            <p style={{ margin: 0, fontSize: 12, color: "#585d6e" }}>Override how vendor casepacks are interpreted per store, vendor, and item</p>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#8b90a0", fontSize: 22, cursor: "pointer", padding: "4px 8px" }}>✕</button>
        </div>

        {/* Add new rule form */}
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", padding: "16px 18px", background: "#161820", borderRadius: 10, border: "1px solid #222536", marginBottom: 20, flexWrap: "wrap" }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: "#585d6e", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 4 }}>Store</label>
            <select value={newRule.store} onChange={(e) => setNewRule({ ...newRule, store: e.target.value })} style={selectStyle}>
              {STORE_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: "#585d6e", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 4 }}>Vendor</label>
            <select value={newRule.vendorKey} onChange={(e) => setNewRule({ ...newRule, vendorKey: e.target.value })} style={selectStyle}>
              {VENDOR_OPTIONS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: "#585d6e", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 4 }}>Match By</label>
            <select value={newRule.matchType} onChange={(e) => setNewRule({ ...newRule, matchType: e.target.value })} style={selectStyle}>
              <option value="keyword">Name keyword</option>
              <option value="d365">D365 ID</option>
              <option value="vpn">VPN</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: "#585d6e", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 4 }}>Match Value</label>
            <input value={newRule.matchValue} onChange={(e) => setNewRule({ ...newRule, matchValue: e.target.value })} placeholder="e.g. SCALLION" style={inputWideStyle} />
          </div>
          <span style={{ fontSize: 12, color: "#585d6e", alignSelf: "center", marginTop: 16, whiteSpace: "nowrap" }}>1 case =</span>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: "#585d6e", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 4 }}>Qty</label>
            <input value={newRule.retailQty} onChange={(e) => setNewRule({ ...newRule, retailQty: e.target.value })} placeholder="48" style={{ ...inputStyle, width: 55 }} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: "#585d6e", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 4 }}>Unit</label>
            <select value={newRule.retailUnit} onChange={(e) => setNewRule({ ...newRule, retailUnit: e.target.value })} style={{ ...selectStyle, width: 60 }}>
              <option value="EA">EA</option><option value="LB">LB</option><option value="KG">KG</option>
            </select>
          </div>
          <button onClick={handleAdd} disabled={!newRule.matchValue || !newRule.retailQty}
            style={{ padding: "6px 18px", borderRadius: 6, border: "none", background: newRule.matchValue && newRule.retailQty ? "#4a7cff" : "#2a2d3a", color: newRule.matchValue && newRule.retailQty ? "#fff" : "#585d6e", fontSize: 12, fontWeight: 600, cursor: newRule.matchValue && newRule.retailQty ? "pointer" : "default", marginTop: 16 }}>
            + Add Rule
          </button>
        </div>

        {/* Rules table */}
        {rules.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#585d6e" }}>
            <div style={{ fontSize: 14, marginBottom: 6 }}>No casepack rules yet</div>
            <div style={{ fontSize: 12 }}>Add rules above to override how vendor casepacks are interpreted</div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>
            <thead>
              <tr>
                {["Store", "Vendor", "Match By", "Match Value", "1 Case =", ""].map((h, i) => (
                  <th key={i} style={{ padding: "8px 12px", fontSize: 10, fontWeight: 600, color: "#8b90a0", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "left", borderBottom: "1px solid #2a2d3a" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => {
                const isEditing = editingRule?.id === rule.id;
                const r = isEditing ? editingRule : rule;
                const storeName = STORE_OPTIONS.find((s) => s.key === r.store)?.label || r.store;
                const vendorName = VENDOR_OPTIONS.find((v) => v.key === r.vendorKey)?.label || r.vendorKey;
                return (
                  <tr key={rule.id} style={{ background: isEditing ? "rgba(74,124,255,0.06)" : "transparent" }}>
                    <td style={{ padding: "8px 12px", borderBottom: "1px solid #1c1f2b" }}>
                      {isEditing ? <select value={r.store} onChange={(e) => setEditingRule({ ...r, store: e.target.value })} style={selectStyle}>{STORE_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select> : <span style={{ color: r.store === "ALL" ? "#585d6e" : "#e8eaf0" }}>{storeName}</span>}
                    </td>
                    <td style={{ padding: "8px 12px", borderBottom: "1px solid #1c1f2b" }}>
                      {isEditing ? <select value={r.vendorKey} onChange={(e) => setEditingRule({ ...r, vendorKey: e.target.value })} style={selectStyle}>{VENDOR_OPTIONS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}</select> : <span style={{ color: r.vendorKey === "ALL" ? "#585d6e" : "#e8eaf0" }}>{vendorName}</span>}
                    </td>
                    <td style={{ padding: "8px 12px", borderBottom: "1px solid #1c1f2b", color: "#8b90a0" }}>
                      {isEditing ? <select value={r.matchType} onChange={(e) => setEditingRule({ ...r, matchType: e.target.value })} style={selectStyle}><option value="keyword">Keyword</option><option value="d365">D365</option><option value="vpn">VPN</option></select> : r.matchType}
                    </td>
                    <td style={{ padding: "8px 12px", borderBottom: "1px solid #1c1f2b", fontWeight: 600, color: "#e8eaf0" }}>
                      {isEditing ? <input value={r.matchValue} onChange={(e) => setEditingRule({ ...r, matchValue: e.target.value })} style={inputWideStyle} /> : r.matchValue}
                    </td>
                    <td style={{ padding: "8px 12px", borderBottom: "1px solid #1c1f2b", fontFamily: "'DM Mono', monospace", fontWeight: 600, color: "#4a7cff" }}>
                      {isEditing ? (
                        <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <input value={r.retailQty} onChange={(e) => setEditingRule({ ...r, retailQty: e.target.value })} style={{ ...inputStyle, width: 55 }} />
                          <select value={r.retailUnit} onChange={(e) => setEditingRule({ ...r, retailUnit: e.target.value })} style={{ ...selectStyle, width: 55 }}><option value="EA">EA</option><option value="LB">LB</option><option value="KG">KG</option></select>
                        </span>
                      ) : `${r.retailQty} ${r.retailUnit}`}
                    </td>
                    <td style={{ padding: "8px 12px", borderBottom: "1px solid #1c1f2b" }}>
                      {isEditing ? (
                        <span style={{ display: "flex", gap: 6 }}>
                          <button onClick={handleEditSave} style={{ padding: "4px 12px", borderRadius: 4, border: "none", background: "#4a7cff", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Save</button>
                          <button onClick={() => setEditingRule(null)} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid #2a2d3a", background: "transparent", color: "#8b90a0", fontSize: 11, cursor: "pointer" }}>Cancel</button>
                        </span>
                      ) : (
                        <span style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => setEditingRule({ ...rule })} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid #2a2d3a", background: "transparent", color: "#8b90a0", fontSize: 11, cursor: "pointer" }}>Edit</button>
                          <button onClick={() => handleDelete(rule.id)} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid #e05252", background: "rgba(224,82,82,0.1)", color: "#e05252", fontSize: 11, cursor: "pointer" }}>✕</button>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div style={{ marginTop: 16, fontSize: 11, color: "#585d6e", lineHeight: 1.6 }}>
          Rules are checked in order — first match wins. Rules persist across sessions.
          <br />After adding or editing rules, re-upload your vendor files to recalculate.
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ITEM DETAIL MODAL — cost & price history (pure SVG)
// ══════════════════════════════════════════════════════════
function SimpleLineChart({ data, width = 820, height = 260 }) {
  const pad = { top: 20, right: 24, bottom: 30, left: 60 };
  const w = width - pad.left - pad.right;
  const h = height - pad.top - pad.bottom;
  const [hover, setHover] = useState(null);

  // Y domain from cost, held price, trend and the competitive line. Comp has to
  // be in here: a Whole Foods price well above or below ours would otherwise be
  // drawn outside the panel and read as missing.
  const allVals = [];
  data.forEach((d) => { if (d.cost != null) allVals.push(d.cost); if (d.priceHeld != null) allVals.push(d.priceHeld); if (d.priceTrend != null) allVals.push(d.priceTrend); if (d.comp != null) allVals.push(d.comp); });
  if (allVals.length === 0) return <div style={{ color: "#585d6e", textAlign: "center", padding: 40 }}>No data</div>;
  const yMin = Math.floor(Math.min(...allVals) * 0.85 * 100) / 100;
  const yMax = Math.ceil(Math.max(...allVals) * 1.15 * 100) / 100;
  const yRange = yMax - yMin || 1;

  const timestamps = data.map((d) => {
    const parts = (d.date || "").split("-");
    return parts.length === 3 ? new Date(parts[0], parts[1] - 1, parts[2]).getTime() : 0;
  });
  const tMin = Math.min(...timestamps), tMax = Math.max(...timestamps);
  const tRange = tMax - tMin || 1;
  const toX = (i) => data.length === 1 ? pad.left + w / 2 : pad.left + ((timestamps[i] - tMin) / tRange) * w;
  const toY = (v) => pad.top + h - ((v - yMin) / yRange) * h;

  const yTicks = [];
  for (let i = 0; i <= 5; i++) yTicks.push(yMin + (yRange * i) / 5);

  // X labels, skipping overlaps
  const visibleLabels = []; let lastX = -Infinity;
  for (let i = 0; i < data.length; i++) {
    const x = toX(i);
    if (x - lastX >= 50 || i === 0 || i === data.length - 1) { visibleLabels.push(i); lastX = x; }
  }

  // Cost — broken segments (a week with no cost breaks the line), dots at each cost
  const costSegs = []; let cc = [];
  data.forEach((d, i) => {
    if (d.cost != null) cc.push({ x: toX(i), y: toY(d.cost) });
    else { if (cc.length) costSegs.push(cc); cc = []; }
  });
  if (cc.length) costSegs.push(cc);
  const costDots = costSegs.reduce((a, s) => a.concat(s), []);

  // Sales — continuous held line; dots ONLY where a real export arrived that week
  const heldPts = data.map((d, i) => d.priceHeld != null ? { x: toX(i), y: toY(d.priceHeld) } : null).filter(Boolean);
  const priceDots = data.map((d, i) => d.price != null ? { x: toX(i), y: toY(d.price) } : null).filter(Boolean);

  // Trend — dashed, no dots
  const trendPts = data.map((d, i) => d.priceTrend != null ? { x: toX(i), y: toY(d.priceTrend) } : null).filter(Boolean);

  // Competitive (Whole Foods) — same axis, same dollars, dashed so it reads as a
  // reference rather than one of our own lines. Breaks at gaps like cost does:
  // a week with no reported price is absent, never interpolated.
  const compSegs = []; let cs = [];
  data.forEach((d, i) => {
    if (d.comp != null) cs.push({ x: toX(i), y: toY(d.comp) });
    else { if (cs.length) compSegs.push(cs); cs = []; }
  });
  if (cs.length) compSegs.push(cs);
  const compDots = compSegs.reduce((a, sg) => a.concat(sg), []);

  const hasComp = data.some((d) => d.comp != null);
  const boxW = 190, boxH = hasComp ? 128 : 108;
  const bx = hover != null ? Math.min(toX(hover) + 12, width - boxW - 4) : 0;
  const Status = ({ y, ok, label }) => (
    <text x={bx + 12} y={y} fill={ok ? "#22c55e" : "#e0685a"} fontSize={11.5} fontFamily="'DM Mono', monospace">
      {ok ? "\u2713 " : "\u2715 "}{label}
    </text>
  );

  return (
    <svg width={width} height={height} style={{ display: "block" }}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        if (mx < pad.left || mx > pad.left + w) { setHover(null); return; }
        let closest = 0, cd = Infinity;
        for (let i = 0; i < data.length; i++) { const dist = Math.abs(toX(i) - mx); if (dist < cd) { cd = dist; closest = i; } }
        setHover(closest);
      }}
      onMouseLeave={() => setHover(null)}>
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={pad.left} y1={toY(v)} x2={pad.left + w} y2={toY(v)} stroke="#222536" strokeDasharray="3 3" />
          <text x={pad.left - 8} y={toY(v) + 4} textAnchor="end" fill="#585d6e" fontSize={11} fontFamily="DM Mono, monospace">${v.toFixed(2)}</text>
        </g>
      ))}
      {visibleLabels.map((i) => (
        <text key={i} x={toX(i)} y={height - 6} textAnchor="middle" fill="#585d6e" fontSize={11}>{data[i].label}</text>
      ))}

      {/* Trend (dashed) */}
      {trendPts.length > 1 && <polyline fill="none" stroke="#22c55e" strokeWidth={2} strokeDasharray="6 3" strokeOpacity={0.6} points={trendPts.map((p) => `${p.x},${p.y}`).join(" ")} />}

      {/* Competitive — dashed amber, broken at gaps */}
      {compSegs.map((seg, si) => seg.length > 1 && <polyline key={"c" + si} fill="none" stroke="#d4a017" strokeWidth={2} strokeDasharray="5 4" strokeOpacity={0.85} points={seg.map((p) => `${p.x},${p.y}`).join(" ")} />)}
      {compDots.map((p, i) => <circle key={"cd" + i} cx={p.x} cy={p.y} r={4} fill="#d4a017" stroke="#0f1117" strokeWidth={1.5} />)}

      {/* Sales — continuous held line, dots only on real export weeks */}
      {heldPts.length > 1 && <polyline fill="none" stroke="#4a7cff" strokeWidth={2.5} points={heldPts.map((p) => `${p.x},${p.y}`).join(" ")} />}
      {priceDots.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={4} fill="#4a7cff" stroke="#0f1117" strokeWidth={1.5} />)}

      {/* Cost — broken segments, dots on real costs only */}
      {costSegs.map((seg, si) => seg.length > 1 && <polyline key={si} fill="none" stroke="#e05252" strokeWidth={2.5} points={seg.map((p) => `${p.x},${p.y}`).join(" ")} />)}
      {costDots.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={4} fill="#e05252" stroke="#0f1117" strokeWidth={1.5} />)}

      {/* Hover tooltip — per-week receipt confirmations for both streams */}
      {hover != null && (
        <g>
          <line x1={toX(hover)} y1={pad.top} x2={toX(hover)} y2={pad.top + h} stroke="#585d6e" strokeDasharray="2 2" />
          <rect x={bx} y={pad.top} width={boxW} height={boxH} rx={7} fill="#1c1f2b" stroke="#2a2d3a" />
          <text x={bx + 12} y={pad.top + 17} fill="#e8eaf0" fontSize={11.5} fontWeight={700}>Week of {data[hover].label}</text>
          <text x={bx + 12} y={pad.top + 36} fill="#e05252" fontSize={12} fontFamily="'DM Mono', monospace">Cost: {data[hover].cost != null ? `$${data[hover].cost.toFixed(2)}` : "\u2014"}</text>
          <Status y={pad.top + 53} ok={data[hover].listReceived} label={data[hover].listReceived ? "price list received" : "no price list received"} />
          <text x={bx + 12} y={pad.top + 74} fill="#4a7cff" fontSize={12} fontFamily="'DM Mono', monospace">Price: {data[hover].priceHeld != null ? `$${data[hover].priceHeld.toFixed(2)}` : "\u2014"}</text>
          <Status y={pad.top + 91} ok={data[hover].exportReceived} label={data[hover].exportReceived ? "export received" : "no export received"} />
          {hasComp && <text x={bx + 12} y={pad.top + 112} fill="#d4a017" fontSize={12} fontFamily="'DM Mono', monospace">Market: {data[hover].comp != null ? `$${data[hover].comp.toFixed(2)}` : "\u2014"}</text>}
        </g>
      )}
    </svg>
  );
}

function ItemDetailModal({ item, history, onClose, compSeries }) {
  if (!item) return null;
  try {
  const data = getItemHistory(history, item);
  // The timeline is the UNION of pricing-history weeks and competitive weeks.
  // Building it from pricing history alone meant a comp week with no vendor or
  // export snapshot that week simply vanished from the chart — the market price
  // showed in the table but never on the graph, which is exactly the kind of
  // silent disagreement between two views that destroys trust in both.
  const anchorDate = thisMonday();
  const weekSet = new Set(data.map((d) => d.date));
  for (const wk of Object.keys(compSeries || {})) weekSet.add(wk);
  const lastKnown = [...weekSet].sort().pop();
  if (lastKnown && lastKnown < anchorDate) weekSet.add(anchorDate);
  const byDate = {};
  for (const d of data) byDate[d.date] = d;
  const points = [...weekSet].sort().map((date) =>
    byDate[date] || { date, cost: null, price: null, listReceived: false, exportReceived: false });

  function linReg(values) {
    const valid = values.map((v, i) => [i, v]).filter(([, v]) => v != null);
    if (valid.length < 2) return null;
    const n = valid.length;
    const sumX = valid.reduce((s, [x]) => s + x, 0);
    const sumY = valid.reduce((s, [, y]) => s + y, 0);
    const sumXY = valid.reduce((s, [x, y]) => s + x * y, 0);
    const sumX2 = valid.reduce((s, [x]) => s + x * x, 0);
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    return { slope, intercept };
  }

  function calcVariance(values) {
    const valid = values.filter((v) => v != null);
    if (valid.length === 0) return null;
    const min = Math.min(...valid);
    const max = Math.max(...valid);
    const avg = valid.reduce((s, v) => s + v, 0) / valid.length;
    const range = max - min;
    const first = valid[0];
    const last = valid[valid.length - 1];
    const pctChange = valid.length >= 2 && first > 0 ? ((last - first) / first) * 100 : null;
    return { min, max, avg, range, pctChange, count: valid.length };
  }

  // Carry the sales price forward — it's "in effect" until a new export changes it.
  let held = null;
  const withHeld = points.map((d) => {
    if (d.price != null) held = d.price;
    return { ...d, label: d.date.slice(5), priceHeld: held };
  });

  const costValues = points.map((d) => d.cost);
  const priceValues = points.map((d) => d.price); // real observed prices only (for stats)
  const priceForStats = item.price != null && !priceValues.some((v) => v != null) ? [item.price] : priceValues;
  const priceTrend = linReg(priceValues);

  // Competitive price per week, derived here from the raw weekly rows and the
  // CURRENT mapping — so a mapping correction reshapes the whole line at once.
  // Manual overrides deliberately do not appear: the line means "what Whole
  // Foods charged", and letting typed values in would make it unreadable later.
  const withTrend = withHeld.map((d, i) => ({
    ...d,
    priceTrend: priceTrend ? Math.round((priceTrend.intercept + priceTrend.slope * i) * 100) / 100 : null,
    comp: compSeries ? (compSeries[d.date] ?? null) : null,
  }));

  const costStats = calcVariance(costValues);
  const priceStats = calcVariance(priceForStats);
  // Margin at each cost observation uses the price in effect that week (held).
  const marginValues = withHeld.map((d) => {
    if (d.priceHeld != null && d.cost != null && d.priceHeld > 0) return Math.round(((d.priceHeld - d.cost) / d.priceHeld) * 1000) / 10;
    return null;
  });
  const marginStats = calcVariance(marginValues);

  const realPointCount = data.length;
  const hasData = withTrend.some((d) => d.cost != null || d.price != null || d.comp != null);

  const StatBlock = ({ label, stats, color, format = "$" }) => {
    if (!stats) return <div style={{ flex: 1, textAlign: "center", color: "#585d6e", fontSize: 12 }}>No data</div>;
    const pctColor = stats.pctChange == null ? "#585d6e" : stats.pctChange > 1 ? "#e05252" : stats.pctChange < -1 ? "#22c55e" : "#8b90a0";
    const fmt = (v) => format === "$" ? `$${v.toFixed(2)}` : `${v.toFixed(1)}%`;
    return (
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>{label} <span style={{ color: "#585d6e", fontWeight: 400 }}>({stats.count} pt{stats.count !== 1 ? "s" : ""})</span></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", fontSize: 12 }}>
          {[{ k: "Min", v: fmt(stats.min) }, { k: "Max", v: fmt(stats.max) }, { k: "Range", v: fmt(stats.range) }, { k: "Average", v: fmt(stats.avg) }].map(({ k, v }) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#585d6e" }}>{k}</span>
              <span style={{ fontFamily: "'DM Mono', monospace", color: "#e8eaf0", fontWeight: 500 }}>{v}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", gridColumn: "1 / -1", marginTop: 2, paddingTop: 4, borderTop: "1px solid #222536" }}>
            <span style={{ color: "#585d6e" }}>% Change</span>
            <span style={{ fontFamily: "'DM Mono', monospace", color: pctColor, fontWeight: 700 }}>
              {stats.pctChange != null ? `${stats.pctChange >= 0 ? "+" : ""}${stats.pctChange.toFixed(1)}%` : "—"}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#0f1117", borderRadius: 16, padding: "28px 32px", width: 900, maxWidth: "95vw", maxHeight: "90vh", overflow: "auto", boxShadow: "0 24px 80px rgba(0,0,0,0.5)", border: "1px solid #2a2d3a" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700, color: "#e8eaf0" }}>{item.name}</h2>
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#8b90a0" }}>
              <span>D365: <span style={{ fontFamily: "'DM Mono', monospace", color: "#e8eaf0" }}>{item.d365}</span></span>
              <span>Vendor: <span style={{ color: "#e8eaf0" }}>{item.vendor}</span></span>
              <span>VPN: <span style={{ fontFamily: "'DM Mono', monospace", color: "#e8eaf0" }}>{item.vpn}</span></span>
              <span>Unit: <span style={{ color: "#e8eaf0" }}>{item.saleUnit}</span></span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#8b90a0", fontSize: 22, cursor: "pointer", padding: "4px 8px", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 20, marginBottom: 28, padding: "14px 20px", background: "#161820", borderRadius: 10, border: "1px solid #222536" }}>
          {[
            { label: "Unit Cost", value: item.cost != null ? `$${item.cost.toFixed(2)}` : "—", color: "#e05252" },
            { label: "Sales Price", value: item.price != null ? `$${item.price.toFixed(2)}` : "—", color: "#4a7cff" },
            { label: "Margin", value: item.margin != null ? `${item.margin.toFixed(1)}%` : "—", color: item.margin < 25 ? "#e05252" : item.margin > 55 ? "#d4a017" : "#3a9a5c" },
            { label: "30% Price", value: item.newPrice != null ? `$${item.newPrice.toFixed(2)}` : "—", color: "#8b90a0" },
            { label: "Casepack", value: item.casepack || "—", color: "#8b90a0" },
            { label: "Data Points", value: String(realPointCount), color: "#8b90a0" },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color, fontFamily: "'DM Mono', monospace" }}>{value}</div>
              <div style={{ fontSize: 10, color: "#585d6e", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>

        {!hasData ? (
          <div style={{ textAlign: "center", padding: "50px 0", color: "#585d6e" }}>
            <div style={{ fontSize: 14, marginBottom: 6 }}>No history data yet</div>
            <div style={{ fontSize: 12 }}>Upload vendor files with dates in the filename to build history</div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ fontSize: 13, fontWeight: 600, color: "#8b90a0", textTransform: "uppercase", letterSpacing: "0.5px", margin: 0 }}>Cost & Price History</h3>
                <div style={{ display: "flex", gap: 16, fontSize: 11 }}>
                  <span><span style={{ display: "inline-block", width: 12, height: 3, background: "#e05252", borderRadius: 1, marginRight: 5, verticalAlign: "middle" }}></span><span style={{ color: "#8b90a0" }}>Cost</span></span>
                  <span><span style={{ display: "inline-block", width: 12, height: 3, background: "#4a7cff", borderRadius: 1, marginRight: 5, verticalAlign: "middle" }}></span><span style={{ color: "#8b90a0" }}>Price</span></span>
                  <span><span style={{ display: "inline-block", width: 12, height: 3, background: "#22c55e", borderRadius: 1, marginRight: 5, verticalAlign: "middle", opacity: 0.6 }}></span><span style={{ color: "#8b90a0" }}>Trend</span></span>
                  {withTrend.some((d) => d.comp != null) && (
                    <span><span style={{ display: "inline-block", width: 12, height: 3, background: "#d4a017", borderRadius: 1, marginRight: 5, verticalAlign: "middle" }}></span><span style={{ color: "#8b90a0" }}>Market</span></span>
                  )}
                </div>
              </div>
              <div style={{ background: "#161820", borderRadius: 10, border: "1px solid #222536", padding: "16px 12px 8px" }}>
                <SimpleLineChart data={withTrend} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 24, padding: "18px 24px", background: "#161820", borderRadius: 10, border: "1px solid #222536" }}>
              <StatBlock label="Vendor Cost" stats={costStats} color="#e05252" />
              <div style={{ width: 1, background: "#2a2d3a" }} />
              <StatBlock label="Sales Price" stats={priceStats} color="#4a7cff" />
              <div style={{ width: 1, background: "#2a2d3a" }} />
              <StatBlock label="Margin" stats={marginStats} color="#3a9a5c" format="%" />
              {priceTrend && (
                <>
                  <div style={{ width: 1, background: "#2a2d3a" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Price Trend</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "6px", fontSize: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "#585d6e" }}>Direction</span>
                        <span style={{ fontFamily: "'DM Mono', monospace", color: priceTrend.slope > 0.01 ? "#e05252" : priceTrend.slope < -0.01 ? "#22c55e" : "#8b90a0", fontWeight: 600 }}>
                          {priceTrend.slope > 0.01 ? "↑ Rising" : priceTrend.slope < -0.01 ? "↓ Falling" : "→ Stable"}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "#585d6e" }}>Per week</span>
                        <span style={{ fontFamily: "'DM Mono', monospace", color: "#e8eaf0", fontWeight: 500 }}>
                          {priceTrend.slope >= 0 ? "+" : ""}${priceTrend.slope.toFixed(3)}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
  } catch(err) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
        <div style={{ background: "#0f1117", borderRadius: 16, padding: "28px 32px", width: 500, boxShadow: "0 24px 80px rgba(0,0,0,0.5)", border: "1px solid #2a2d3a" }} onClick={(e) => e.stopPropagation()}>
          <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700, color: "#e8eaf0" }}>{item.name}</h2>
          <p style={{ color: "#e05252", fontSize: 13 }}>Error loading chart: {err.message}</p>
          <div style={{ marginTop: 16, display: "flex", gap: 20, fontSize: 12, color: "#8b90a0" }}>
            <span>Cost: {item.cost != null ? `$${item.cost.toFixed(2)}` : "—"}</span>
            <span>Price: {item.price != null ? `$${item.price.toFixed(2)}` : "—"}</span>
            <span>Margin: {item.margin != null ? `${item.margin.toFixed(1)}%` : "—"}</span>
          </div>
          <button onClick={onClose} style={{ marginTop: 16, padding: "8px 20px", borderRadius: 6, border: "1px solid #2a2d3a", background: "transparent", color: "#8b90a0", fontSize: 13, cursor: "pointer" }}>Close</button>
        </div>
      </div>
    );
  }
}
// ══════════════════════════════════════════════════════════
export default function PricingDashboard() {
  // === PERSISTENT STATE — loaded from localStorage on init ===
  const [activeStore, setActiveStore] = useState(() => {
    try { return localStorage.getItem("ep_activeStore") || "BO"; } catch { return "BO"; }
  });
  const [storeItems, setStoreItems] = useState(() => {
    try { const raw = localStorage.getItem("ep_storeItems"); const defaults = { BO: [], SH: [], KP: [], AV: [], WP: [], CH: [], DA: [], SV: [], LA: [], TO: [], SG: [], DM: [], EC: [] }; if (!raw) return defaults; const parsed = JSON.parse(raw); return { ...defaults, ...parsed }; } catch { return { BO: [], SH: [], KP: [], AV: [], WP: [], CH: [], DA: [], SV: [], LA: [], TO: [], SG: [], DM: [], EC: [] }; }
  });
  const [vendorData, setVendorData] = useState(() => {
    try {
      const raw = localStorage.getItem("ep_vendorData");
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      // New format: { _ts: "...", data: {...} }. Old format: plain object.
      if (parsed._ts && parsed.data) return parsed.data;
      return parsed;
    } catch { return {}; }
  });
  const [salesPriceLookup, setSalesPriceLookup] = useState(() => {
    try { const raw = localStorage.getItem("ep_salesPriceLookup"); return raw ? JSON.parse(raw) : {}; } catch { return {}; }
  });
  const [uploadLog, setUploadLog] = useState(() => {
    try { const raw = localStorage.getItem("ep_uploadLog"); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });
  // Inputs ledger — every processed file, grouped by the week it landed on.
  // Inputs ledger — shared across the team; hydrated from Supabase on mount.
  const [inputs, setInputs] = useState([]);
  const [showInputsPanel, setShowInputsPanel] = useState(false);
  const [showMarginTrend, setShowMarginTrend] = useState(false);

  // === SALES + AUDIT TABS ===
  // salesWeeks: store -> Monday -> catapultId -> {qty,amt,name,sub}. Keyed this way
  // so re-uploading one week replaces just that week and leaves the rest intact.
  const [activeTab, setActiveTab] = useState("pricing");   // pricing | sales | audit
  const [salesWeeks, setSalesWeeks] = useState({});
  const [catalogIndex, setCatalogIndex] = useState({});    // store -> catalog items with Catapult IDs
  const [listPrices, setListPrices] = useState({});        // persisted via ep_listPrices (Sets serialised)
  // Non-null when loading saved data failed. Saving stays blocked while set, so
  // an empty screen can never be written over good data.
  const [loadFailed, setLoadFailed] = useState(null);
  // Market prices live in their own store, like casepack rules, rather than
  // inside the big storeItems blob. They are researched by hand, they outlive
  // any one catalog upload, and they must survive an item temporarily dropping
  // off the catalog — none of which is true of anything that rides along inside
  // storeItems. Shape: { [store]: { [d365]: number } }.
  const [marketPrices, setMarketPrices] = useState({});
  // Competitive pricing, kept as two separate stores so the mapping stays a real
  // source of truth: compWeeks holds the RAW weekly report rows, compMapping the
  // current Eataly<->WF mapping. Comp prices are derived from the pair at read
  // time, so correcting a mapping error fixes every past week at once.
  // compWeeks: { [monday]: { asOf, byCity: { CITY: { wfId: {price,uom,name} } } } }
  const [compWeeks, setCompWeeks] = useState({});
  const [compMapping, setCompMapping] = useState(null);

  // === NON-PERSISTENT UI STATE ===
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [sortCol, setSortCol] = useState("margin");
  const [sortDir, setSortDir] = useState("asc");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [dragTarget, setDragTarget] = useState(null);
  const [notification, setNotification] = useState(null);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadType, setUploadType] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [storeHistory, setStoreHistory] = useState({});
  const [casepackRules, setCasepackRules] = useState([]); // shared: hydrated from Supabase on mount
  const [showRulesPanel, setShowRulesPanel] = useState(false);
  const lastUploadDateRef = useRef(null);

  // === PERSISTENCE ===
  // Small things (active store, upload log, inputs ledger) stay in localStorage.
  // The big blobs (store items, vendor lists, sales lookup) go to IndexedDB, which
  // isn't capped at ~5MB. Writes are gated until after hydration so we never clobber
  // saved data with the empty initial state, and a real failure notifies the user.
  const hydratedRef = useRef(false);
  const saveTimerRef = useRef(null);
  const idbSaveTimerRef = useRef(null);
  const idbSaveFailedRef = useRef(false);

  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        // Per-browser only: last upload log + which store tab this user last viewed.
        // (Shared data — items, vendor, sales, history, rules, inputs — goes to Supabase.)
        localStorage.setItem("ep_uploadLog", JSON.stringify(uploadLog.slice(-50)));
        localStorage.setItem("ep_activeStore", activeStore);
      } catch (e) { console.warn("localStorage save failed:", e); }
    }, 500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [uploadLog, activeStore]);

  useEffect(() => {
    if (!hydratedRef.current) return; // don't persist before we've loaded from IDB
    if (loadFailed) return;           // and never while a load failure is outstanding
    if (idbSaveTimerRef.current) clearTimeout(idbSaveTimerRef.current);
    idbSaveTimerRef.current = setTimeout(async () => {
      try {
        await idbSet("ep_storeItems", storeItems);
        await idbSet("ep_vendorData", vendorData);
        await idbSet("ep_salesPriceLookup", salesPriceLookup);
        await idbSet("ep_casepackRules", casepackRules);
        await idbSet("ep_inputs", inputs.slice(-500));
        await idbSet("ep_salesWeeks", trimSalesWeeks(salesWeeks, 26));
        await idbSet("ep_catalogIndex", catalogIndex);
        await idbSet("ep_listPrices", serializeListPrices(listPrices));
        await idbSet("ep_marketPrices", marketPrices);
        await idbSet("ep_compWeeks", trimCompWeeks(compWeeks));
        await idbSet("ep_compMapping", compMapping);
        // Read-back verify: confirm the write actually landed (a silent no-op is worse than an error).
        const check = await idbGet("ep_storeItems");
        if (check == null) throw new Error("write did not persist (read-back empty)");
        idbSaveFailedRef.current = false;
      } catch (e) {
        console.error("Shared database save failed:", e);
        if (!idbSaveFailedRef.current) {
          idbSaveFailedRef.current = true;
          showNotification_(`⚠ Changes did NOT save to the shared database (${e?.name || e?.message || String(e)}). Check your connection — see console.`, "warn");
        }
      }
    }, 600);
    return () => { if (idbSaveTimerRef.current) clearTimeout(idbSaveTimerRef.current); };
  }, [storeItems, vendorData, salesPriceLookup, casepackRules, inputs, salesWeeks, catalogIndex, listPrices, marketPrices, compWeeks, compMapping, loadFailed]);

  // Hydrate big blobs from IndexedDB on mount; migrate any legacy localStorage copy.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const defaults = { BO: [], SH: [], KP: [], AV: [], WP: [], CH: [], DA: [], SV: [], LA: [], TO: [], SG: [], DM: [], EC: [] };
      const [si, vd, sp] = await Promise.all([idbRead("ep_storeItems"), idbRead("ep_vendorData"), idbRead("ep_salesPriceLookup")]);
      if (cancelled) return;

      // If ANY of the three core reads failed, stop here. Do not seed defaults,
      // do not clear legacy keys, and leave hydratedRef false so the save effect
      // stays blocked — otherwise the empty starting state gets written over
      // real data and a temporary outage becomes permanent deletion.
      const failed = [si, vd, sp].filter((r) => !r.ok);
      if (failed.length) {
        setLoadFailed(failed[0].error?.message || "could not reach the database");
        console.error("Hydration failed; saving is disabled to protect stored data.", failed.map((f) => f.error));
        return;
      }

      if (si.value != null) setStoreItems({ ...defaults, ...si.value });
      if (vd.value != null) setVendorData(vd.value);
      if (sp.value != null) setSalesPriceLookup(sp.value);
      // Shared casepack rules + inputs ledger. Migrate a legacy per-browser copy up
      // to the shared DB the first time, so nothing is lost moving to multi-user.
      const sw = await idbRead("ep_salesWeeks");
      if (sw.value != null && !cancelled) setSalesWeeks(sw.value);
      const ci = await idbRead("ep_catalogIndex");
      if (ci.value != null && !cancelled) setCatalogIndex(ci.value);
      const lp = await idbRead("ep_listPrices");
      if (lp.value != null && !cancelled) setListPrices(hydrateListPrices(lp.value));
      // Market prices: read the dedicated store, and if it doesn't exist yet,
      // lift any values still sitting inside storeItems into it once.
      const cw = await idbRead("ep_compWeeks");
      if (cw.value != null && !cancelled) setCompWeeks(cw.value);
      const cm = await idbRead("ep_compMapping");
      if (cm.value != null && !cancelled) setCompMapping(cm.value);
      const mp = await idbRead("ep_marketPrices");
      if (mp.value != null && !cancelled) setMarketPrices(mp.value);
      else if (mp.ok && !cancelled && si.value) {
        const lifted = {};
        for (const [abbr, rows] of Object.entries(si.value)) {
          for (const r of rows || []) {
            const d = String(r?.d365 ?? "").trim();
            if (d && r.marketPrice != null) { (lifted[abbr] = lifted[abbr] || {})[d] = { value: r.marketPrice, at: "0000-00-00" }; }
          }
        }
        if (Object.keys(lifted).length) {
          setMarketPrices(lifted);
          try { await idbSet("ep_marketPrices", lifted); } catch {}
        }
      }
      let cr = await idbGet("ep_casepackRules");
      if (cr == null) { try { const raw = localStorage.getItem("casepack_rules"); if (raw) { cr = JSON.parse(raw); await idbSet("ep_casepackRules", cr); } } catch {} }
      if (cr != null && !cancelled) setCasepackRules(cr);
      let inp = await idbGet("ep_inputs");
      if (inp == null) { try { const raw = localStorage.getItem("ep_inputs"); if (raw) { inp = JSON.parse(raw); await idbSet("ep_inputs", inp); } } catch {} }
      if (inp != null && !cancelled) setInputs(inp);
      // Only free the oversized localStorage keys once IndexedDB has safely taken over.
      // Only reached when all three core reads succeeded, so the shared DB is
      // authoritative and the legacy per-browser copies can go.
      try { localStorage.removeItem("ep_storeItems"); localStorage.removeItem("ep_vendorData"); localStorage.removeItem("ep_salesPriceLookup"); } catch {}
      // One-time sweep: history from pre-IndexedDB builds may still sit in localStorage
      // under hist:* keys and keep the 5MB quota wedged. Best-effort migrate each into IDB
      // (only if IDB doesn't already have it), then ALWAYS free the localStorage copy —
      // the real history lives in IDB now, and any copy that fails to migrate was
      // truncated/corrupt (worthless) and would only keep the quota jammed.
      try {
        const legacyHist = Object.keys(localStorage).filter((k) => k.startsWith("hist:"));
        for (const k of legacyHist) {
          try {
            const existing = await idbGet(k);
            if (existing == null) {
              const raw = localStorage.getItem(k);
              if (raw) { try { await idbSet(k, JSON.parse(raw)); } catch {} }
            }
          } catch {}
          try { localStorage.removeItem(k); } catch {}
        }
      } catch {}
      hydratedRef.current = true;
    })();
    return () => { cancelled = true; };
  }, []);

  // On mount, confirm the shared backend is reachable and writable so a
  // misconfiguration surfaces immediately instead of silently losing data.
  useEffect(() => {
    if (!supabase) {
      showNotification_("⚠ Backend not configured — set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY. Data won't be saved.", "warn");
      return;
    }
    // quick self-test: write and read back a probe key against the shared database
    (async () => {
      try {
        await idbSet("ep_probe", { t: Date.now() });
        const back = await idbGet("ep_probe");
        if (back == null) throw new Error("probe read-back empty");
      } catch (e) {
        showNotification_(`⚠ Couldn't reach the shared database (${e?.name || e?.message || e}). Changes may not save — see console.`, "warn");
        console.error("Supabase self-test failed:", e);
      }
    })();
  }, []);

  // Persist rules whenever they change
  // Rules persist to the shared database via the main save effect (deps include casepackRules).

  // Auto-reprocess all stores when casepack rules change (skip on initial mount)
  const rulesInitialized = useRef(false);
  useEffect(() => {
    if (!rulesInitialized.current) {
      rulesInitialized.current = true;
      return; // Skip first render
    }
    // Re-run processStore for all stores that have items
    if (Object.keys(vendorData).length === 0) return; // No vendor data loaded yet
    setStoreItems((prev) => {
      const next = { ...prev };
      const preserveEdits = (newItems, oldItems) => {
        if (!oldItems || oldItems.length === 0) return newItems;
        const carryMap = {};
        for (const old of oldItems) {
          if (old.discount?.active || old.adjPrice != null) {
            carryMap[old.d365] = { discount: old.discount?.active ? old.discount : null, adjPrice: old.adjPrice };
          }
        }
        return newItems.map((it) => {
          const carry = carryMap[it.d365];
          if (!carry) return it;
          return { ...it, ...(carry.discount ? { discount: carry.discount } : {}), ...(carry.adjPrice != null ? { adjPrice: carry.adjPrice } : {}) };
        });
      };
      for (const abbr of ACTIVE_STORES) {
        if (prev[abbr]?.length > 0) {
          const store = STORES.find((s) => s.abbr === abbr);
          const catItems = prev[abbr].map((it) => ({
            d365: it.d365, name: it.name, unit: it.saleUnit || it.unit || "EA", vendor: it.vendor, vpn: it.vpn
          }));
          next[abbr] = tagNewIn(preserveEdits(processStore(catItems, vendorData, salesPriceLookup, store, casepackRules), prev[abbr]), abbr, storeHistory);
        }
      }
      return next;
    });
  }, [casepackRules]);

  // Startup cleanup — trim old history and clear stale vendor data
  useEffect(() => {
    (async () => {
      // Trim history snapshots
      for (const abbr of ACTIVE_STORES) {
        await loadHistory(abbr);
      }
    })();
  }, []);

  // Remove one week: deletes that Monday's snapshot from every store's history
  // and removes that week's file entries from the Inputs ledger (kept in sync).
  const handleRemoveWeek = useCallback((week) => {
    if (!window.confirm(`Remove everything recorded for the week of ${week}?\n\nThis deletes that week's snapshot from all stores' cost/price history and removes that week's files from the Inputs list. Your current items and prices aren't affected — only the recorded history for that week. This cannot be undone.`)) return;
    (async () => {
      try {
        const keys = (await idbKeys()).filter((k) => typeof k === "string" && k.startsWith("hist:"));
        for (const k of keys) {
          const arr = (await idbGet(k)) || [];
          await idbSet(k, arr.filter((s) => s.date !== week)).catch(() => {});
        }
      } catch {}
    })();
    setStoreHistory((prev) => {
      const next = {};
      for (const [abbr, arr] of Object.entries(prev)) next[abbr] = (arr || []).filter((s) => s.date !== week);
      return next;
    });
    // Competitive data is stored per week too, so it has to go with the rest.
    // Without this a week removed from history keeps plotting its market point,
    // which is how a mis-dated upload survives a cleanup and quietly reappears.
    setCompWeeks((prev) => {
      if (!prev || !(week in prev)) return prev;
      const next = { ...prev };
      delete next[week];
      return next;
    });
    setInputs((prev) => prev.filter((e) => e.date !== week));
    showNotification_(`Removed the week of ${week}`, "info");
  }, []);

  // Clear the active store's price/cost history so it can be rebuilt cleanly by
  // re-uploading dated files under the current rules.
  const handleRebuildHistory = useCallback(() => {
    if (!window.confirm(`Clear cost & price history for ${activeStore}? This removes the recorded weekly snapshots for this store only — items, market prices, discounts and new prices are kept. Re-upload your dated vendor/sales files (Monday-dated) to rebuild it. This cannot be undone.`)) return;
    idbDel(`hist:${activeStore}`);
    try { localStorage.removeItem(`hist:${activeStore}`); } catch {}
    setStoreHistory((prev) => ({ ...prev, [activeStore]: [] }));
    showNotification_(`History cleared for ${activeStore} — re-upload dated files to rebuild`, "info");
  }, [activeStore]);

  // Clear adjPrice (New Price column) for the active store only
  const handleClearNewPrices = useCallback(() => {
    setStoreItems((prev) => {
      const cur = prev[activeStore] || [];
      const count = cur.filter((i) => i.adjPrice != null).length;
      if (count === 0) { showNotification_("No new prices to clear", "info"); return prev; }
      showNotification_(`Cleared ${count} new price${count !== 1 ? "s" : ""} for ${activeStore}`, "info");
      return { ...prev, [activeStore]: cur.map((it) => it.adjPrice != null ? { ...it, adjPrice: null } : it) };
    });
  }, [activeStore]);

  // Clear all persistent data
  const handleClearAllData = useCallback(() => {
    if (!window.confirm("Clear all data? This removes items, vendor data, history and discounts. Casepack rules and market prices are kept. This cannot be undone.")) return;
    try {
      const keys = Object.keys(localStorage).filter((k) => (k.startsWith("ep_") || k.startsWith("hist:")) && k !== "casepack_rules");
      keys.forEach((k) => localStorage.removeItem(k));
    } catch {}
    idbClear(); // wipe IndexedDB (items, vendor data, history)
    hydratedRef.current = true; // allow the now-empty state to persist
    setStoreItems({ BO: [], SH: [], KP: [], AV: [], WP: [], CH: [], DA: [], SV: [], LA: [], TO: [], SG: [], DM: [], EC: [] });
    setVendorData({});
    setSalesPriceLookup({});
    setUploadLog([]);
    setInputs([]);
    setStoreHistory({});
    setSelectedItems(new Set());
    showNotification_("All data cleared (casepack rules and market prices kept)", "info");
  }, []);

  // Inline notification helper (defined early for handleClearAllData)
  const showNotification_ = useCallback((msg, type = "info") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  // Load history from persistent storage when store changes
  useEffect(() => {
    (async () => {
      if (!storeHistory[activeStore]) {
        const hist = await loadHistory(activeStore);
        if (hist.length > 0) {
          setStoreHistory((prev) => ({ ...prev, [activeStore]: hist }));
        }
      }
    })();
  }, [activeStore]);

  const showNotification = showNotification_;

  const rawItems = storeItems[activeStore] || [];

  // ── Gross profit per item ────────────────────────────────
  // Sizes the decision: a 5-point margin move on something selling 400 lb a
  // week matters, the same move on a 2-unit item does not.
  //
  // Quantity comes from the most recent COMPLETE sales week for this store,
  // matched the same two-pass way the Sales tab matches — Catapult ID first,
  // then the D365 number — because an item the register rings under its D365
  // number is a mapping defect, not a zero seller, and reading it as zero would
  // hide exactly the items that need attention.
  const lastSalesWeek = useMemo(() => {
    const weeks = Object.keys(salesWeeks[activeStore] || {}).sort();
    return weeks.length ? weeks[weeks.length - 1] : null;
  }, [salesWeeks, activeStore]);

  // d365 -> qty, built once per store/week.
  const qtyByItem = useMemo(() => {
    const out = {};
    if (!lastSalesWeek) return out;
    const wk = (salesWeeks[activeStore] || {})[lastSalesWeek] || {};
    const cat = catalogIndex[activeStore] || [];
    const byCid = {}, byD365 = {};
    for (const c of cat) {
      const cid = String(c.cid ?? "").trim();
      const d = String(c.d365 ?? "").trim();
      if (cid) (byCid[cid] = byCid[cid] || []).push(d);
      if (d) (byD365[d] = byD365[d] || []).push(d);
    }
    for (const [pid, rec] of Object.entries(wk)) {
      const key = String(pid ?? "").trim();
      const targets = byCid[key] || byD365[key];   // pass 1, then pass 2
      if (!targets) continue;                      // rang with no catalog row
      const q = Number(rec?.qty);
      if (!isFinite(q)) continue;
      for (const d of targets) out[d] = (out[d] || 0) + q;
    }
    return out;
  }, [salesWeeks, catalogIndex, activeStore, lastSalesWeek]);

  // Attach quantity and the two gross-profit figures.
  //
  //   GP $     = (price − cost) × qty, all three as they stood in the sales week.
  //              A real historical figure: what the item actually earned.
  //   New GP $ = (current price − current cost) × the same qty, overridden by
  //              the New Price once one is typed.
  //
  // Holding quantity constant across both columns is deliberate — only price and
  // cost move, so the difference between them is attributable. With no New Price
  // entered, that difference is the effect of the cost change on its own: if a
  // vendor raised you, you see the dollar damage before deciding anything. Type
  // a New Price and you see whether it recovers.
  //
  // Prices and costs carry forward from the last week each was observed, and
  // independently of one another, because they arrive in different files and a
  // week with no vendor upload means the price didn't change — not that it
  // became unknown. The tooltip names the week each figure came from whenever
  // it isn't the sales week itself.
  // Latest comp week and its resolved rows for this store, derived from the raw
  // report plus the CURRENT mapping — never from anything stored pre-resolved.
  // { week -> comp price } for one item at the active store, derived fresh from
  // the raw weekly rows and the current mapping every time it's opened.
  const compSeriesFor = useCallback((item) => {
    if (!item || !compMapping) return null;
    const out = {};
    for (const [week, wk] of Object.entries(compWeeks || {})) {
      const r = compPriceFor(item.d365, activeStore, wk.byCity, compMapping);
      if (r) out[week] = r.price;
    }
    return Object.keys(out).length ? out : null;
  }, [compWeeks, compMapping, activeStore]);

  const latestCompWeek = useMemo(() => {
    const ks = Object.keys(compWeeks || {}).sort();
    return ks.length ? ks[ks.length - 1] : null;
  }, [compWeeks]);

  const items = useMemo(() => {
    const hist = storeHistory[activeStore] || [];
    const compCity = (latestCompWeek && compWeeks[latestCompWeek]) ? compWeeks[latestCompWeek].byCity : null;
    const mappedIds = new Set((catalogIndex[activeStore] || []).map((c) => String(c.d365 ?? "").trim()).filter(Boolean));
    // Snapshots at or before the sales week, newest first.
    const priorSnaps = lastSalesWeek ? hist.filter((h) => h.date <= lastSalesWeek).sort((a, b) => (a.date < b.date ? 1 : -1)) : [];

    return rawItems.map((it) => {
      const d = String(it.d365 ?? "").trim();
      const qty = !lastSalesWeek || !mappedIds.has(d) ? null : (qtyByItem[d] || 0);

      // Last observed cost and price at or before the sales week, each found
      // independently.
      let hCost = null, hPrice = null, costWk = null, priceWk = null;
      if (qty != null) {
        for (const snap of priorSnaps) {
          if (hCost != null && hPrice != null) break;
          const rec = readHistRec(snap, it);
          if (!rec) continue;
          if (hCost == null && rec.cost != null) { hCost = rec.cost; costWk = snap.date; }
          if (hPrice == null && rec.price != null) { hPrice = rec.price; priceWk = snap.date; }
        }
      }
      const gp = qty == null || hCost == null || hPrice == null ? null : (hPrice - hCost) * qty;

      // Forward-looking: live cost, and the New Price if typed, else live price.
      const fwdPrice = it.adjPrice != null ? it.adjPrice : it.price;
      const newGp = qty == null || it.cost == null || fwdPrice == null ? null : (fwdPrice - it.cost) * qty;

      const gpDelta = gp == null || newGp == null ? null : newGp - gp;
      // Market price: reported value vs manual entry, most recent write wins.
      // Absence never overwrites — a missing city row or a skipped week leaves
      // whatever is already there, including a cell cleared by hand.
      const manual = (marketPrices[activeStore] || {})[d];
      const comp = compCity ? compPriceFor(d, activeStore, compCity, compMapping) : null;
      const manualAt = manual && typeof manual === "object" ? manual.at : (manual != null ? "0000-00-00" : null);
      const manualVal = manual && typeof manual === "object" ? manual.value : (manual ?? null);
      // >= not >: an edit made in the same week the report covers is a correction
      // TO that report and must stand. Only a LATER report supersedes it.
      const useManual = manual != null && (!comp || !latestCompWeek || String(manualAt) >= String(latestCompWeek));
      const marketPrice = useManual ? manualVal : (comp ? comp.price : (manual != null ? manualVal : null));
      const gapPct = marketPrice != null && marketPrice > 0 && it.price != null
        ? ((it.price - marketPrice) / marketPrice) * 100 : null;

      return { ...it,
        marketPrice,
        marketIsManual: useManual,
        comp,                      // null when this item has no comp this week
        gapPct,
        qtySold: qty, gpDollars: gp, newGpDollars: newGp, gpDelta,
        gpCost: hCost, gpPrice: hPrice, gpCostWeek: costWk, gpPriceWeek: priceWk,
        newGpUsesAdj: it.adjPrice != null };
    });
  }, [rawItems, qtyByItem, lastSalesWeek, catalogIndex, activeStore, storeHistory, marketPrices, compWeeks, compMapping, latestCompWeek]);

  // ── File handler — auto-detects file type and parses ──
  const handleFiles = useCallback(async (files) => {
    setProcessing(true);
    const log = [];

    // Helper: check if workbook looks like a sales price export by column names
    const looksLikeSalesPrice = (workbook) => {
      try {
        const ws = workbook.Sheets[workbook.SheetNames[0]];
        const headers = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" })[0] || [];
        const hUpper = headers.map((h) => String(h).toUpperCase().trim());
        return hUpper.some((h) => h === "ITEM") && hUpper.some((h) => h.includes("ACCOUNT")) && hUpper.some((h) => h.includes("PRICE"));
      } catch { return false; }
    };

    // Classify all files first
    const vendorFiles = [];
    const catalogFiles = [];
    const salesFiles = [];
    const inputEntries = [];   // ledger rows for this upload
    const skipped = [];        // files rejected for having no date
    const uploadedAt = new Date().toISOString();
    let eid = 0;

    for (const file of files) {
      try {
        const ab = await file.arrayBuffer();
        const wb = readXlsx(ab);
        const fn = file.name.toUpperCase();
        const fileDate = parseDateFromFilename(file.name);

        // Catalogs are a roster (not weekly cost/price data), so they may be
        // undated. Vendor lists and sales exports carry weekly data — if they
        // have no parseable date we REJECT them rather than risk stamping an old
        // file onto the current week.
        // The competitive MAPPING is the same kind of thing: a reference list
        // replaced wholesale, with no week attached. The competitive REPORT is
        // weekly and still needs its date.
        const isUndatedReference = fn.includes("CATALOG") || fn.includes("D365") || fn.includes("CANADA")
          || fn.includes("MAPPING");
        // A year typo sails through every other check: 2016-10-10 is a perfectly
        // valid Monday. It only shows up much later as a stray point on a chart
        // whose labels don't carry the year. Reject anything implausibly old.
        if (fileDate != null) {
          const ageWeeks = (Date.now() - new Date(fileDate + "T00:00:00").getTime()) / (7 * 86400000);
          if (ageWeeks > 104) {
            skipped.push(file.name);
            log.push(`⏭ Skipped — ${file.name} dates to ${fileDate}, over two years ago. Check the year in the filename.`);
            continue;
          }
        }
        if (fileDate == null && !isUndatedReference) {
          skipped.push(file.name);
          log.push(`⏭ Skipped — no date in filename: ${file.name}`);
          continue;
        }

        const vBefore = vendorFiles.length, cBefore = catalogFiles.length, sBefore = salesFiles.length;

        if (fn.includes("BALDOR")) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "baldor", parse: () => parseBaldor(wb) });
        } else if (fn.includes("FP_FL") || fn.includes("FP FL") || fn.includes("FRESHPOINT_FL") || (fn.includes("FRESHPOINT") && fn.includes("FLORIDA"))) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "freshpoint_fl", parse: () => parseFreshpointFL(wb) });
        } else if (fn.includes("FP_DA") || fn.includes("FP DA") || fn.includes("FRESHPOINT_DA") || (fn.includes("FRESHPOINT") && fn.includes("DALLAS"))) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "freshpoint_da", parse: () => parseFreshpointDA(wb) });
        } else if (fn.includes("FP_SC") || fn.includes("FP SC") || fn.includes("FRESHPOINT_SC") || (fn.includes("FRESHPOINT") && fn.includes("CALIFORNIA"))) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "freshpoint_sc", parse: () => parseFreshpointSC(wb) });
        } else if (fn.includes("MR_GREEN") || fn.includes("MRGREEN") || fn.includes("MR GREEN") || fn.includes("FLORIDA_VEG") || fn.includes("FLORIDA VEG")) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "mrgreen", parse: () => parseMrGreens(wb) });
        } else if (fn.includes("TESTA")) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "testa", parse: () => parseTesta(wb) });
        } else if (fn.includes("MIDWEST")) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "midwest", parse: () => parseMidwest(wb) });
        } else if (fn.includes("HARDIE") || fn.includes("HARDIES") || fn.includes("HARDIE_S") || fn.includes("HARDIE\'S")) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "hardies", parse: () => parseHardies(wb) });
        } else if (fn.includes("OK") && (fn.includes("PRODUCE") || fn.startsWith("OK_") || fn.startsWith("OK.") || fn.includes("BOOK2"))) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "ok", parse: () => parseOKProduce(wb) });
        } else if (fn.includes("VESTA")) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "vesta", parse: () => parseVesta(wb) });
        } else if (fn.includes("CHARLIE")) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "charlies", parse: () => parseCharlies(wb) });
        // === CANADA VENDORS ===
        } else if (fn.includes("ZEUS")) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "zeus", parse: () => parseZeus(wb) });
        } else if (fn.includes("BONDI")) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "bondi", parse: () => parseBondi(wb) });
        } else if (fn.includes("BURNAC") || fn.includes("PROVINCIAL")) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "burnac", parse: () => parseBurnac(wb) });
        } else if (fn.includes("CW") || fn.includes("DAIRYLAND") || fn.includes("CHEF")) {
          vendorFiles.push({ fn: file.name, fileDate, vendorKey: "cw", parse: () => {
            const regions = ["NY", "FL", "Chicago", "Texas", "LA", "SF"];
            const result = {};
            const regionsFound = [];
            for (const region of regions) {
              if (wb.SheetNames.some((s) => s.toUpperCase() === region.toUpperCase())) {
                result[`cw_${region}`] = parseCW(wb, region);
                regionsFound.push(region);
              }
            }
            if (regionsFound.length > 0) result.cw = result[`cw_${regionsFound[0]}`];
            return { _multi: true, data: result, regions: regionsFound };
          }});
        // === CANADA CATALOG (must come before generic CATALOG check) ===
        } else if (fn.includes("CANADA")) {
          const canItems = parseCanadaCatalog(wb);
          try {                                             // Catapult IDs for Sales + Audit
            const { byStore } = parseCatalogCatapult(wb, ["TO", "SG", "DM", "EC"], true);
            setCatalogIndex((prev) => ({ ...prev, ...byStore }));
          } catch (e) { console.warn("Canada Catapult index failed:", e); }
          for (const [abbr, items] of Object.entries(canItems)) {
            if (items.length > 0) {
              catalogFiles.push({ abbr, items });
              log.push(`✓ ${STORES.find((s) => s.abbr === abbr)?.name || abbr} catalog: ${items.length} Produce RTL items`);
            }
          }
        // === COMPETITIVE PRICE MAPPING ===
        // Before the report branch: both filenames contain "COMPETITIVE PRIC".
        // Content sniffing decides, so a rename can't misroute either one.
        } else if (looksLikeCompMapping(wb)) {
          const parsed = parseCompMapping(wb);
          if (!parsed) { log.push(`⚠ ${file.name}: couldn't read the mapping sheet`); }
          else {
            setCompMapping(parsed.map);          // wholesale replace: source of truth
            const n = Object.keys(parsed.map).length;
            log.push(`✓ Competitive mapping: ${n} items`);
            inputEntries.push({ id: `in${uploadedAt}-${eid++}`, name: file.name, type: "Comp mapping",
              target: "Market price mapping", date: fileDate || thisMonday(), uploadedAt, rows: n });
            if (parsed.multi.length) log.push(`  ↳ ${parsed.multi.length} item(s) with alternates: ${parsed.multi.join(", ")} (first with a price wins)`);
          }
        // === COMPETITIVE PRICING REPORT (Whole Foods) ===
        } else if (looksLikeCompReport(wb)) {
          const parsed = parseCompReport(wb);
          if (!parsed) { log.push(`⚠ ${file.name}: couldn't read the competitive report`); }
          else {
            // Stored raw under the week from the FILENAME, per the usual rule.
            // "As of Date" is the vendor's stamp and is kept for display only —
            // in the first file it was a Tuesday, so it can't drive bucketing.
            setCompWeeks((prev) => trimCompWeeks({ ...prev, [fileDate]: { asOf: parsed.asOf, byCity: parsed.byCity } }));
            const cities = Object.keys(parsed.byCity);
            const rows = cities.reduce((n, c) => n + Object.keys(parsed.byCity[c]).length, 0);
            log.push(`✓ Competitive prices ${fileDate}: ${rows} items across ${cities.length} cities${parsed.asOf ? ` (as of ${parsed.asOf})` : ""}`);
            inputEntries.push({ id: `in${uploadedAt}-${eid++}`, name: file.name, type: "Comp prices",
              target: cities.join(", "), date: fileDate, uploadedAt, rows });
            if (parsed.unknownCities.length) log.push(`  ⚠ unmapped cities ignored: ${parsed.unknownCities.join(", ")}`);
          }
        // === CATAPULT PRODUCT SALES REPORT ===
        // Placed before the sales-PRICE branches because this filename also
        // contains the word "Sales".
        } else if (fn.includes("PRODUCT SALES") || looksLikeSalesReport(wb)) {
          const wk = salesWeekFromFilename(file.name);
          if (!wk) {
            skipped.push(file.name);
            log.push(`\u23ed Skipped \u2014 no date in filename: ${file.name}`);
            continue;
          }
          if (wk.warning) log.push(`\u26a0 ${file.name}: ${wk.warning}`);
          const parsedSales = parseSalesReport(wb);
          const salesStores = Object.keys(parsedSales).filter((k) => k !== "_meta");
          setSalesWeeks((prev) => {
            const next = { ...prev };
            for (const st of salesStores) next[st] = { ...(next[st] || {}), [wk.week]: parsedSales[st] };
            return next;
          });
          const nRows = salesStores.reduce((n, st) => n + Object.keys(parsedSales[st]).length, 0);
          log.push(`\u2713 Sales report ${wk.covers[0]}\u2026${wk.covers[1]}: ${salesStores.length} stores, ${nRows} rows`);
          inputEntries.push({ id: `in${uploadedAt}-${eid++}`, name: file.name, type: "Sales report",
            target: salesStores.join(", "), date: wk.week, uploadedAt, rows: nRows });
        // === CANADA SALES PRICES (CAN in name + looks like sales price) ===
        } else if (fn.includes("CAN") && !fn.includes("SCAN") && looksLikeSalesPrice(wb)) {
          const parsed = parseSalesPrice(wb);
          salesFiles.push({ parsed, fileDate });
          // Canada list prices were being dropped here — this branch parsed the
          // pricing-side lookup but never the Sales tab's list prices, so TO/SG/
          // DM/EC showed a blank List column whenever Canada arrived as its own
          // file. Merged per store so a US export and a Canada export combine.
          try {
            const canLp = parseListPrices(wb);
            setListPrices((prev) => ({ ...prev, ...canLp }));
            log.push(`\u2713 Canada list prices: ${Object.keys(canLp).join(", ") || "none"}`);
          } catch (e) { console.warn("Canada list prices failed:", e); }
          log.push(`✓ Canada Sales Prices: ${Object.keys(parsed).length} records`);
        } else if (
          fn.includes("SALES") || fn.includes("PRICE_AGREEMENT") || fn.includes("PRICE AGREEMENT") ||
          fn.includes("PRICING") || fn.includes("PRICE_EXPORT") || looksLikeSalesPrice(wb)
        ) {
          const parsed = parseSalesPrice(wb);
          salesFiles.push({ parsed, fileDate });
          try {
            const lp = parseListPrices(wb);
            // Merge per store rather than replace: a US-only export must not wipe
            // Canada's list prices (or vice versa) when they arrive separately.
            setListPrices((prev) => ({ ...prev, ...lp }));
            const nKeys = Object.values(lp).reduce((n, b) => n + Object.keys(b.price).length, 0);
            log.push(`\u2713 List prices: ${Object.keys(lp).length} stores, ${nKeys} priced items`);
          } catch (e) { console.warn("list prices failed:", e); }
          log.push(`✓ Sales Prices: ${Object.keys(parsed).length} records`);
        } else if (fn.includes("CATALOG") || fn.includes("D365")) {
          // Regional (multi-store) format first, detected by content: a sheet
          // with store-code columns and a Catapult ID column. Filename tells us
          // nothing useful here — "Tri-State_Area_-_Fresh_Catalog" and
          // "D365_Catalog_Boston" are the same format now — and the sheet names
          // are mid-rename. If this returns null the file is the old per-store
          // shape and we fall through to filename matching below.
          let matched = false;
          let regional = null;
          try { regional = parseRegionalCatalog(wb); }
          catch (e) { console.warn("Regional catalog parse failed:", e); }

          if (regional) {
            const m = regional.meta;
            setCatalogIndex((prev) => ({ ...prev, ...regional.catapultByStore }));
            for (const abbr of m.stores) {
              const items = regional.byStore[abbr] || [];
              if (!items.length) continue;
              catalogFiles.push({ abbr, items });
              log.push(`✓ ${STORES.find((s) => s.abbr === abbr)?.name || abbr} catalog: ${items.length} Produce RTL items`);
            }
            const dropped = [];
            if (m.excludedBackup) dropped.push(`${m.excludedBackup} back-up`);
            if (m.excludedProduction) dropped.push(`${m.excludedProduction} production`);
            if (m.dupRows) dropped.push(`${m.dupRows} duplicate rows`);
            log.push(`  ↳ sheet "${m.sheet}", ${m.rows} produce rows${dropped.length ? ` (excluded ${dropped.join(", ")})` : ""}`);
            if (m.noCatapult) log.push(`  ⚠ ${m.noCatapult} rows have no Catapult ID — these cannot match sales`);
            // Say out loud which store columns were seen but not parsed, so a
            // store going live is a deliberate change rather than a surprise.
            if (m.inactiveCols.length) log.push(`  ↳ ignored store columns: ${m.inactiveCols.join(", ")} (not active in the dashboard)`);
            matched = true;
          }

          const storePatterns = [
            { abbr: "BO", patterns: ["BOSTON", "_BO_", "_BO.", "BO_"] },
            { abbr: "SH", patterns: ["SHORT_HILL", "SHORT HILL", "SHORTHILLS", "_SH_", "_SH.", "SH_"] },
            { abbr: "KP", patterns: ["KOP", "KING_OF_PRUSSIA", "KING OF PRUSSIA", "KINGOFPRUSSIA", "_KP_", "_KP.", "KP_"] },
            { abbr: "AV", patterns: ["AVENTURA", "_AV_", "_AV.", "AV_"] },
            { abbr: "WP", patterns: ["WEST_PALM", "WEST PALM", "WESTPALM", "_WP_", "_WP.", "WP_"] },
            { abbr: "CH", patterns: ["CHICAGO", "_CH_", "_CH.", "CH_"] },
            { abbr: "DA", patterns: ["DALLAS", "_DA_", "_DA.", "DA_"] },
            { abbr: "SV", patterns: ["SILICON", "SAN_JOSE", "SAN JOSE", "_SV_", "_SV.", "SV_"] },
            { abbr: "LA", patterns: ["LOS_ANGELES", "LOS ANGELES", "LOSANGELES", "_LA_", "_LA.", "LA_"] },
          ];
          for (const { abbr, patterns } of matched ? [] : storePatterns) {
            if (patterns.some((p) => fn.includes(p))) {
              const store = STORES.find((s) => s.abbr === abbr);
              const items = parseCatalog(wb, store);
              try {                                         // Catapult IDs for Sales + Audit
                const { byStore } = parseCatalogCatapult(wb, [store.abbr]);
                setCatalogIndex((prev) => ({ ...prev, ...byStore }));
              } catch (e) { console.warn("Catapult index failed for " + store.abbr + ":", e); }
              catalogFiles.push({ abbr, items });
              log.push(`✓ ${store.name} catalog: ${items.length} Produce RTL items`);
              matched = true;
              break;
            }
          }
          if (!matched) log.push(`⚠ Could not match catalog: ${file.name}`);
        } else {
          log.push(`⚠ Unknown file: ${file.name}`);
        }

        // Record a ledger entry for whatever this file became.
        if (vendorFiles.length > vBefore) {
          const vf = vendorFiles[vendorFiles.length - 1];
          vf.entry = { id: `in${uploadedAt}-${eid++}`, name: file.name, type: "Vendor list", target: VENDOR_LABELS[vf.vendorKey] || vf.vendorKey, date: fileDate, uploadedAt, rows: null };
          inputEntries.push(vf.entry); // rows filled in Phase 2 after parse()
        } else if (salesFiles.length > sBefore) {
          const sf = salesFiles[salesFiles.length - 1];
          inputEntries.push({ id: `in${uploadedAt}-${eid++}`, name: file.name, type: "Sales export", target: "Sales prices", date: fileDate, uploadedAt, rows: Object.keys(sf.parsed || {}).length });
        } else if (catalogFiles.length > cBefore) {
          const added = catalogFiles.slice(cBefore);
          const stores = added.map((c) => STORES.find((s) => s.abbr === c.abbr)?.name || c.abbr);
          const rows = added.reduce((n, c) => n + (c.items?.length || 0), 0);
          // Catalogs are a roster (no weekly snapshot); group under their date if present else this week.
          inputEntries.push({ id: `in${uploadedAt}-${eid++}`, name: file.name, type: "Catalog", target: stores.join(", "), date: fileDate || thisMonday(), uploadedAt, rows });
        }
      } catch (e) {
        log.push(`✗ Error reading ${file.name}: ${e.message}`);
      }
    }

    // === PHASE 1: Load catalogs and sales prices ===
    let newSalesPriceLookup = { ...salesPriceLookup };
    for (const sf of salesFiles) newSalesPriceLookup = { ...newSalesPriceLookup, ...sf.parsed };
    setSalesPriceLookup(newSalesPriceLookup);

    const catalogsByStore = {};
    for (const cf of catalogFiles) catalogsByStore[cf.abbr] = cf.items;

    // Get catalog items for stores (from this upload or existing state)
    const currentItems = {};
    for (const abbr of ACTIVE_STORES) {
      if (catalogsByStore[abbr]) {
        currentItems[abbr] = catalogsByStore[abbr];
      } else if (storeItems[abbr]?.length > 0) {
        currentItems[abbr] = storeItems[abbr].map((it) => ({
          d365: it.d365, name: it.name, unit: it.saleUnit || it.unit || "EA", vendor: it.vendor, vpn: it.vpn
        }));
      }
    }

    // === PHASE 2: Group vendor files by date and process each date ===
    const dateGroups = {};
    for (const vf of vendorFiles) {
      if (!dateGroups[vf.fileDate]) dateGroups[vf.fileDate] = [];
      dateGroups[vf.fileDate].push(vf);
    }
    // Group sales exports by the date they were received, so a snapshot's price
    // reflects only weeks an export was actually provided (not the cumulative lookup).
    const _now = new Date();
    const _todayStr = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}-${String(_now.getDate()).padStart(2, "0")}`;
    const salesByDate = {};
    for (const sf of salesFiles) {
      const d = sf.fileDate || _todayStr;
      salesByDate[d] = { ...(salesByDate[d] || {}), ...sf.parsed };
    }
    // Snapshot on any date we received vendor files OR a sales export.
    const sortedDates = [...new Set([...Object.keys(dateGroups), ...Object.keys(salesByDate)])].sort();

    let latestVendorData = { ...vendorData };
    let updatedHistory = { ...storeHistory };
    const vendorNames = VENDOR_LABELS;

    for (const date of sortedDates) {
      const filesForDate = dateGroups[date] || [];
      let dateVendorData = { ...latestVendorData };
      // Vendors actually uploaded FOR THIS DATE — this (not the carried-forward set)
      // is what a cost node on this date is allowed to be based on.
      const observedVendorData = {};

      for (const vf of filesForDate) {
        const parsed = vf.parse();
        if (parsed && parsed._multi) {
          dateVendorData = { ...dateVendorData, ...parsed.data };
          Object.assign(observedVendorData, parsed.data);
          const totalCount = Object.values(parsed.data).reduce((s, d) => s + Object.keys(d).length, 0);
          if (vf.entry) vf.entry.rows = totalCount;
          log.push(`✓ CW/Dairyland: ${totalCount} items (${date})`);
        } else {
          dateVendorData = { ...dateVendorData, [vf.vendorKey]: parsed };
          observedVendorData[vf.vendorKey] = parsed;
          if (vf.entry) vf.entry.rows = Object.keys(parsed || {}).length;
          log.push(`✓ ${vendorNames[vf.vendorKey] || vf.vendorKey}: ${Object.keys(parsed).length} items (${date})`);
        }
      }

      // Sales prices received on this date only (empty if no export this date).
      const dateSalesLookup = salesByDate[date] || {};
      const exportReceived = Object.keys(dateSalesLookup).length > 0;

      // Process all stores and snapshot history for this date
      for (const abbr of ACTIVE_STORES) {
        const catItems = currentItems[abbr];
        if (!catItems || catItems.length === 0) continue;
        const store = STORES.find((s) => s.abbr === abbr);
        // Cost comes only from vendors observed this date; price only from a sales
        // export received this date. Anything not observed stays absent (a real gap).
        const processed = processStore(catItems, observedVendorData, dateSalesLookup, store, casepackRules);
        const itemsMap = {};
        let hasData = false;
        for (const it of processed) {
          // Was THIS item's vendor list uploaded this week (even if the item
          // wasn't on it)? Drives the "price list received" confirmation.
          let listReceived = false;
          if (it.vendorKey === "cw") listReceived = !!(observedVendorData[`cw_${it.cwRegion}`] || observedVendorData.cw);
          else if (it.vendorKey) listReceived = !!observedVendorData[it.vendorKey];
          const rec = {};
          if (it.cost != null) rec.cost = it.cost;
          if (it.price != null) rec.price = it.price;
          if (listReceived) rec.listReceived = true;
          if (rec.cost != null || rec.price != null || rec.listReceived) { itemsMap[histKey(it)] = rec; hasData = true; }
        }
        if (hasData || exportReceived) {
          const hist = updatedHistory[abbr] || await loadHistory(abbr);
          updatedHistory[abbr] = mergeSnapshot([...hist], date, itemsMap, exportReceived);
        }
      }
      latestVendorData = dateVendorData;
    }

    // === PHASE 3: Save history and update final state ===
    for (const abbr of ACTIVE_STORES) {
      if (updatedHistory[abbr]?.length > 0) await saveHistory(abbr, updatedHistory[abbr]);
    }
    setStoreHistory(updatedHistory);
    setVendorData(latestVendorData);

    const preserveDiscounts = (newItems, oldItems) => {
      if (!oldItems || oldItems.length === 0) return newItems;
      const carryMap = {};
      for (const old of oldItems) {
        if (old.discount?.active || old.adjPrice != null) {
          carryMap[old.d365] = { discount: old.discount?.active ? old.discount : null, adjPrice: old.adjPrice };
        }
      }
      return newItems.map((it) => {
        const carry = carryMap[it.d365];
        if (!carry) return it;
        return { ...it, ...(carry.discount ? { discount: carry.discount } : {}), ...(carry.adjPrice != null ? { adjPrice: carry.adjPrice } : {}) };
      });
    };

    setStoreItems((prev) => {
      const next = { ...prev };
      // Recompute EVERY store that has items — not just the ones whose catalog was in
      // this upload. A shared vendor list or sales export affects all stores, and Phase 2
      // already recorded it into every store's history; if we only refreshed catalog
      // stores here, the others would keep stale live cost/price while their graph moved
      // on. Use a freshly-uploaded roster where present, otherwise the existing roster,
      // and always recompute against the latest cumulative vendor + sales data.
      for (const abbr of ACTIVE_STORES) {
        let catItems = catalogsByStore[abbr];
        if (!catItems) {
          if (!(prev[abbr]?.length > 0)) continue;
          catItems = prev[abbr].map((it) => ({ d365: it.d365, name: it.name, unit: it.saleUnit || it.unit || "EA", vendor: it.vendor, vpn: it.vpn }));
        }
        const store = STORES.find((s) => s.abbr === abbr);
        next[abbr] = tagNewIn(preserveDiscounts(processStore(catItems, latestVendorData, newSalesPriceLookup, store, casepackRules), prev[abbr]), abbr, updatedHistory);
      }
      return next;
    });

    // Commit the ledger and finish. inputEntries have their row counts filled by now.
    if (inputEntries.length > 0) setInputs((prev) => [...prev, ...inputEntries]);
    setUploadLog(log);
    setProcessing(false);
    const okCount = inputEntries.length;
    const parts = [];
    if (okCount > 0) parts.push(`${okCount} file${okCount !== 1 ? "s" : ""} processed`);
    if (skipped.length > 0) parts.push(`${skipped.length} skipped (no date): ${skipped.join(", ")}`);
    showNotification(parts.length ? parts.join(" · ") : "No files recognized", skipped.length ? "warn" : "info");
  }, [vendorData, salesPriceLookup, storeItems, storeHistory, casepackRules, showNotification]);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setDragTarget(null);
    const files = Array.from(e.dataTransfer?.files || []).filter((f) => /\.(xlsx|xls|csv)$/i.test(f.name));
    if (files.length > 0) handleFiles(files);
  }, [handleFiles]);

  // ── Display logic ──
  const displayItems = useMemo(() => {
    let filtered = items;
    if (filter === "red") filtered = items.filter((i) => i.cost != null && i.margin != null && i.margin < 25);
    else if (filter === "yellow") filtered = items.filter((i) => i.margin != null && i.margin > 55);
    else if (filter === "safe") filtered = items.filter((i) => i.margin != null && i.margin >= 25 && i.margin <= 55);
    else if (filter === "errors") filtered = items.filter((i) => i.error);
    else if (filter === "disc") filtered = items.filter((i) => i.discount?.active === true);
    else if (filter === "unavail") filtered = items.filter((i) => i.error && (i.error.includes("not found in vendor") || i.error.includes("VPN is blank") || i.error.includes("Vendor file not loaded") || i.error.includes("Vendor price blank")));
    else if (filter === "newin") filtered = items.filter((i) => i.newIn === true);
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter((i) => i.name.toLowerCase().includes(q) || i.d365.includes(q) || i.vendor.toLowerCase().includes(q) || (i.error && i.error.toLowerCase().includes(q)));
    }
    return [...filtered].sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case "name": va = a.name; vb = b.name; break;
        case "margin": va = a.margin ?? -999; vb = b.margin ?? -999; break;
        case "price": va = a.price ?? -999; vb = b.price ?? -999; break;
        case "cost": va = a.cost ?? -999; vb = b.cost ?? -999; break;
        case "vendor": va = a.vendor; vb = b.vendor; break;
        case "error": va = a.error || ""; vb = b.error || ""; break;
        case "gp": va = a.gpDollars ?? -Infinity; vb = b.gpDollars ?? -Infinity; break;
        case "newgp": va = a.newGpDollars ?? -Infinity; vb = b.newGpDollars ?? -Infinity; break;
        case "gpdelta": va = a.gpDelta ?? -Infinity; vb = b.gpDelta ?? -Infinity; break;
        case "gappct": va = a.gapPct ?? -Infinity; vb = b.gapPct ?? -Infinity; break;
        default: va = a.d365; vb = b.d365;
      }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [items, filter, search, sortCol, sortDir]);

  const handleSort = (col) => { if (sortCol === col) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("asc"); } };
  const toggleSelect = (id) => setSelectedItems((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelectedItems(selectedItems.size === displayItems.length ? new Set() : new Set(displayItems.map((i) => i.id)));

  const handleCellEdit = useCallback((itemId, field, value) => {
    // Market price is held outside storeItems so it survives catalog uploads,
    // recomputes, and an item dropping off the catalog for a week.
    if (field === "marketPrice") {
      const row = (storeItems[activeStore] || []).find((i) => i.id === itemId);
      const d = String(row?.d365 ?? "").trim();
      if (!d) return;
      // Stamped with today's Monday so the next report can supersede it, and
      // stored as {value, at} — a cleared cell keeps an entry with value null,
      // because "I deliberately blanked this" is a real state that must survive
      // until a report value arrives, not an absence.
      setMarketPrices((prev) => {
        const store = { ...(prev[activeStore] || {}) };
        store[d] = { value: value == null ? null : value, at: mondayOf(new Date()) };
        return { ...prev, [activeStore]: store };
      });
      return;
    }
    setStoreItems((prev) => ({
      ...prev,
      [activeStore]: prev[activeStore].map((item) => {
        if (item.id !== itemId) return item;
        // "New Price / LB" is a second view of adjPrice, not its own value:
        // writing it back-calculates the per-unit price. Last edit wins, so
        // typing in either cell overwrites whatever the other one showed.
        if (field === "adjPriceLb") {
          return { ...item, adjPrice: value == null ? null : fromLb(value) };
        }
        const updated = { ...item, [field]: value };
        if (field === "cost" || field === "price") {
          const cost = parseFloat(field === "cost" ? value : item.cost) || 0;
          const price = parseFloat(field === "price" ? value : item.price) || 0;
          updated.cost = cost; updated.price = price;
          updated.margin = price > 0 ? Math.round(((price - cost) / price) * 1000) / 10 : 0;
          updated.newPrice = cost > 0 ? roundTo09(cost / 0.70) : null;
          updated.newMargin = calcMarginPct(updated.newPrice, cost);
        }
        return updated;
      }),
    }));
  }, [activeStore, storeItems]);

  const handleDiscountToggle = useCallback((itemId) => {
    setStoreItems((prev) => ({
      ...prev,
      [activeStore]: prev[activeStore].map((item) => {
        if (item.id !== itemId) return item;
        if (item.discount?.active) {
          // Turning off discount — clear it
          return { ...item, discount: null };
        } else {
          // Turning on discount — default to current price
          return { ...item, discount: { active: true, discountPrice: item.price ?? 0 } };
        }
      }),
    }));
  }, [activeStore]);

  const handleDiscountPrice = useCallback((itemId, newDiscountPrice) => {
    const dp = parseFloat(newDiscountPrice);
    if (isNaN(dp)) return;
    setStoreItems((prev) => ({
      ...prev,
      [activeStore]: prev[activeStore].map((item) => {
        if (item.id !== itemId) return item;
        return { ...item, discount: { active: true, discountPrice: dp } };
      }),
    }));
  }, [activeStore]);

  const handleAddItem = (form) => {
    const price = parseFloat(form.price) || 0; const cost = parseFloat(form.cost) || 0;
    const margin = price > 0 ? Math.round(((price - cost) / price) * 1000) / 10 : 0;
    setStoreItems((prev) => ({ ...prev, [activeStore]: [...prev[activeStore], { ...form, id: `${activeStore}-${form.d365}-new-${Date.now()}`, cost, price, margin, costUnit: form.unit || "EA", saleUnit: form.unit || "EA", newPrice: roundTo09(cost / 0.70), newMargin: calcMarginPct(roundTo09(cost / 0.70), cost), casepack: "", error: "", weeks: [margin, margin, margin, margin] }] }));
    showNotification(`Added ${form.name} to ${activeStore}`);
  };

  const handleDeleteSelected = () => {
    setStoreItems((prev) => ({ ...prev, [activeStore]: prev[activeStore].filter((i) => !selectedItems.has(i.id)) }));
    showNotification(`Removed ${selectedItems.size} item(s)`);
    setSelectedItems(new Set());
  };

  const handleExportExcel = useCallback(() => {
    const toExport = selectedItems.size > 0 ? displayItems.filter((i) => selectedItems.has(i.id)) : displayItems;
    if (toExport.length === 0) return;
    const storeName = STORES.find((s) => s.abbr === activeStore)?.name || activeStore;
    const rows = toExport.map((item) => {
      const isDisc = item.discount?.active === true;
      const discPrice = isDisc ? item.discount.discountPrice : null;
      const discMargin = isDisc && discPrice != null && item.cost != null ? calcMarginPct(discPrice, item.cost) : null;
      return {
        "D365 ID": item.d365, "Product Name": item.name, "Vendor": item.vendor, "VPN": item.vpn,
        "Casepack": item.casepack, "Cost Unit": item.costUnit, "Unit Cost": item.cost,
        "Sale Unit": item.saleUnit, "Current Price": item.price, "Current Margin %": item.margin,
        "New Retail (30%)": item.newPrice, "New Margin %": item.newMargin,
        ...(isCanDual(activeStore) ? { "Price / LB": isKgItem(item) && item.price != null ? toLb(item.price) : null } : {}),
        "New Price": item.adjPrice, "Adj Margin %": item.adjPrice != null && item.cost != null && item.adjPrice > 0 ? Math.round(((item.adjPrice - item.cost) / item.adjPrice) * 1000) / 10 : null,
        ...(isCanDual(activeStore) ? { "New Price / LB": isKgItem(item) && item.adjPrice != null ? toLb(item.adjPrice) : null } : {}),
        "Market Price": item.marketPrice,
        "Discount": isDisc ? "Y" : "", "Disc Price": discPrice, "Disc Margin %": discMargin != null ? Math.round(discMargin * 10) / 10 : null,
        "Error": item.error,
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 12 }, { wch: 36 }, { wch: 18 }, { wch: 12 }, { wch: 18 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 40 }];
    const wb2 = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb2, ws, `${activeStore} - ${storeName}`.slice(0, 31));
    const d = new Date(); const ds = `${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}${String(d.getFullYear()).slice(-2)}`;
    XLSX.writeFile(wb2, `Pricing_${selectedItems.size > 0 ? "Export" : "Full"}_${activeStore}_${ds}.xlsx`);
    showNotification(`Exported ${toExport.length} items`, "success");
  }, [displayItems, selectedItems, activeStore, showNotification]);

  // ── Sales / Audit export ─────────────────────────────────
  // Both tabs hand back the rows currently on screen, already narrowed to the
  // selection when there is one. Columns mirror what's visible — no weekly
  // history — so what lands in Excel is what you were looking at.
  const handleExportSales = useCallback((rows, weeks) => {
    if (!rows || !rows.length) { showNotification("Nothing to export", "warn"); return; }
    const wk = Array.isArray(weeks) && weeks.length ? weeks[weeks.length - 1] : null;
    const out = rows.map((r) => {
      const captured = r.qtyCur && r.amtCur != null && r.qtyCur !== 0 ? r.amtCur / r.qtyCur : null;
      const gap = priceGapPct({ qty: r.qtyCur, amt: r.amtCur, listPrice: r.listPrice, placeholder: r.placeholder });
      return {
        "Store": r.store, "Status": SALES_STATUS_LABEL[r.status] || r.status || "", "Catapult ID": r.pid, "D365 ID": r.d365 || "",
        "Product": r.name, "Subcategory": r.sub || "",
        "Qty": r.qtyCur ?? null, "Qty prior": r.qtyPrev ?? null,
        "Net amount": r.amtCur ?? null, "Net prior": r.amtPrev ?? null,
        "Captured price": captured != null ? Math.round(captured * 100) / 100 : null,
        "List price": r.placeholder ? null : (r.listPrice ?? null),
        "Gap %": r.placeholder || gap == null ? null : Math.round(gap * 10) / 10,
      };
    });
    const ws = XLSX.utils.json_to_sheet(out);
    ws["!cols"] = [{ wch: 7 }, { wch: 14 }, { wch: 13 }, { wch: 12 }, { wch: 38 }, { wch: 20 },
      { wch: 10 }, { wch: 10 }, { wch: 13 }, { wch: 13 }, { wch: 14 }, { wch: 12 }, { wch: 9 }];
    const wb2 = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb2, ws, `Sales ${activeStore}`.slice(0, 31));
    XLSX.writeFile(wb2, `Sales_${activeStore}_${wk || todayStamp()}.xlsx`);
    showNotification(`Exported ${out.length} rows`, "success");
  }, [activeStore, showNotification]);

  const handleExportAudit = useCallback((rows, bucket) => {
    if (!rows || !rows.length) { showNotification("Nothing to export", "warn"); return; }
    const notSold = bucket === "notSold";
    const out = rows.map((r) => ({
      "Store": r.store, "Bucket": AUDIT_BUCKET_LABEL[bucket] || bucket,
      "Catapult ID": r.pid || "", "D365 ID": r.d365 || "",
      "Product": r.name, ...(r.catalogName ? { "Catalog name": r.catalogName } : { "Catalog name": "" }),
      ...(notSold ? {} : { "Subcategory": r.sub || "", "Qty": r.qty ?? null, "Net amount": r.amt ?? null }),
      "List price": r.placeholder ? null : (r.listPrice ?? null),
      ...(notSold ? { "Catalog status": r.catalogStatus || "" } : {}),
    }));
    const ws = XLSX.utils.json_to_sheet(out);
    ws["!cols"] = [{ wch: 7 }, { wch: 22 }, { wch: 13 }, { wch: 12 }, { wch: 38 }, { wch: 30 },
      { wch: 20 }, { wch: 10 }, { wch: 13 }, { wch: 12 }, { wch: 16 }];
    const wb2 = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb2, ws, `Audit ${activeStore}`.slice(0, 31));
    XLSX.writeFile(wb2, `Audit_${bucket}_${activeStore}_${todayStamp()}.xlsx`);
    showNotification(`Exported ${out.length} rows`, "success");
  }, [activeStore, showNotification]);

  // ── Weighted department margin ───────────────────────────
  // A straight average of item margins treats two bunches of herbs the same as
  // 400 lb of bananas. What the department is actually running at is
  //     (revenue − cost) / revenue   summed across the department,
  // which is the number worth holding against the 30% target.
  //
  // Every Monday brings: this week's vendor cost (holds for the next 7 days),
  // current retail prices (hold until changed), and last week's sales. So the
  // only honest forward estimate uses LAST week's quantities as the proxy for
  // next week's. That makes two figures:
  //
  //   actual — cost, price and quantity all from last week. Nothing estimated.
  //   new    — this week's cost and price (New Price where typed) against last
  //            week's quantities. An estimate, and the quantity is the estimated
  //            part.
  //
  // Both run over ONE eligible basket: items with quantity last week and enough
  // data on both sides. Computing each side over whatever it could would make
  // the ppt move and the dollar move describe different populations, and they
  // would not reconcile when someone checked.
  const weightedMargin = useCallback((rows) => {
    let revA = 0, costA = 0, revN = 0, costN = 0, n = 0;
    for (const it of rows) {
      const q = it.qtySold;
      if (!q) continue;                                   // no units, or unmapped
      const priceA = it.gpPrice, costPerA = it.gpCost;    // observed last week
      const priceN = it.adjPrice != null ? it.adjPrice : it.price;
      const costPerN = it.cost;
      if (priceA == null || costPerA == null || priceN == null || costPerN == null) continue;
      revA += priceA * q; costA += costPerA * q;
      revN += priceN * q; costN += costPerN * q;
      n++;
    }
    const mA = revA > 0 ? ((revA - costA) / revA) * 100 : null;
    const mN = revN > 0 ? ((revN - costN) / revN) * 100 : null;
    return {
      count: n,
      actual: mA, next: mN,
      profit: n > 0 ? revA - costA : null,          // what the basket earned last week
      ppt: mA != null && mN != null ? mN - mA : null,
      profitDelta: n > 0 ? (revN - costN) - (revA - costA) : null,
    };
  }, []);

  const stats = useMemo(() => {
    const errors = items.filter((i) => i.error).length;
    return { total: items.length, errors, store: weightedMargin(items) };
  }, [items, weightedMargin]);

  // Weighted margin and profit for every week we can compute, from data already
  // stored: units from that week's sales, price and cost from that week's
  // snapshot (carried forward per field when a week was skipped, same rule the
  // GP columns use). Each historical week uses ITS OWN units — unlike the
  // forward estimate, which has to borrow last week's.
  //
  // The eligible basket is recomputed per week, so it moves when data
  // completeness moves. A week missing a vendor file has fewer items and a
  // dented profit figure for reasons that are nothing to do with pricing, which
  // is why the readout carries the item count: a count that falls off a cliff
  // explains a margin that does the same.
  const marginSeries = useCallback((restrictTo) => {
    const hist = storeHistory[activeStore] || [];
    const weeks = Object.keys(salesWeeks[activeStore] || {}).sort();
    if (!weeks.length || !hist.length) return [];
    const snapsAsc = [...hist].sort((a, b) => (a.date < b.date ? -1 : 1));
    const cat = catalogIndex[activeStore] || [];
    const byCid = {}, byD365 = {};
    for (const c of cat) {
      const cid = String(c.cid ?? "").trim(), d = String(c.d365 ?? "").trim();
      if (cid && d) (byCid[cid] = byCid[cid] || []).push(d);
      if (d) (byD365[d] = byD365[d] || []).push(d);
    }
    const rowsByD365 = {};
    for (const it of rawItems) {
      const d = String(it.d365 ?? "").trim();
      if (d) (rowsByD365[d] = rowsByD365[d] || []).push(it);
    }
    const allow = restrictTo ? new Set(restrictTo) : null;

    return weeks.map((wk) => {
      // units for this week, matched the same two-pass way as everywhere else
      const sales = (salesWeeks[activeStore] || {})[wk] || {};
      const qty = {};
      for (const [pid, rec] of Object.entries(sales)) {
        const key = String(pid ?? "").trim();
        const targets = byCid[key] || byD365[key];
        if (!targets) continue;
        const q = Number(rec?.qty);
        if (!isFinite(q)) continue;
        for (const d of targets) qty[d] = (qty[d] || 0) + q;
      }
      // snapshots at or before this week, newest first
      const prior = snapsAsc.filter((h) => h.date <= wk).reverse();
      let rev = 0, cost = 0, n = 0;
      for (const [d, q] of Object.entries(qty)) {
        if (!q) continue;
        if (allow && !allow.has(d)) continue;
        const rows = rowsByD365[d];
        if (!rows || !rows.length) continue;
        let p = null, c = null;
        for (const snap of prior) {
          if (p != null && c != null) break;
          for (const it of rows) {
            const rec = readHistRec(snap, it);
            if (!rec) continue;
            if (c == null && rec.cost != null) c = rec.cost;
            if (p == null && rec.price != null) p = rec.price;
          }
        }
        if (p == null || c == null) continue;
        rev += p * q; cost += c * q; n++;
      }
      return { week: wk, margin: rev > 0 ? ((rev - cost) / rev) * 100 : null,
        profit: n > 0 ? rev - cost : null, count: n };
    });
  }, [storeHistory, salesWeeks, catalogIndex, activeStore, rawItems]);

  // The filtered figure answers "is this slice fixed"; the store figure answers
  // "did it move the number we are judged on". Raising 20 items in the <25%
  // filter swings the slice hugely and the department barely — seeing only one
  // of those either flatters the work or hides it. Shown only when something is
  // actually narrowing the view, so its presence means "this is a subset".
  const isFiltered = filter !== "all" || !!search;
  const filterStats = useMemo(
    () => (isFiltered ? weightedMargin(displayItems) : null),
    [isFiltered, displayItems, weightedMargin]);

  const SortArrow = ({ col }) => { if (sortCol !== col) return <span style={{ opacity: 0.25, fontSize: 10 }}> ↕</span>; return <span style={{ fontSize: 10 }}> {sortDir === "asc" ? "↑" : "↓"}</span>; };
  const hasSelection = selectedItems.size > 0;

  return (
    <div style={{
      "--bg-primary": "#0f1117", "--bg-secondary": "#161820", "--bg-tertiary": "#1c1f2b", "--bg-row-hover": "#1a1d28",
      "--text-primary": "#e8eaf0", "--text-secondary": "#8b90a0", "--text-tertiary": "#585d6e",
      "--border": "#2a2d3a", "--border-light": "#222536", "--accent": "#4a7cff", "--accent-dim": "rgba(74,124,255,0.1)",
      "--red-bg": "rgba(224,82,82,0.08)", "--yellow-bg": "rgba(212,160,23,0.08)",
      "--safe-zone": "#3a9a5c", "--bar-track": "#1e2130", "--week-dot": "#6b8aad",
      "--export-green": "#22c55e", "--export-green-dim": "rgba(34,197,94,0.12)",
      fontFamily: "'DM Sans', 'SF Pro Display', -apple-system, sans-serif", color: "var(--text-primary)",
      height: "100vh", background: "var(--bg-primary)", position: "relative", display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />

      {notification && <div style={{ position: "fixed", top: 20, right: 20, zIndex: 2000, padding: "12px 20px", borderRadius: 8, background: notification.type === "success" ? "#1a3a2a" : "#1a2a3a", border: `1px solid ${notification.type === "success" ? "#2a5a3a" : "#2a3a5a"}`, color: "#e8eaf0", fontSize: 13, fontWeight: 500, boxShadow: "0 8px 30px rgba(0,0,0,0.4)", animation: "slideIn 0.3s ease-out", maxWidth: 500 }}>{notification.msg}</div>}

      {/* Header + Upload */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragTarget("main"); }}
        onDragLeave={(e) => { if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) setDragTarget(null); }}
        onDrop={handleDrop}
        style={{ padding: "20px 28px 0", borderBottom: "1px solid var(--border)", background: dragTarget === "main" ? "rgba(74,124,255,0.03)" : "linear-gradient(180deg, #12141c 0%, var(--bg-primary) 100%)", transition: "background 0.2s", flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, background: "linear-gradient(135deg, #e8eaf0 0%, #8b90a0 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Produce Pricing</h1>
              <span style={{ fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 4, background: "var(--accent-dim)", color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.8px" }}>Dashboard</span>
              {processing && <span style={{ fontSize: 11, color: "var(--accent)", animation: "pulse 1s infinite" }}>Processing...</span>}
            </div>
            <p style={{ fontSize: 12, color: "var(--text-tertiary)", margin: 0 }}>Drop catalog, vendor, or sales price files anywhere · All 9 US stores</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => fileInputRef.current?.click()}
              style={{ padding: "8px 18px", borderRadius: 6, border: "1.5px dashed var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text-secondary)"; }}>
              <span style={{ fontSize: 16 }}>↑</span> Upload Files
            </button>
            {inputs.length > 0 && (
              <button onClick={() => setShowInputsPanel(true)}
                style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 11, cursor: "pointer" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text-secondary)"; }}>
                Inputs ({inputs.length})
              </button>
            )}
            <button onClick={handleClearAllData}
              style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #2a2d3a", background: "transparent", color: "var(--text-tertiary)", fontSize: 11, cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#e05252"; e.currentTarget.style.color = "#e05252"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#2a2d3a"; e.currentTarget.style.color = "var(--text-tertiary)"; }}>
              Clear Data
            </button>
          </div>
        </div>
        <input ref={fileInputRef} type="file" multiple accept=".xlsx,.xls,.csv" style={{ display: "none" }}
          onChange={(e) => { if (e.target.files.length > 0) handleFiles(Array.from(e.target.files)); e.target.value = ""; }} />

        {/* App tabs. Below the upload row on purpose: the single drop zone and
            Inputs panel above continue to serve all three views. */}
        <div style={{ display: "flex", gap: 2, marginTop: 12, borderBottom: "1px solid var(--border)" }}>
          {[["pricing", "Pricing", "#22c55e"], ["sales", "Sales", "#4a7cff"], ["audit", "Audit", "#a78bfa"]].map(([id, label, col]) => (
            <div key={id} onClick={() => setActiveTab(id)}
              style={{ padding: "9px 18px", fontSize: 13, cursor: "pointer",
                color: activeTab === id ? "var(--text-primary)" : "var(--text-secondary)",
                fontWeight: activeTab === id ? 700 : 400,
                borderBottom: `2px solid ${activeTab === id ? col : "transparent"}` }}>
              {label}
            </div>
          ))}
        </div>

        {/* Store Tabs */}
        <div style={{ display: "flex", gap: 2, marginTop: 4 }}>
          {STORES.map((store) => {
            const isActive = activeStore === store.abbr;
            const isWired = ACTIVE_STORES.includes(store.abbr);
            const storeArr = storeItems[store.abbr] || [];
            const redCount = storeArr.filter((i) => i.margin != null && i.margin < 25 && i.cost != null).length;
            const itemCount = storeArr.length;
            return (
              <button key={store.abbr} onClick={() => { setActiveStore(store.abbr); setSelectedItems(new Set()); }}
                style={{
                  padding: "10px 14px", borderRadius: "8px 8px 0 0", border: "none",
                  background: isActive ? "var(--bg-tertiary)" : "transparent",
                  color: isActive ? "var(--text-primary)" : isWired ? "var(--text-secondary)" : "var(--text-tertiary)",
                  fontSize: 13, fontWeight: isActive ? 600 : 400, cursor: isWired ? "pointer" : "default", transition: "all 0.15s",
                  borderBottom: isActive ? "2px solid var(--accent)" : "2px solid transparent",
                  display: "flex", alignItems: "center", gap: 6, opacity: isWired ? 1 : 0.4,
                }}>
                {store.abbr}
                {itemCount > 0 && <span style={{ fontSize: 9, color: "var(--text-tertiary)" }}>({itemCount})</span>}
                {redCount > 0 && <span style={{ fontSize: 9, fontWeight: 700, background: "#e05252", color: "#fff", borderRadius: 10, padding: "1px 5px" }}>{redCount}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === "sales" && (
        <SalesTab salesWeeks={salesWeeks} catalogIndex={catalogIndex}
                  listPrices={listPrices} store={activeStore} onExport={handleExportSales} />
      )}
      {activeTab === "audit" && (
        <AuditTab salesWeeks={salesWeeks} catalogIndex={catalogIndex}
                  listPrices={listPrices} store={activeStore} onExport={handleExportAudit} />
      )}

      {/* ── Pricing view ─────────────────────────────────────────────── */}
      <div style={{ display: activeTab === "pricing" ? "contents" : "none" }}>
      {/* Stats bar */}
      <div style={{ padding: "14px 28px", background: "var(--bg-tertiary)", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <div>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{STORES.find((s) => s.abbr === activeStore)?.name}</span>
          <span style={{ fontSize: 12, color: "var(--text-tertiary)", marginLeft: 10 }}>{STORES.find((s) => s.abbr === activeStore)?.location}</span>
        </div>
        <div onClick={() => setShowMarginTrend(true)} title="Open margin and profit history"
          style={{ cursor: "pointer", borderRadius: 10, padding: "6px 12px 7px", border: "1px solid transparent", position: "relative", transition: "background .15s, border-color .15s" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#191c25"; e.currentTarget.style.borderColor = "#585d6e"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}>
          <div style={{ display: "grid", gridTemplateColumns: "58px 92px 92px 96px 70px 92px", columnGap: 12, fontSize: 9.5,
            color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: ".5px", paddingBottom: 3 }}>
            <span></span><span style={{ textAlign: "right" }}>Margin</span><span style={{ textAlign: "right" }}>New</span>
            <span style={{ textAlign: "right" }}>Profit</span>
            <span style={{ textAlign: "right" }}>Δ ppt</span>
            <span style={{ textAlign: "right" }}>Δ Profit<span style={{ marginLeft: 7, color: "var(--accent)" }}>trend ›</span></span>
          </div>
          <MarginRow label="Store" s={stats.store} week={lastSalesWeek} />
          {filterStats && <MarginRow label="Filter" s={filterStats} week={lastSalesWeek} dim />}
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ padding: "12px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--border-light)", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {[{ key: "all", label: "All Items" }, { key: "red", label: "< 25%" }, { key: "yellow", label: "> 55%" }, { key: "safe", label: "25–55%" }, { key: "errors", label: "Errors" }, { key: "unavail", label: "Unavailable" }, { key: "newin", label: "New In" }, { key: "disc", label: "Discounted" }].map(({ key, label }) => (
            <button key={key} onClick={() => setFilter(key)}
              style={{ padding: "5px 14px", borderRadius: 20, border: `1px solid ${filter === key ? (key === "disc" ? "#9333ea" : key === "newin" ? "#22c55e" : "var(--accent)") : "var(--border)"}`, background: filter === key ? (key === "disc" ? "rgba(147,51,234,0.15)" : key === "newin" ? "rgba(34,197,94,0.12)" : "var(--accent-dim)") : "transparent", color: filter === key ? (key === "disc" ? "#c084fc" : key === "newin" ? "#4ade80" : "var(--accent)") : "var(--text-secondary)", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>{label}{key === "newin" && items.filter((i) => i.newIn).length > 0 ? ` (${items.filter((i) => i.newIn).length})` : ""}</button>
          ))}
          <div style={{ width: 1, height: 20, background: "var(--border)", margin: "0 6px" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items..."
            style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 12, outline: "none", width: 180 }} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {(hasSelection || items.length > 0) && (
            <button onClick={handleExportExcel}
              style={{ padding: "6px 16px", borderRadius: 6, border: `1px solid ${hasSelection ? "var(--export-green)" : "var(--border)"}`, background: hasSelection ? "var(--export-green-dim)" : "transparent", color: hasSelection ? "var(--export-green)" : "var(--text-secondary)", fontSize: 12, fontWeight: hasSelection ? 600 : 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              {hasSelection ? `Export ${selectedItems.size}` : "Export All"}
            </button>
          )}
          {hasSelection && <button onClick={handleDeleteSelected} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #e05252", background: "rgba(224,82,82,0.1)", color: "#e05252", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Remove {selectedItems.size}</button>}
          <button onClick={() => setShowAddModal(true)} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "var(--accent)", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Add Item</button>
          <button onClick={() => setShowRulesPanel(true)}
            style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #f59e0b", background: "rgba(245,158,11,0.1)", color: "#f59e0b", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
            ⚙ Rules{casepackRules.length > 0 ? ` (${casepackRules.length})` : ""}
          </button>
          {(storeItems[activeStore] || []).some((i) => i.adjPrice != null) && (
            <button onClick={handleClearNewPrices}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #38bdf8", background: "rgba(56,189,248,0.08)", color: "#38bdf8", fontSize: 12, fontWeight: 500, cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#e05252"; e.currentTarget.style.color = "#e05252"; e.currentTarget.style.background = "rgba(224,82,82,0.08)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#38bdf8"; e.currentTarget.style.color = "#38bdf8"; e.currentTarget.style.background = "rgba(56,189,248,0.08)"; }}>
              ✕ Clear New Prices
            </button>
          )}
          {(storeHistory[activeStore] || []).length > 0 && (
            <button onClick={handleRebuildHistory}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 12, fontWeight: 500, cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#e05252"; e.currentTarget.style.color = "#e05252"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text-secondary)"; }}
              title="Clear this store's weekly history so it can be rebuilt by re-uploading dated files">
              ↻ Rebuild History
            </button>
          )}
        </div>
      </div>

      {/* Table or empty state */}
      <div style={{ padding: "0 28px 28px", overflow: "auto", flex: 1, minHeight: 0 }}>
        {loadFailed ? (
          // An empty screen caused by a failed load must never be mistaken for
          // an empty database — re-uploading on top of it is how good data gets
          // replaced. Say what happened and what to do.
          <div style={{ maxWidth: 620, margin: "70px auto", padding: "22px 26px", borderRadius: 12,
            background: "var(--red-bg, rgba(224,82,82,0.08))", border: "1px solid #e0525255" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#e05252", marginBottom: 10 }}>
              Couldn't load your saved data
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text-secondary)", marginBottom: 14 }}>
              The database didn't respond, so nothing is shown. <strong>Your saved data is still there</strong> —
              this screen is empty because the read failed, not because the data is gone. Saving is
              switched off until the connection is back, so nothing here can overwrite it.
              <br /><br />
              A free-tier database pauses after about a week idle and takes a few seconds to wake.
              Reload and it usually comes straight back.
            </div>
            <button onClick={() => window.location.reload()}
              style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid #e0525255", background: "#e0525218",
                color: "#e05252", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Reload
            </button>
            <div style={{ marginTop: 12, fontFamily: "'DM Mono', monospace", fontSize: 10.5, color: "var(--text-tertiary)" }}>
              {String(loadFailed)}
            </div>
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "var(--text-tertiary)" }}>
            <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>↓</div>
            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8, color: "var(--text-secondary)" }}>Drop files to get started</div>
            <div style={{ fontSize: 13, lineHeight: 1.8 }}>
              Upload store catalogs (D365_Catalog_*.xlsx),<br />
              Baldor price list (BALDOR*.xlsx),<br />
              CW/Dairyland (CW.xlsx), and<br />
              Sales price export (Sales_Price_Export*.xlsx)
            </div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 13, marginTop: 8 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 36 }}><input type="checkbox" checked={selectedItems.size === displayItems.length && displayItems.length > 0} onChange={toggleSelectAll} style={{ accentColor: "var(--accent)" }} /></th>
                <th style={thStyle} />
                <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort("d365")}>D365 ID<SortArrow col="d365" /></th>
                <th style={{ ...thStyle, cursor: "pointer", textAlign: "left" }} onClick={() => handleSort("name")}>Product Name<SortArrow col="name" /></th>
                <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort("vendor")}>Vendor<SortArrow col="vendor" /></th>
                <th style={thStyle}>VPN</th>
                <th style={thStyle}>Casepack</th>
                <th style={thStyle}>Cost Unit</th>
                <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort("cost")}>Cost<SortArrow col="cost" /></th>
                <th style={thStyle}>Sale Unit</th>
                <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort("price")}>
                  {isCanDual(activeStore) ? "Price / Unit" : "Price"}<SortArrow col="price" /></th>
                {isCanDual(activeStore) && (
                  <th style={{ ...thStyle, color: "#9ca3af" }} title="Price per unit ÷ 2.20462 · KG items only">Price / LB</th>
                )}
                <th style={{ ...thStyle, cursor: "pointer", minWidth: 78 }} onClick={() => handleSort("margin")}>Margin<SortArrow col="margin" /></th>
                <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort("gp")}
                    title={lastSalesWeek ? `What this earned in the week of ${lastSalesWeek}: (price − cost) × units sold, all as they stood that week` : "No sales data loaded"}>
                  GP $<SortArrow col="gp" />
                  <div style={{ fontSize: 9, fontWeight: 400, color: "var(--text-tertiary)", letterSpacing: 0 }}>
                    {lastSalesWeek ? `wk ${lastSalesWeek.slice(5)}` : "no sales data"}</div>
                </th>
                <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort("newgp")}
                    title="(Current price − current cost) × units sold last week. Switches to the New Price once you enter one.">
                  New GP $<SortArrow col="newgp" />
                  <div style={{ fontSize: 9, fontWeight: 400, color: "var(--text-tertiary)", letterSpacing: 0 }}>at current cost</div>
                </th>
                <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort("gpdelta")}
                    title="New GP $ minus GP $. Without a New Price this is the effect of the cost change alone.">
                  Δ<SortArrow col="gpdelta" />
                  <div style={{ fontSize: 9, fontWeight: 400, color: "var(--text-tertiary)", letterSpacing: 0 }}>new vs last wk</div>
                </th>
                <th style={thStyle}>30% Price</th>
                <th style={{ ...thStyle, background: "#1a1d20", borderBottom: "2px solid #38bdf8", color: "#7dd3fc" }}>
                  {isCanDual(activeStore) ? "New Price / Unit" : "New Price"}</th>
                <th style={{ ...thStyle, background: "#1a1d20", borderBottom: "2px solid #38bdf8", color: "#7dd3fc" }}>Adj Margin</th>
                {isCanDual(activeStore) && (
                  <th style={{ ...thStyle, background: "#1a1d20", borderBottom: "2px solid #38bdf8", color: "#7dd3fc" }}
                      title="Edits either way: typing here rewrites New Price / Unit">New Price / LB</th>
                )}
                <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort("gappct")}
                    title="(Our price − market price) ÷ market price. Positive means we are above Whole Foods.">
                  Gap<SortArrow col="gappct" />
                  <div style={{ fontSize: 9, fontWeight: 400, color: "var(--text-tertiary)", letterSpacing: 0 }}>vs market</div>
                </th>
                <th style={{ ...thStyle, background: "#1a1a20", borderBottom: "2px solid #22c55e", color: "#4ade80" }}>
                  Mkt Price
                  {latestCompWeek && <div style={{ fontSize: 9, fontWeight: 400, color: "var(--text-tertiary)", letterSpacing: 0 }}>wk {latestCompWeek.slice(5)}</div>}
                </th>
                <th style={{ ...thStyle, background: "#1a1520", borderBottom: "2px solid #9333ea", color: "#c084fc" }}>Disc</th>
                <th style={{ ...thStyle, background: "#1a1520", borderBottom: "2px solid #9333ea", color: "#c084fc" }}>Disc Price</th>
                <th style={{ ...thStyle, background: "#1a1520", borderBottom: "2px solid #9333ea", color: "#c084fc", minWidth: 130 }}>Disc Margin</th>
                <th style={{ ...thStyle, cursor: "pointer", textAlign: "left", maxWidth: 200 }} onClick={() => handleSort("error")}>Error<SortArrow col="error" /></th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map((item) => {
                const isSelected = selectedItems.has(item.id);
                const isDisc = item.discount?.active === true;
                const discPrice = item.discount?.discountPrice ?? null;
                const discMargin = isDisc && discPrice != null && item.cost != null ? calcMarginPct(discPrice, item.cost) : null;
                const rowBg = isDisc ? "rgba(147,51,234,0.06)" : (item.margin != null && item.cost != null && item.margin < 25) ? "var(--red-bg)" : (item.margin != null && item.margin > 55) ? "var(--yellow-bg)" : "transparent";
                return (
                  <tr key={item.id} style={{ background: isSelected ? "var(--accent-dim)" : rowBg, transition: "background 0.1s" }}
                    onMouseEnter={(e) => { if (!isSelected && rowBg === "transparent") e.currentTarget.style.background = "var(--bg-row-hover)"; }}
                    onMouseLeave={(e) => { if (!isSelected && rowBg === "transparent") e.currentTarget.style.background = "transparent"; }}>
                    <td style={tdStyle}><input type="checkbox" checked={isSelected} onChange={() => toggleSelect(item.id)} style={{ accentColor: "var(--accent)" }} /></td>
                    <td style={{ ...tdStyle, width: 16 }}><StatusDot margin={item.cost != null ? item.margin : null} /></td>
                    <td style={{ ...tdStyle, fontFamily: "'DM Mono', monospace", fontSize: 12, color: "var(--text-secondary)" }}><EditableCell value={item.d365} field="d365" itemId={item.id} onSave={handleCellEdit} mono /></td>
                    <td style={{ ...tdStyle, fontWeight: 500, textAlign: "left", maxWidth: 280, cursor: "pointer" }}
                      onClick={() => setDetailItem(item)}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#4a7cff"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = ""; }}>
                      {item.name}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: "var(--text-secondary)" }}><EditableCell value={item.vendor} field="vendor" itemId={item.id} onSave={handleCellEdit} /></td>
                    <td style={{ ...tdStyle, fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-tertiary)" }}><EditableCell value={item.vpn} field="vpn" itemId={item.id} onSave={handleCellEdit} mono /></td>
                    <td style={{ ...tdStyle, fontSize: 11, color: "var(--text-tertiary)" }}>{item.casepack || "—"}</td>
                    <td style={{ ...tdStyle, fontSize: 11, color: "var(--text-tertiary)", fontWeight: 500 }}>{item.costUnit || "—"}</td>
                    <td style={{ ...tdStyle, fontFamily: "'DM Mono', monospace" }}><EditableCell value={item.cost} field="cost" itemId={item.id} onSave={handleCellEdit} isNumeric isCurrency mono /></td>
                    <td style={{ ...tdStyle, fontSize: 11, color: "var(--text-tertiary)", fontWeight: 500 }}>{item.saleUnit || "—"}</td>
                    <td style={{ ...tdStyle, fontFamily: "'DM Mono', monospace" }}><EditableCell value={item.price} field="price" itemId={item.id} onSave={handleCellEdit} isNumeric isCurrency mono /></td>
                    {isCanDual(activeStore) && (
                      <td style={{ ...tdStyle, fontFamily: "'DM Mono', monospace", fontSize: 12, color: "var(--text-secondary)" }}>
                        {isKgItem(item) && item.price != null ? `$${toLb(item.price).toFixed(2)}` : "—"}
                      </td>
                    )}
                    <td style={tdStyle}><MarginBar value={item.cost != null ? item.margin : null} /></td>
                    <td style={{ ...tdStyle, fontFamily: "'DM Mono', monospace" }} title={gpTitle(item, lastSalesWeek)}>
                      <GPCell value={item.gpDollars} qty={item.qtySold} />
                    </td>
                    <td style={{ ...tdStyle, fontFamily: "'DM Mono', monospace" }} title={newGpTitle(item)}>
                      <GPCell value={item.newGpDollars} qty={item.qtySold} bold={item.newGpUsesAdj} />
                    </td>
                    <td style={{ ...tdStyle, fontFamily: "'DM Mono', monospace" }}>
                      <GPDeltaCell value={item.gpDelta} />
                    </td>
                    <td style={{ ...tdStyle, fontFamily: "'DM Mono', monospace", fontSize: 12, color: "var(--text-tertiary)" }}>{item.newPrice != null ? `$${item.newPrice.toFixed(2)}` : "—"}</td>
                    <td style={{ ...tdStyle, background: item.adjPrice != null ? "rgba(56,189,248,0.06)" : "transparent", fontFamily: "'DM Mono', monospace" }}>
                      <EditableCell value={item.adjPrice} field="adjPrice" itemId={item.id} onSave={handleCellEdit} isNumeric isCurrency mono clearable />
                    </td>
                    <td style={{ ...tdStyle, background: item.adjPrice != null ? "rgba(56,189,248,0.06)" : "transparent", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                      {(() => { if (item.adjPrice == null || item.cost == null || item.adjPrice === 0) return "—"; const am = Math.round(((item.adjPrice - item.cost) / item.adjPrice) * 1000) / 10; return <span style={{ fontWeight: 600, color: am < 25 ? "#e05252" : am > 55 ? "#d4a017" : "#3a9a5c" }}>{am.toFixed(1)}%</span>; })()}
                    </td>
                    {isCanDual(activeStore) && (
                      <td style={{ ...tdStyle, background: item.adjPrice != null ? "rgba(56,189,248,0.06)" : "transparent", fontFamily: "'DM Mono', monospace" }}>
                        {isKgItem(item)
                          ? <EditableCell value={toLb(item.adjPrice)} field="adjPriceLb" itemId={item.id}
                              onSave={handleCellEdit} isNumeric isCurrency mono clearable />
                          : <span style={{ color: "var(--text-tertiary)" }}>—</span>}
                      </td>
                    )}
                    <td style={{ ...tdStyle, fontFamily: "'DM Mono', monospace" }}><GapCell value={item.gapPct} /></td>
                    <td style={{ ...tdStyle, background: item.marketPrice != null ? "rgba(34,197,94,0.06)" : "transparent", fontFamily: "'DM Mono', monospace", position: "relative" }}
                        title={mktTitle(item, latestCompWeek)}>
                      <EditableCell value={item.marketPrice} field="marketPrice" itemId={item.id} onSave={handleCellEdit} isNumeric isCurrency mono clearable />
                      {item.marketIsManual && item.marketPrice != null && (
                        <span title="Typed by hand — the chart shows the reported price"
                          style={{ position: "absolute", top: 6, right: 5, width: 5, height: 5, borderRadius: "50%", background: "#d4a017" }} />
                      )}
                    </td>
                    <td style={{ ...tdStyle, background: isDisc ? "rgba(147,51,234,0.08)" : "transparent" }}>
                      <input type="checkbox" checked={isDisc} onChange={() => handleDiscountToggle(item.id)}
                        style={{ accentColor: "#9333ea", width: 14, height: 14, cursor: "pointer" }} />
                    </td>
                    <td style={{ ...tdStyle, background: isDisc ? "rgba(147,51,234,0.08)" : "transparent", fontFamily: "'DM Mono', monospace" }}>
                      {isDisc ? (
                        <DiscountPriceInput value={discPrice} onChange={(v) => handleDiscountPrice(item.id, v)} />
                      ) : (
                        <span style={{ color: "var(--text-tertiary)", fontSize: 11 }}>—</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, background: isDisc ? "rgba(147,51,234,0.08)" : "transparent" }}>
                      {isDisc && discMargin != null ? (
                        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: discMargin < 15 ? "#e05252" : discMargin < 25 ? "#f59e0b" : "#3a9a5c", fontSize: 13 }}>
                          {discMargin.toFixed(1)}%
                        </span>
                      ) : (
                        <span style={{ color: "var(--text-tertiary)", fontSize: 11 }}>—</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 11, color: item.error ? "#f59e0b" : "var(--text-tertiary)", textAlign: "left", maxWidth: 200, whiteSpace: "normal", lineHeight: 1.3 }}>{item.error || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {items.length > 0 && displayItems.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0", color: "var(--text-tertiary)" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>∅</div>
            <div style={{ fontSize: 14 }}>No items match your filters</div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: "10px 28px", background: "var(--bg-secondary)", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "var(--text-tertiary)", flexShrink: 0 }}>
        <span>
          {items.length > 0 ? `Showing ${displayItems.length} of ${items.length} items · Click any cell to edit` : "No data loaded — drop files above"}
          {hasSelection && <span style={{ color: "var(--accent)", marginLeft: 12 }}>· {selectedItems.size} selected</span>}
        </span>
        <span>{(() => {
          const vendorNames = { baldor: "Baldor", freshpoint_fl: "FP FL", freshpoint_da: "FP DA", freshpoint_sc: "FP SC", mrgreen: "Mr Greens", testa: "Testa", midwest: "Midwest", hardies: "Hardie's", ok: "OK", vesta: "Vesta", charlies: "Charlie's", zeus: "Zeus", bondi: "Bondi", burnac: "Burnac" };
          const loaded = Object.entries(vendorNames).filter(([k]) => vendorData[k]).map(([k, n]) => `${n}: ${Object.keys(vendorData[k]).length}`);
          // CW regions
          const cwRegions = ["NY", "FL", "Chicago", "Texas", "LA", "SF"].filter((r) => vendorData[`cw_${r}`]);
          if (cwRegions.length > 0) loaded.push(`CW: ${cwRegions.join("/")}`);
          return loaded.length > 0 ? loaded.join(" · ") + ` · Sales: ${Object.keys(salesPriceLookup).length}` : `No vendors loaded · Sales: ${Object.keys(salesPriceLookup).length}`;
        })()}</span>
      </div>

      </div>{/* end Pricing view */}

      {showAddModal && <AddItemModal onClose={() => setShowAddModal(false)} onAdd={handleAddItem} />}
      {detailItem && <ItemDetailModal item={detailItem} history={storeHistory[activeStore] || []} onClose={() => setDetailItem(null)} compSeries={compSeriesFor(detailItem)} />}
      {showRulesPanel && <CasepackRulesPanel rules={casepackRules} onUpdate={setCasepackRules} onClose={() => setShowRulesPanel(false)} />}
      {showMarginTrend && (
        <MarginTrendModal
          store={STORES.find((st) => st.abbr === activeStore)?.name || activeStore}
          storeRows={marginSeries(null)}
          filterRows={isFiltered ? marginSeries(displayItems.map((i) => String(i.d365 ?? "").trim())) : null}
          filterLabel={FILTER_LABELS[filter] || (search ? "Search results" : "Filter")}
          onClose={() => setShowMarginTrend(false)} />
      )}
      {showInputsPanel && <InputsPanel inputs={inputs} onRemoveWeek={handleRemoveWeek} onClose={() => setShowInputsPanel(false)} dataWeeks={Object.keys(compWeeks || {})} />}
      <style>{`
        @keyframes slideIn { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        input[type="checkbox"] { width: 14px; height: 14px; cursor: pointer; }
        ::-webkit-scrollbar { width: 10px; height: 8px; }
        ::-webkit-scrollbar-track { background: var(--bg-primary); border-radius: 5px; }
        ::-webkit-scrollbar-thumb { background: #3a3d4a; border-radius: 5px; border: 2px solid var(--bg-primary); }
        ::-webkit-scrollbar-thumb:hover { background: #4a4d5a; }
      `}</style>
    </div>
  );
}

const thStyle = { padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#8b90a0", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", borderBottom: "1px solid #2a2d3a", position: "sticky", top: 0, background: "#0f1117", zIndex: 10, whiteSpace: "nowrap", userSelect: "none" };
const tdStyle = { padding: "9px 12px", textAlign: "center", borderBottom: "1px solid #1c1f2b", whiteSpace: "nowrap" };
